import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import takataApi from '../services/takata';
import concessionsApi from '../services/concessions';
import usersApi from '../services/users';
import permissionsApi from '../services/permissions';
import aiApi from '../services/ai';
import { matchesSearch } from '../utils/search';
import Copyable from './Copyable';
import * as XLSX from 'xlsx';
import './Takata.css';

import { texteTheme } from '../utils/theme';
import EmlApercu, { estEml } from './shared/EmlApercu';
// ─── PDF.js (lazy) ────────────────────────────────────────────────────────────
let _pdfjsLib = null;
async function getPdfjsLib() {
  if (!_pdfjsLib) {
    _pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
    _pdfjsLib.GlobalWorkerOptions.workerSrc =
      '//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
  }
  return _pdfjsLib;
}

// ─── Miniature PDF ────────────────────────────────────────────────────────────
const PREVIEW_W = 165;
const PREVIEW_H = 147;

function PdfThumbnail({ url }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await getPdfjsLib();
        const pdf   = await pdfjs.getDocument({ url, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const DPR  = window.devicePixelRatio || 1;
        const vp0  = page.getViewport({ scale: 1 });
        const scale = Math.max(PREVIEW_W / vp0.width, PREVIEW_H / vp0.height) * DPR;
        const vp    = page.getViewport({ scale });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        canvas.style.width  = Math.round(vp.width  / DPR) + 'px';
        canvas.style.height = Math.round(vp.height / DPR) + 'px';
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);
  if (error) return <div className="doc-icon"><span className="doc-icon-emoji">📄</span><span className="doc-icon-label">PDF</span></div>;
  return <canvas ref={canvasRef} />;
}

// ─── Visionneuse plein écran (lightbox) ───────────────────────────────────────
function TakataDocViewer({ doc, onClose }) {
  const isImage = doc.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.name || '');
  const isPdf   = doc.type === 'application/pdf'  || /\.pdf$/i.test(doc.name || '');
  const isEml   = !isImage && !isPdf && estEml(doc);
  return (
    <div className="doc-viewer-overlay" onClick={onClose}>
      <div className={`doc-viewer-content${isEml ? ' eml-viewer' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="doc-viewer-header">
          <span className="doc-viewer-title">{doc.name}</span>
          {isEml && <a href={doc.url} download={doc.name} className="eml-dl">Télécharger</a>}
          <button className="doc-viewer-close" onClick={onClose}>✕</button>
        </div>
        <div className="doc-viewer-body">
          {isImage && <img src={doc.url} alt={doc.name} className="doc-viewer-image" />}
          {isPdf   && <embed src={doc.url} type="application/pdf" className="doc-viewer-pdf" />}
          {isEml   && <EmlApercu url={doc.url} />}
          {!isImage && !isPdf && !isEml && (
            <div className="doc-viewer-fallback">
              <span style={{ fontSize: '60px' }}>📎</span>
              <p>{doc.name}</p>
              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn-primary">Télécharger</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Vignette document Takata (URL chargée depuis S3) ─────────────────────────
function TakataThumbnail({ docId, nom, canRemove, onRemove, onRename }) {
  const [url, setUrl]             = useState(null);
  const [lightboxOpen, setLightbox] = useState(false);
  const [renaming, setRenaming]   = useState(false);
  const [newName, setNewName]     = useState(nom || '');
  const inputRef = useRef(null);

  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(nom || '');
  const isPdf   = /\.pdf$/i.test(nom || '');
  const ext     = (nom || '').split('.').pop().toUpperCase();

  useEffect(() => { setNewName(nom || ''); }, [nom]);
  useEffect(() => { if (renaming) inputRef.current?.focus(); }, [renaming]);
  useEffect(() => {
    takataApi.getDocumentUrl(docId).then(r => setUrl(r.data.url)).catch(() => {});
  }, [docId]);

  const submitRename = () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== nom && onRename) onRename(docId, trimmed);
    setRenaming(false);
  };

  return (
    <>
      <div className="doc-thumbnail" title={nom}>
        {canRemove && (
          <button className="doc-remove-btn" onClick={e => { e.stopPropagation(); onRemove(docId); }}>✕</button>
        )}
        <div className="doc-preview" onClick={url ? () => setLightbox(true) : undefined}>
          {!url ? (
            <div className="doc-icon"><div className="doc-uploading-spinner" /></div>
          ) : isImage ? (
            <img src={url} alt={nom} />
          ) : isPdf ? (
            <PdfThumbnail url={url} />
          ) : (
            <div className="doc-icon">
              <span className="doc-icon-emoji">📎</span>
              <span className="doc-icon-label">{ext}</span>
            </div>
          )}
        </div>
        {renaming ? (
          <input ref={inputRef} className="doc-rename-input" value={newName}
            onChange={e => setNewName(e.target.value)}
            onBlur={submitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') submitRename();
              if (e.key === 'Escape') { setNewName(nom || ''); setRenaming(false); }
            }}
          />
        ) : (
          <div className="doc-name" title={onRename ? 'Double-cliquer pour renommer' : nom}
            onDoubleClick={() => onRename && setRenaming(true)}>
            {nom}
          </div>
        )}
      </div>
      {lightboxOpen && url && (
        <TakataDocViewer doc={{ url, name: nom }} onClose={() => setLightbox(false)} />
      )}
    </>
  );
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const CONSTRUCTEURS = ['Chevrolet', 'Ford', 'Honda', 'Mazda', 'Nissan', 'Opel', 'Skoda', 'Suzuki'];

const STATUTS = [
  { value: 'casse',         label: 'Cassé',           color: '#57534e' },
  { value: 'clos',          label: 'Clos',            color: 'var(--text-muted)' },
  { value: 'en_cours',      label: 'En cours',        color: '#f97316' },
  { value: 'epave',         label: 'Épave',           color: '#78350f' },
  { value: 'gele',          label: 'Gelé',            color: '#7c3aed' },
  { value: 'non_contacte',  label: 'Non contacté',    color: '#ef4444' },
  // Non contacté + date de 1er contact → Contacté (trigger serveur, cf. KNOWLEDGE.md).
  { value: 'contacte',      label: 'Contacté',        color: 'var(--color-info)' },
  { value: 'piece_en_attente', label: 'Pièce(s) en attente', color: '#0f766e' },
  { value: 'rdv_programme', label: 'RDV programmé',   color: '#3b82f6' },
  { value: 'rdv_propose',   label: 'RDV proposé',     color: '#f59e0b' },
  { value: 'reforme',       label: 'Réformé',         color: '#92400e' },
  { value: 'repare',        label: 'Réparé',          color: '#22c55e' },
  { value: 'vendu',         label: 'Vendu',           color: '#0891b2' },
  { value: 'vole',          label: 'Volé',            color: '#7c3aed' },
];

const TYPES_CONTACT = [
  { value: 'appel_entrant', label: 'Appel entrant', picto: '📲' },
  { value: 'appel_sortant', label: 'Appel sortant', picto: '📞' },
  { value: 'autres',        label: 'Autres',        picto: '📝' },
  { value: 'courrier',      label: 'Courrier',      picto: '📬' },
  { value: 'email',         label: 'E-mail',        picto: '✉️' },
  { value: 'sms',           label: 'SMS',           picto: '💬' },
  { value: 'visite',        label: 'Visite',        picto: '🏢' },
];

const RESULTATS_CONTACT = [
  { value: 'joint',                 label: 'Contacté',              color: '#22c55e' },
  { value: 'message_vocal_laisse',  label: 'Message vocal laissé',  color: '#f97316' },
  { value: 'pas_de_messagerie',     label: 'Pas de messagerie',     color: '#ef4444' },
  { value: 'mauvais_numero',        label: 'Mauvais numéro',        color: '#ef4444' },
  { value: 'changement_proprio',    label: 'Chgt propriétaire',     color: '#f97316' },
  { value: 'epave',                 label: 'Épave',                 color: '#6b7280' },
  { value: 'detruit',               label: 'Détruit',               color: '#6b7280' },
  { value: 'reforme',               label: 'Réformé',               color: '#6b7280' },
  { value: 'vole',                  label: 'Volé',                  color: '#dc2626' },
  { value: 'voyant_airbag',         label: 'Voyant airbag',         color: '#f59e0b' },
  { value: 'intervention_domicile', label: 'Intervention à domicile', color: '#0ea5e9' },
  { value: 'remorquage',            label: 'Remorquage',            color: '#a855f7' },
  { value: 'vh_pret',               label: 'VH Prêt',               color: '#0891b2' },
  // Déclenche un email immédiat aux destinataires de la concession (alerte_client_relance).
  { value: 'client_relance',        label: 'Le client relance',     color: 'var(--color-warning)' },
  { value: 'autres',                label: 'Autres',                color: '#6b7280' },
  // Valeurs héritées — non proposées à la création, conservées pour l'affichage des dossiers existants
  // Retirées du choix le 2026-09-09 (à la demande) mais TOUJOURS déclarées ici et
  // dans `takata_contacts_resultat_check` : des entrées de journal les portent déjà,
  // les supprimer afficherait une pastille vide et casserait la contrainte au boot.
  { value: 'messagerie',         label: 'Messagerie',         color: '#f97316' },
  { value: 'pas_de_reponse',     label: 'Pas de réponse',     color: 'var(--text-muted)' },
  { value: 'accepte_rdv',        label: 'Accepte RDV',        color: '#3b82f6' },
  { value: 'refuse',             label: 'Refus',              color: '#dc2626' },
  { value: 'courrier_envoye',    label: 'Envoyé',             color: '#8b5cf6' },
  { value: 'distribue',         label: 'Distribué',           color: '#22c55e' },
  { value: 'npai',               label: 'NPAI',               color: '#ef4444' },
  { value: 'non_reclame',        label: 'Non réclamé',        color: '#dc2626' },
];

// Aide contextuelle par action du journal (champ `resultat`, cf. RESULTATS_CONTACT) —
// rappel des infos à collecter, purement informatif. Clé = valeur technique de l'action
// (pas le libellé affiché) pour rester valide si un libellé change un jour. Les actions
// absentes de cette table n'affichent aucune bulle (pas d'icône ⓘ superflue).
const ACTION_HELP_TEXT = {
  joint:               'VH circule toujours ?',
  intervention_domicile: 'VH circule toujours ?',
  changement_proprio:  'Certificat de cession ? Coordonnées du nouveau propriétaire (nom, tél, email) ? Vente en quelle année ?',
  epave:               "Obligation de récupérer l'airbag dangereux. VH praticable / accessible ? Si VHU, manque-t-il des pièces ? Jerecyclemonvehicule.fr prime entre 100 € et 200 € pour l'enlèvement et la destruction gratuits.",
  detruit:             'Certificat de destruction ? Sinon, quelle casse ou commune ? Quelle date / année ?',
  reforme:             "Rapport d'expert ou d'assurance ? VH parti à la casse (laquelle ?), repris par l'assureur (lequel ?)",
  vole:                'Transmettre la plainte, sinon nous continuerons à vous envoyer des courriers en recommandé.',
};

// Icône d'aide ⓘ accessible : bulle au survol souris ET au focus clavier, fermeture au
// blur/mouseleave/Échap. Purement informatif (aucune saisie) — `stopPropagation` sur le
// clic pour ne pas re-basculer le bouton d'action parent (case toggle au clic).
//
// La bulle est rendue dans un portail (document.body) en position: fixed, comme
// SearchableSelect : en position absolue dans la modale du journal, elle était
// rognée par `.modal-content { overflow: hidden }` / `.modal-body { overflow-y: auto }`
// — illisible pour les textes longs placés à droite de la rangée (Épave, Détruit).
// Position recadrée dans la fenêtre (marge 8px), basculée au-dessus de l'icône si
// la place manque en dessous ; fermée au scroll / resize (elle ne suit pas l'icône).
const HELP_MARGE = 8;
function ActionHelpTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const bubbleRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const icon = iconRef.current?.getBoundingClientRect();
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!icon || !bubble) return undefined;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(HELP_MARGE, Math.min(icon.left, vw - bubble.width - HELP_MARGE));
    const dessous = icon.bottom + 6;
    const top = (dessous + bubble.height > vh - HELP_MARGE && icon.top - 6 - bubble.height >= HELP_MARGE)
      ? icon.top - 6 - bubble.height
      : dessous;
    setPos({ top, left });
    const fermer = () => setOpen(false);
    window.addEventListener('scroll', fermer, true);
    window.addEventListener('resize', fermer);
    return () => {
      window.removeEventListener('scroll', fermer, true);
      window.removeEventListener('resize', fermer);
    };
  }, [open]);

  return (
    <span
      ref={iconRef}
      className="takata-action-help"
      tabIndex={0}
      role="note"
      aria-label={`Aide : ${text}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
      onClick={e => e.stopPropagation()}
    >
      ⓘ
      {open && createPortal(
        // Premier rendu masqué (visibility) le temps de mesurer la bulle.
        <span
          ref={bubbleRef}
          className="takata-action-help-bubble"
          role="tooltip"
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
        >{text}</span>,
        document.body
      )}
    </span>
  );
}

const STATUTS_RDV = [
  { value: 'propose',  label: 'Proposé',   color: '#f97316' },
  { value: 'confirme', label: 'Confirmé',  color: '#3b82f6' },
  { value: 'honore',   label: 'Honoré',    color: '#22c55e' },
  { value: 'no_show',  label: 'No Show',   color: '#ef4444' },
  { value: 'reporte',  label: 'Reporté',   color: '#8b5cf6' },
  { value: 'refuse',   label: 'Refusé',    color: '#f59e0b' },
  { value: 'annule',   label: 'Annulé',    color: 'var(--text-muted)' },
];

// Actions possibles sur un RDV (cf. RdvTab). Tous les boutons partagent la même
// UI : la couleur appartient au badge de statut, pas aux actions. `visible`
// décrit depuis quel statut l'action a un sens ; l'action correspondant au
// statut courant en est EXCLUE — la répéter en gris à côté du badge faisait
// doublon (2026-09-09).
const ACTIONS_RDV = [
  { value: 'confirme', label: 'Confirmer', titre: 'Confirmer le rendez-vous',
    visible: (st) => st === 'propose' },
  { value: 'refuse',   label: 'Refusé',    titre: 'Le client a refusé le créneau',
    visible: (st) => st === 'propose' },
  { value: 'honore',   label: 'Honoré',    titre: 'Le client est venu',
    visible: (st) => ['confirme', 'no_show', 'reporte'].includes(st) },
  { value: 'no_show',  label: 'No Show',   titre: "Le client n'est pas venu",
    visible: (st) => ['confirme', 'honore', 'reporte'].includes(st) },
  { value: 'reporte',  label: 'Reporté',   titre: 'Reporter : ouvre la création d\'un nouveau RDV',
    visible: (st) => ['confirme', 'honore', 'no_show'].includes(st) },
  { value: 'annule',   label: 'Annuler',   titre: 'Annuler le rendez-vous',
    visible: (st) => ['propose', 'refuse'].includes(st) },
];

const TYPES_DOC = [
  { value: 'ar',        label: 'Accusé de réception' },
  { value: 'autre',     label: 'Autre' },
  { value: 'courrier',  label: 'Courrier' },
  { value: 'email',     label: 'E-mail' },
  { value: 'log_appel', label: 'Log appel' },
];

const STATUTS_RETOUR = [
  { value: 'en_attente',        label: 'En attente',          color: 'var(--text-muted)' },
  { value: 'distribue',         label: 'Distribué',           color: '#22c55e' },
  { value: 'npai',              label: 'NPAI',                color: '#ef4444' },
  { value: 'changement_proprio',label: 'Changement propriétaire', color: '#f97316' },
  { value: 'non_reclame',       label: 'Non réclamé',         color: '#dc2626' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

// « Non contacté » / « Contacté » sont de vrais statuts depuis le 2026-10-26
// (le relibellé d'affichage « Leads » d'un Non contacté daté est retiré).
const statutInfo = (value) => STATUTS.find(s => s.value === value) || { label: value, color: 'var(--text-muted)' };
// Fond teinté d'un badge de statut : fonctionne avec une couleur hexadécimale
// comme avec un token `var(--…)` (la concaténation « couleur + '22' » ne marche
// qu'avec un hexadécimal).
const fondStatut = (color) => `color-mix(in srgb, ${color} 13%, transparent)`;
const rdvStatutInfo = (value) => STATUTS_RDV.find(s => s.value === value) || { label: value, color: 'var(--text-muted)' };
const retourInfo = (value) => STATUTS_RETOUR.find(s => s.value === value) || { label: value, color: 'var(--text-muted)' };

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const getStatutCampagneLabel = (d) => {
  const drivConcerned = !!d.driv_airbag_concerned;
  const passConcerned = !!d.pass_airbag_concerned;
  const drivDone = !!d.driv_airbag_replaced || !!d.driv_airbag_inspected;
  const passDone = !!d.passenger_airbag_replaced || !!d.pass_airbag_inspected;
  if (drivConcerned && passConcerned) {
    if (drivDone && passDone) return 'Terminé';
    if (drivDone || passDone) return 'Partiel';
    return 'A faire';
  }
  if (drivConcerned) return drivDone ? 'Terminé' : 'A faire';
  if (passConcerned) return passDone ? 'Terminé' : 'A faire';
  return 'A faire';
};

const normalizeImmat = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, '');

// VIN standard ISO 3779 : 17 caractères alphanumériques, sans I, O ni Q
// (même règle que la webapp publique webapp-vin-check et que le back).
const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

const parseLocalDate = (d) => {
  if (!d) return null;
  const [y, m, day] = (typeof d === 'string' ? d : '').split('T')[0].split('-').map(Number);
  return (y && m && day) ? new Date(y, m - 1, day) : null;
};

// Écart en **jours calendaires** entre deux dates, tel que l'utilisateur le compte
// sur les deux dates affichées (10/07 → 15/07 = 5 j).
// ⚠️ Ne JAMAIS soustraire les valeurs brutes : les délais comparent une colonne
// `date` (ex. replacement_date_pass, date_debut → minuit) à une colonne `timestamptz`
// (ex. date_premier_contact, created_at → heure réelle). La différence brute vaut alors
// 4 j 5 h ; `Math.round` rendait 4 ou 5 **selon l'heure du contact**, d'où un délai
// tantôt juste tantôt faux d'un jour, incohérent avec les dates affichées juste au-dessus.
// On normalise donc les deux bornes à minuit via `parseLocalDate` (même partie date que
// `formatDate`) : la soustraction est un multiple exact de 86400000 (pas de DST aux Antilles).
const diffJours = (from, to) => {
  const a = parseLocalDate(from);
  const b = parseLocalDate(to);
  return (a && b) ? Math.round((b - a) / 86400000) : null;
};
// Convertit une date ISO (YYYY-MM-DD ou timestamp) en Date **minuit local** pour XLSX.
// ⚠️ La lib xlsx convertit les Date en serial Excel via l'heure LOCALE du navigateur :
// une Date à minuit UTC devient « veille 20:00 » en Martinique (UTC-4) → J-1 dans Excel.
// Il faut donc produire minuit LOCAL. Pour un timestamp, on prend la date locale affichée
// dans l'app (même rendu que formatDateTime), l'heure est volontairement ignorée.
const toXlsDate = (v) => {
  if (!v) return '';
  const str = String(v);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  if (str.length <= 10) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(str);
  if (isNaN(d)) return new Date(+m[1], +m[2] - 1, +m[3]);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const formatDate = (iso) => {
  const dt = parseLocalDate(iso);
  return dt ? dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
};
const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Transforme les URLs présentes dans un texte libre en liens cliquables
// (ex. lien Google Maps de la géoloc webapp dans les notes de journal).
// Le reste du texte est rendu tel quel — pas d'interprétation HTML.
const linkifyText = (text) => {
  if (!text) return text;
  const parts = String(text).split(/(https?:\/\/[^\s|]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
      : part
  );
};

// Retire un éventuel code postal en tête du libellé ville (ex. « 97110 Pointe A Pitre »
// → « Pointe A Pitre »). Purement à l'affichage/filtrage — les données en base ne sont
// pas modifiées. Ne corrige pas les fautes d'orthographe (nettoyage manuel côté métier).
const villeSansCP = (v) => (v == null ? '' : String(v).replace(/^\s*\d{4,5}\s+/, '').trim());
const cpFromVille = (v) => { const m = String(v||'').match(/^\s*(\d{4,5})\s/); return m ? m[1] : ''; };
// Ville du Reporting constructeur : celle de l'adresse du client principal
// (onglet 👥 Clients, alias principal_* de GET /dossiers) fait foi — la ville
// seule, sans code postal (celui-ci n'alimente que la colonne Code Postal des
// exports). Vide sans client principal ou sans ville renseignée → l'appelant
// retombe sur takata_dossiers.ville.
const villeDuPrincipal = (d) => (d.principal_ville || '').trim();
const cpDuPrincipal = (d) => (d.principal_code_postal || '').trim();
// Identité client du Reporting constructeur (tableau, filtre, tri, modale, exports) :
// même règle que l'en-tête de dossier — si un client est coché « principal » dans
// l'onglet 👥 Clients, TOUS les champs viennent de sa fiche (pas de mélange avec
// l'ancien propriétaire) ; sinon repli sur takata_dossiers.client_* (import).
// Calculé à chaque chargement de GET /dossiers : suit le changement de principal.
const aUnClientPrincipal = (d) => !!(d.principal_nom || d.principal_prenom || d.principal_telephone
  || d.principal_email || d.principal_adresse || d.principal_ville);
const clientDuReporting = (d) => (aUnClientPrincipal(d)
  ? {
      nom: d.principal_nom || '', prenom: d.principal_prenom || '',
      telephone: d.principal_telephone || '', email: d.principal_email || '',
      adresse: [d.principal_adresse, d.principal_complement].map(v => (v || '').trim()).filter(Boolean).join(', '),
    }
  : {
      nom: d.client_nom || '', prenom: d.client_prenom || '',
      telephone: d.client_telephone || '', email: d.client_email || '',
      adresse: d.client_adresse || '',
    });
const formatDateInput = (iso) => {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 16);
};

// ─── Multi-select dropdown ────────────────────────────────────────────────────

function MultiSelectFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const displayLabel = selected.length === 0 ? label : selected.length === 1
    ? (options.find(o => o.value === selected[0])?.label || selected[0])
    : `${selected.length} sélectionnés`;

  return (
    <div className="tk-multiselect" ref={ref}>
      <button
        className={`tk-multiselect-btn${selected.length ? ' active' : ''}`}
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {displayLabel}
        <span className="tk-multiselect-arrow">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="tk-multiselect-dropdown">
          <button className="tk-multiselect-clear" onClick={() => { onChange([]); }} type="button">Tout effacer</button>
          {options.map(o => (
            <label key={o.value} className="tk-multiselect-option">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Configuration permissions ───────────────────────────────────────────────

// Définition des onglets Takata (réutilisée aussi dans le composant principal)
const TAKATA_TABS_DEF = [
  { id: 'dossiers',         label: 'Dossiers',         emoji: '📂' },
  { id: 'a_rappeler',       label: 'A rappeler',        emoji: '📞' },
  { id: 'stocks',           label: 'Stocks airbags',    emoji: '🔩' },
  { id: 'budget',           label: 'Budget',            emoji: '💰' },
  { id: 'courriers_global', label: 'Courriers',         emoji: '📬' },
  { id: 'import',           label: 'Import VINs',       emoji: '⬆️' },
  { id: 'casses',           label: 'Casses',            emoji: '🚧' },
  { id: 'webapp',           label: 'Webapp',            emoji: '📱' },
  { id: 'dashboard',        label: 'Tableau de bord',   emoji: '📊' },
  { id: 'reporting',        label: 'Reporting',         emoji: '📤' },
  { id: 'echanges',         label: 'Echanges internes', emoji: '💬' },
];

// Labels affichés dans la barre d'onglets principale
const TAKATA_MAIN_TABS = [
  { id: 'dossiers',         label: '📂 Dossiers' },
  { id: 'a_rappeler',       label: '📞 A rappeler' },
  { id: 'stocks',           label: '🔩 Stocks airbags' },
  { id: 'budget',           label: '💰 Budget' },
  { id: 'courriers_global', label: '📬 Courriers' },
  { id: 'echanges',         label: '💬 Echanges internes' },
  { id: 'webapp',           label: '📱 Webapp' },
  { id: 'dashboard',        label: '📊 Tableau de bord' },
  { id: 'reporting',        label: '📤 Reporting constructeur' },
  { id: 'import',           label: '⬆️ Import VINs' },
  { id: 'casses',           label: '🚧 Casses' },
  { id: 'config',           label: '⚙️ Configuration' },
];

function TakataTabBar({ activeTab, onTabChange }) {
  const containerRef = useRef(null);
  const measureRef   = useRef(null);
  const menuRef      = useRef(null);
  const [visibleCount, setVisibleCount] = useState(TAKATA_MAIN_TABS.length);
  const [menuOpen, setMenuOpen]         = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const measure   = measureRef.current;
    if (!container || !measure) return;

    const calc = () => {
      const MORE_BTN_W = 90; // largeur réservée au bouton "···"
      const availW = container.offsetWidth - MORE_BTN_W;
      const btns   = Array.from(measure.children);
      let used = 0, count = 0;
      for (let i = 0; i < btns.length; i++) {
        used += btns[i].offsetWidth;
        if (used > availW) break;
        count = i + 1;
      }
      // Si tout rentre, pas besoin du bouton "···" → réserver l'espace
      if (count === TAKATA_MAIN_TABS.length) {
        setVisibleCount(TAKATA_MAIN_TABS.length);
      } else {
        setVisibleCount(Math.max(count, 1));
      }
    };

    const ro = new ResizeObserver(calc);
    ro.observe(container);
    calc();
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const visible       = TAKATA_MAIN_TABS.slice(0, visibleCount);
  const overflow      = TAKATA_MAIN_TABS.slice(visibleCount);
  const activeOverflow = overflow.find(t => t.id === activeTab);

  return (
    <div className="page-tabs takata-tab-bar" ref={containerRef}>
      {/* Barre de mesure invisible — permet de calculer la largeur réelle de chaque onglet */}
      <div ref={measureRef} className="takata-tab-measure" aria-hidden="true">
        {TAKATA_MAIN_TABS.map(t => <button key={t.id} tabIndex={-1}>{t.label}</button>)}
      </div>

      {/* Onglets visibles */}
      {visible.map(t => (
        <button
          key={t.id}
          className={activeTab === t.id ? 'active' : ''}
          onClick={() => onTabChange(t.id)}
        >{t.label}</button>
      ))}

      {/* Bouton "···" pour les onglets qui débordent */}
      {overflow.length > 0 && (
        <div className="takata-tab-more" ref={menuRef}>
          <button
            className={`takata-tab-more-btn${activeOverflow ? ' active' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
          >
            {activeOverflow ? activeOverflow.label : '···'} <span className="takata-tab-chevron">▾</span>
          </button>
          {menuOpen && (
            <div className="takata-tab-dropdown">
              {overflow.map(t => (
                <button
                  key={t.id}
                  className={activeTab === t.id ? 'active' : ''}
                  onClick={() => { onTabChange(t.id); setMenuOpen(false); }}
                >{t.label}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TakataConfigView({ user, allUsers }) {
  const isSuperAdmin = user.role === 'super-admin' || user.role === 'admin'; // peut modifier les toggles L/E
  const canManageUsers = user.role === 'super-admin'; // peut ajouter/révoquer des membres
  const [rows, setRows] = useState([]);
  const [configConcessions, setConfigConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(null);
  const [savingConc, setSavingConc] = useState(null);
  // Catalogue des droits spécifiques, servi par le backend (source unique).
  const [capacitesCatalogue, setCapacitesCatalogue] = useState([]);
  const [savingCap, setSavingCap] = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const r = await takataApi.getConfig();
      setRows(r.data.users);
      setConfigConcessions(r.data.concessions || []);
      setCapacitesCatalogue(r.data.capacites_catalogue || []);
    } catch (e) { alert('Erreur chargement : ' + e.message); }
    setLoading(false);
  };

  const togglePerm = useCallback((userId, tabId, type) => {
    if (!isSuperAdmin) return;
    setRows(prev => {
      const next = prev.map(u => {
        if (u.id !== userId) return u;
        // Si tabs === null (accès total hérité), on initialise tous les onglets à on avant de modifier
        const baseTabs = u.tabs ?? Object.fromEntries(
          TAKATA_TABS_DEF.map(t => [t.id, { can_read: true, can_write: true }])
        );
        const cur = baseTabs[tabId] || { can_read: false, can_write: false };
        const np = { ...cur, [type]: !cur[type] };
        if (type === 'can_write' && np.can_write) np.can_read = true;
        if (type === 'can_read' && !np.can_read)  np.can_write = false;
        return { ...u, tabs: { ...baseTabs, [tabId]: np } };
      });
      const updated = next.find(u => u.id === userId);
      if (updated && updated.role !== 'super-admin') {
        setSaving(userId);
        takataApi.updateConfigUser(userId, { tabs: updated.tabs || {} })
          .catch(e => { alert('Erreur : ' + (e.response?.data?.error || e.message)); load(); })
          .finally(() => setSaving(null));
      }
      return next;
    });
  }, [isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Droits spécifiques — enregistrement immédiat, comme les toggles L/E.
  const toggleCapacite = async (userId, capacite) => {
    if (!canManageUsers) return;
    const u = rows.find(x => x.id === userId);
    if (!u) return;
    const actuelles = u.capacites || [];
    const next = actuelles.includes(capacite)
      ? actuelles.filter(c => c !== capacite)
      : [...actuelles, capacite];
    setRows(prev => prev.map(x => x.id === userId ? { ...x, capacites: next } : x));
    setSavingCap(userId);
    try {
      await takataApi.setCapacites(userId, next);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.error || e.message));
      load();
    } finally { setSavingCap(null); }
  };

  const handleRevoke = async (userId, userName) => {
    if (!window.confirm(`Retirer l'accès Takata à ${userName} ?`)) return;
    try {
      await takataApi.revokeAccess(userId);
      setRows(prev => prev.filter(u => u.id !== userId));
    } catch (e) { alert('Erreur : ' + e.message); }
  };

  if (loading) return <div className="mh-empty">Chargement…</div>;

  const nonSuperAdmins = rows.filter(u => u.role !== 'super-admin');
  const superAdmins    = rows.filter(u => u.role === 'super-admin');

  return (
    <div className="page-container">
      <div className="takata-config-header">
        <div>
          <div className="takata-config-title">Droits d'accès</div>
          <div className="takata-config-sub">
            Visible par tous les utilisateurs Takata · Modifiable par les admins et super-admins
          </div>
        </div>
        {canManageUsers && (
          <button className="page-action-btn" onClick={() => setShowAddModal(true)}>
            + Ajouter un utilisateur
          </button>
        )}
      </div>

      {/* Super-admins — accès total */}
      <section className="mh-panel">
        <h3>Super-admins — accès total</h3>
        <div className="takata-config-sa-list">
          {superAdmins.map(u => (
            <div key={u.id} className="takata-config-sa-chip">
              <span className="takata-config-sa-dot" />
              <strong>{u.prenom} {u.nom}</strong>
              <span className="takata-config-sa-badge">SA</span>
              {u.concession_nom && <span className="takata-config-conc">{u.concession_nom}</span>}
            </div>
          ))}
        </div>
      </section>

      {/* Matrice permissions */}
      <section className="mh-panel">
        <h3>Permissions par onglet</h3>
        <div className="takata-config-matrix-wrap">
          <table className="takata-config-matrix">
            <thead>
              <tr>
                <th className="takata-config-user-col">Utilisateur</th>
                {TAKATA_TABS_DEF.map(t => (
                  <th key={t.id} className="takata-config-tab-col">
                    <div className="takata-config-tab-header">{t.emoji}</div>
                    <div className="takata-config-tab-name">{t.label}</div>
                  </th>
                ))}
                {canManageUsers && <th className="takata-config-actions-col" />}
              </tr>
              {/* Légende L / E */}
              <tr className="takata-config-legend-row">
                <td />
                {TAKATA_TABS_DEF.map(t => (
                  <td key={t.id}>
                    <div className="takata-config-rw-legend"><span>L</span><span>E</span></div>
                  </td>
                ))}
                {canManageUsers && <td />}
              </tr>
            </thead>
            <tbody>
              {nonSuperAdmins.length === 0 ? (
                <tr><td colSpan={TAKATA_TABS_DEF.length + 2} className="mh-empty">Aucun utilisateur.</td></tr>
              ) : nonSuperAdmins.map(u => {
                const tabs = u.tabs; // null = accès total, {} ou {...} = configuré
                const isSaving = saving === u.id;
                return (
                  <tr key={u.id} className={isSaving ? 'takata-config-saving' : ''}>
                    <td className="takata-config-user-cell">
                      <div className="takata-config-user-name">{u.prenom} {u.nom}</div>
                      {u.concession_nom && <div className="takata-config-conc">{u.concession_nom}</div>}
                      <span className={`takata-config-role-badge takata-config-role-${u.role}`}>{u.role}</span>
                    </td>
                    {TAKATA_TABS_DEF.map(t => {
                      // null tabs = accès total hérité (pas encore de config individuelle)
                      const perm = tabs ? (tabs[t.id] || { can_read: false, can_write: false }) : { can_read: true, can_write: true };
                      const isTotal = tabs === null;
                      return (
                        <td key={t.id} className="takata-config-perm-cell">
                          <div className="takata-config-rw">
                            <button
                              className={`takata-config-toggle ${perm.can_read ? 'on' : 'off'} ${!isSuperAdmin ? 'readonly' : ''}`}
                              onClick={() => togglePerm(u.id, t.id, 'can_read')}
                              disabled={!isSuperAdmin}
                              title={isTotal ? 'Accès total — cliquer pour restreindre' : (perm.can_read ? 'Lecture activée — clic pour désactiver' : 'Lecture désactivée — clic pour activer')}
                            >L</button>
                            <button
                              className={`takata-config-toggle ${perm.can_write ? 'on' : 'off'} ${!isSuperAdmin ? 'readonly' : ''}`}
                              onClick={() => togglePerm(u.id, t.id, 'can_write')}
                              disabled={!isSuperAdmin || !perm.can_read}
                              title={isTotal ? 'Accès total — cliquer pour restreindre' : (perm.can_write ? 'Écriture activée' : 'Écriture désactivée')}
                            >E</button>
                          </div>
                          {isTotal && <div className="takata-config-total-dot" title="Accès total hérité — cliquer pour configurer" />}
                        </td>
                      );
                    })}
                    {canManageUsers && (
                      <td className="takata-config-actions-col">
                        <button
                          className="btn-icon delete"
                          title="Révoquer l'accès Takata"
                          onClick={() => handleRevoke(u.id, `${u.prenom} ${u.nom}`)}
                        >🗑️</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {nonSuperAdmins.some(u => u.tabs === null) && (
          <div className="takata-config-info">
            ℹ️ <strong>Accès total (hérité)</strong> : aucune restriction configurée pour cet utilisateur — il voit tous les onglets en lecture et écriture. Cliquez sur un toggle L ou E pour commencer à restreindre ses accès.
          </div>
        )}
      </section>

      {/* Droits spécifiques — « rôle custom » (2026-09-09) */}
      <section className="mh-panel">
        <h3>Droits spécifiques</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Pouvoirs ponctuels qui <strong>s'ajoutent</strong> au rôle de l'utilisateur sans le modifier.
          Les super-admins en disposent d'office. {canManageUsers ? 'Attribution réservée aux super-admins.' : 'Seul un super-admin peut les attribuer.'}
        </p>
        {capacitesCatalogue.length === 0 ? (
          <div className="takata-config-info">Aucun droit spécifique disponible.</div>
        ) : (
          <div className="takata-config-matrix-wrap">
            <table className="takata-config-matrix">
              <thead>
                <tr>
                  <th className="takata-config-user-col">Utilisateur</th>
                  {capacitesCatalogue.map(c => (
                    <th key={c.id} className="takata-config-tab-col" title={c.description}>
                      <div className="takata-config-tab-name">{c.nom}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nonSuperAdmins.map(u => (
                  <tr key={u.id}>
                    <td className="takata-config-user-cell">
                      <div className="takata-config-user-name">{u.prenom} {u.nom}</div>
                      {u.concession_nom && <div className="takata-config-conc">{u.concession_nom}</div>}
                      <span className={`takata-config-role-badge takata-config-role-${u.role}`}>{u.role}</span>
                    </td>
                    {capacitesCatalogue.map(c => {
                      const actif = (u.capacites || []).includes(c.id);
                      return (
                        <td key={c.id} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={actif}
                            disabled={!canManageUsers || savingCap === u.id}
                            onChange={() => toggleCapacite(u.id, c.id)}
                            aria-label={`${c.nom} — ${u.prenom} ${u.nom}`}
                            title={c.description}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Accès par concession */}
      <section className="mh-panel">
        <h3>Accès par concession</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Définit les concessions visibles par chaque utilisateur non super-admin. Sans restriction configurée, l'utilisateur voit toutes les concessions.
        </p>
        {canManageUsers && configConcessions.length > 0 && (
          <table className="takata-config-matrix" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th className="takata-config-user-col">Utilisateur</th>
                {configConcessions.map(c => <th key={c.id} style={{ textAlign: 'center', padding: '4px 8px', fontSize: 12 }}>{c.nom}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.filter(u => u.role !== 'super-admin').map(u => (
                <tr key={u.id}>
                  <td className="takata-config-user-col">
                    <div className="takata-config-user-name">{u.prenom} {u.nom}</div>
                  </td>
                  {configConcessions.map(c => {
                    const checked = (u.concession_ids || []).includes(c.id);
                    return (
                      <td key={c.id} style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={savingConc === u.id}
                          onChange={async () => {
                            const next = checked
                              ? (u.concession_ids || []).filter(id => id !== c.id)
                              : [...(u.concession_ids || []), c.id];
                            setSavingConc(u.id);
                            try {
                              await takataApi.updateConcessionAccess(u.id, next);
                              setRows(prev => prev.map(r => r.id === u.id ? { ...r, concession_ids: next } : r));
                            } catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
                            finally { setSavingConc(null); }
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!canManageUsers && (
          <div className="takata-config-info">
            🔒 Seuls les super-admins peuvent modifier les accès par concession.
          </div>
        )}
      </section>

      <SmsModelesConfig canEdit={isSuperAdmin} />

      {showAddModal && (
        <TakataAddUserModal
          allUsers={allUsers}
          existingIds={rows.map(u => u.id)}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Gestion des modèles de SMS (dans l'onglet Configuration) ────────────────
// Données d'exemple pour l'aperçu : ce que verra l'opérateur, avec un dossier
// fictif — pour se rendre compte du rendu des variables sans ouvrir un dossier.
const SMS_SAMPLE = {
  immat: 'AB-123-CD', client_prenom: 'Marie', client_nom: 'Durand',
  marque: 'Peugeot', modele: '208', concession_nom: 'CENTRAUTODIS',
  ville: 'Fort-de-France',
  date_rdv2: (() => { const d = new Date(); d.setDate(d.getDate() + 3); d.setHours(9, 30, 0, 0); return d.toISOString(); })(),
};

function SmsModelesConfig({ canEdit }) {
  const [modeles, setModeles] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // { id?, libelle, corps, ordre, actif }
  const [saving, setSaving] = useState(false);
  const corpsRef = useRef(null);

  const load = () => takataApi.getSmsModeles()
    .then(r => { setModeles(r.data.modeles || []); setVariables(r.data.variables || []); })
    .catch(() => {})
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const nouveau = () => setEdit({ libelle: '', corps: '', ordre: (modeles.length + 1) * 10, actif: true });

  const insererVar = (cle) => {
    const token = `{{${cle}}}`;
    const ta = corpsRef.current;
    const val = edit.corps || '';
    if (!ta) { setEdit(e => ({ ...e, corps: val + token })); return; }
    const start = ta.selectionStart ?? val.length;
    const end = ta.selectionEnd ?? val.length;
    const next = val.slice(0, start) + token + val.slice(end);
    setEdit(e => ({ ...e, corps: next }));
    requestAnimationFrame(() => { ta.focus(); const p = start + token.length; ta.setSelectionRange(p, p); });
  };

  const enregistrer = async () => {
    if (!edit.libelle.trim() || !edit.corps.trim()) return;
    setSaving(true);
    try {
      const payload = { libelle: edit.libelle.trim(), corps: edit.corps, ordre: edit.ordre, actif: edit.actif };
      if (edit.id) await takataApi.updateSmsModele(edit.id, payload);
      else await takataApi.createSmsModele(payload);
      setEdit(null); load();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const supprimer = async (m) => {
    if (!window.confirm(`Supprimer le modèle « ${m.libelle} » ?`)) return;
    try { await takataApi.deleteSmsModele(m.id); load(); }
    catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
  };

  const apercu = edit ? stripAccentsForPreview(renderSmsModele(edit.corps, SMS_SAMPLE)) : '';
  const seg = smsSegments(apercu);

  return (
    <section className="takata-config-section" style={{ marginTop: 24 }}>
      <div className="takata-config-header">
        <div>
          <div className="takata-config-title">Modèles de SMS</div>
          <div className="takata-config-sub">
            Textes précomplétés proposés à l'envoi depuis un dossier. Variables : {variables.map(v => `{{${v.cle}}}`).join(', ')}
          </div>
        </div>
        {canEdit && !edit && <button className="btn-primary btn-sm" onClick={nouveau}>+ Nouveau modèle</button>}
      </div>

      {loading ? <div className="mh-empty">Chargement…</div> : (
        <div className="takata-sms-modeles">
          {modeles.length === 0 && !edit && <div className="takata-config-info">Aucun modèle pour l'instant.</div>}
          {modeles.map(m => (
            <div key={m.id} className="takata-sms-modele-row">
              <div className="takata-sms-modele-main">
                <span className="takata-sms-modele-lib">
                  {m.libelle}{!m.actif && <span className="takata-badge" style={{ marginLeft: 8, background: 'var(--bg-muted)', color: 'var(--text-muted)' }}>inactif</span>}
                </span>
                <span className="takata-sms-modele-corps">{m.corps}</span>
              </div>
              {canEdit && (
                <div className="takata-sms-modele-actions">
                  <button className="btn-secondary btn-sm" onClick={() => setEdit({ ...m })}>Modifier</button>
                  <button className="btn-danger btn-sm" onClick={() => supprimer(m)}>Supprimer</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && edit && (
        <div className="takata-sms-editor">
          <div className="takata-sms-editor-grid">
            <label>Libellé
              <input value={edit.libelle} onChange={e => setEdit(x => ({ ...x, libelle: e.target.value }))} placeholder="Ex. Confirmation RDV" maxLength={120} />
            </label>
            <label style={{ maxWidth: 110 }}>Ordre
              <input type="number" value={edit.ordre} onChange={e => setEdit(x => ({ ...x, ordre: parseInt(e.target.value, 10) || 0 }))} />
            </label>
            <label className="takata-sms-editor-actif">
              <input type="checkbox" checked={!!edit.actif} onChange={e => setEdit(x => ({ ...x, actif: e.target.checked }))} /> Actif
            </label>
          </div>
          <div className="takata-sms-vars">
            {variables.map(v => (
              <button key={v.cle} type="button" className="takata-sms-var-chip" onClick={() => insererVar(v.cle)} title={`Insérer ${v.label}`}>+ {v.label}</button>
            ))}
          </div>
          <textarea
            ref={corpsRef} rows={4} value={edit.corps}
            onChange={e => setEdit(x => ({ ...x, corps: e.target.value }))}
            placeholder="Texte du SMS avec des {{variables}}…" className="takata-sms-corps" maxLength={1000}
          />
          <div className="takata-sms-preview">
            <span className="takata-sms-preview-label">Aperçu (données d'exemple)</span>
            <div className="takata-sms-preview-box">{apercu}</div>
            <span className="takata-sms-preview-meta">{apercu.length} caractères · {seg} SMS</span>
          </div>
          <div className="takata-sms-editor-footer">
            <button className="btn-secondary" onClick={() => setEdit(null)} disabled={saving}>Annuler</button>
            <button className="btn-primary" onClick={enregistrer} disabled={saving || !edit.libelle.trim() || !edit.corps.trim()}>
              {saving ? 'Enregistrement…' : (edit.id ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function TakataAddUserModal({ allUsers, existingIds, onClose, onAdded }) {
  const available = allUsers.filter(u => !existingIds.includes(u.id) && u.role !== 'super-admin' && u.actif !== false);
  const [selectedUser, setSelectedUser] = useState('');
  const [tabs, setTabs] = useState(() => {
    const t = {};
    TAKATA_TABS_DEF.forEach(tab => { t[tab.id] = { can_read: true, can_write: false }; });
    return t;
  });
  const [saving, setSaving] = useState(false);

  const toggleTab = (tabId, type) => {
    setTabs(prev => {
      const cur = prev[tabId] || { can_read: false, can_write: false };
      const np = { ...cur, [type]: !cur[type] };
      if (type === 'can_write' && np.can_write) np.can_read = true;
      if (type === 'can_read' && !np.can_read)  np.can_write = false;
      return { ...prev, [tabId]: np };
    });
  };

  const handleSave = async () => {
    if (!selectedUser) { alert('Sélectionnez un utilisateur'); return; }
    setSaving(true);
    try {
      await takataApi.grantAccess({ user_id: parseInt(selectedUser, 10), tabs });
      onAdded();
    } catch (e) { alert('Erreur : ' + e.message); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Ajouter un utilisateur à Takata</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="si-field">
            <label>Utilisateur *</label>
            <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} style={{ height: 36, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', width: '100%' }}>
              <option value="">— Sélectionner —</option>
              {available.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.role})</option>)}
            </select>
          </div>
          <div className="si-field" style={{ marginTop: 16 }}>
            <label>Accès aux onglets</label>
            <div className="takata-config-add-grid">
              {TAKATA_TABS_DEF.map(t => {
                const p = tabs[t.id] || { can_read: false, can_write: false };
                return (
                  <div key={t.id} className="takata-config-add-row">
                    <span className="takata-config-add-tab">{t.emoji} {t.label}</span>
                    <div className="takata-config-rw">
                      <button className={`takata-config-toggle ${p.can_read ? 'on' : 'off'}`} onClick={() => toggleTab(t.id, 'can_read')}>L</button>
                      <button className={`takata-config-toggle ${p.can_write ? 'on' : 'off'}`} onClick={() => toggleTab(t.id, 'can_write')} disabled={!p.can_read}>E</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || !selectedUser}>
            {saving ? 'Enregistrement…' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

function Takata({ user, editMode, setHeaderAction, initialDossierId = null }) {
  const [mainTab, setMainTab] = useState('dossiers');
  const [dossiers, setDossiers] = useState([]);
  // Jeu de données du Tableau de bord et du Reporting constructeur. Volontairement
  // distinct de `dossiers` : la recherche VIN/immat de l'onglet Dossiers ne doit
  // PAS restreindre ces deux vues, qui raisonnent sur des agrégats globaux et
  // possèdent déjà leurs propres filtres (cf. KNOWLEDGE.md).
  const [dossiersGlobaux, setDossiersGlobaux] = useState([]);
  const [loadingGlobaux, setLoadingGlobaux] = useState(false);
  const [stats, setStats] = useState(null);
  const [concessions, setConcessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtreConstructeur, setFiltreConstructeur] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState([]);
  // Concession du menu (2026-10-26) pour le super-admin / téléopérateur ; les
  // autres comptes gardent le filtre automatique sur leurs concessions Takata
  // (takata_concession_access, appliqué au chargement plus bas).
  const [filtreConcession, setFiltreConcession] = useState(() =>
    (['super-admin', 'teleoperateur'].includes(user?.role) && user?.concession_id) ? [String(user.concession_id)] : []);
  const [search, setSearch] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkStatut, setBulkStatut] = useState('');
  const [bulkEditing, setBulkEditing] = useState(false);
  const [bulkFields, setBulkFields] = useState({ type_approval_number: '', location: '', date_first_letter: '', marque: '', modele: '', recall_code: '' });
  const [bulkApplying, setBulkApplying] = useState(false);

  const [facturesMap, setFacturesMap] = useState({});
  const [allContactsMap, setAllContactsMap] = useState({});
  const [filtreFacture, setFiltreFacture] = useState(false);
  const [factureModal, setFactureModal] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportChoiceModal, setShowExportChoiceModal] = useState(false);
  const [showImportMailevaModal, setShowImportMailevaModal] = useState(false);
  const [courriersFilters, setCourriersFilters] = useState({});
  // null = super-admin (toutes concessions), [] = aucune configurée, [id1,…] = filtre imposé
  const [allowedConcessions, setAllowedConcessions] = useState(null);
  // Droits spécifiques (« rôle custom ») de l'utilisateur connecté — s'ajoutent
  // à son rôle sans le changer. Renvoyés par GET /takata/config/me.
  const [capacites, setCapacites] = useState([]);
  const [filtreDriv, setFiltreDriv] = useState('');
  const [filtrePass, setFiltrePass] = useState('');
  const [filtreCampagne, setFiltreCampagne] = useState([]);
  const [filtreLocation, setFiltreLocation] = useState([]);
  const [sortCol, setSortCol] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  // Reset page quand un filtre ou la recherche change
  React.useEffect(() => { setCurrentPage(1); }, [filtreConstructeur, filtreStatut, filtreConcession, filtreDriv, filtrePass, filtreCampagne, filtreLocation, filtreFacture, search]);

  // Ref pour stocker la liste filtrée+triée (accessible par la modale facture)
  const filteredDossiersRef = React.useRef([]);

  const toggleSort = (col) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIndicator = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ── Colonnes exportables ──
  const EXPORT_COLUMNS = [
    { id: 'vin', label: 'VIN' },
    { id: 'immat', label: 'Immatriculation' },
    { id: 'marque', label: 'Constructeur' },
    { id: 'modele', label: 'Modèle' },
    { id: 'statut', label: 'Statut' },
    { id: 'statut_campagne', label: 'Statut Campagne' },
    { id: 'type_approval_number', label: 'Type Approval Number' },
    { id: 'location', label: 'Location' },
    { id: 'date_first_letter', label: 'Date 1st Letter' },
    { id: 'date_last_letter', label: 'Date Last Letter' },
    { id: 'date_production', label: 'Date de production' },
    { id: 'recall_code', label: 'Recall Code' },
    { id: 'recall_code_driv', label: 'Recall Code Driv' },
    { id: 'recall_code_pass', label: 'Recall Code Pass' },
    { id: 'driv_airbag_concerned', label: 'Airbag Conducteur Concerné' },
    { id: 'driv_airbag_replaced', label: 'Airbag Conducteur Remplacé' },
    { id: 'replacement_date_driv', label: 'Date Remplacement Driv' },
    { id: 'pass_airbag_concerned', label: 'Airbag Passager Concerné' },
    { id: 'passenger_airbag_replaced', label: 'Airbag Passager Remplacé' },
    { id: 'replacement_date_pass', label: 'Date Remplacement Pass' },
    { id: 'stop_drive', label: 'Stop Drive' },
    { id: 'client_nom', label: 'Nom client' },
    { id: 'client_prenom', label: 'Prénom client' },
    { id: 'client_adresse', label: 'Adresse client' },
    { id: 'client_telephone', label: 'Téléphone client' },
    { id: 'client_email', label: 'Email client' },
    { id: 'concession_nom', label: 'Concession' },
    { id: 'assigne_nom_complet', label: 'Assigné à' },
    { id: 'nb_contacts', label: 'Nb contacts' },
    { id: 'dernier_contact', label: 'Dernier contact' },
    { id: 'prochain_rdv', label: 'Prochain RDV' },
    { id: 'dernier_rdv', label: 'Date RDV (le plus récent)' },
    { id: 'demande_info_seulement', label: 'Demande info seulement' },
    { id: 'accident_signale', label: 'Accident signalé' },
    { id: 'facture_date', label: 'Date dernière facture' },
    { id: 'facture_num', label: 'N° facture' },
    { id: 'facture_montant', label: 'Montant HT facture' },
    { id: 'created_at', label: 'Date création' },
    { id: 'updated_at', label: 'Date MAJ' },
  ];

  // Calcul automatique du statut campagne
  const getStatutCampagne = (d) => {
    const drivConcerned = !!d.driv_airbag_concerned;
    const passConcerned = !!d.pass_airbag_concerned;
    const drivDone = !!d.driv_airbag_replaced || !!d.driv_airbag_inspected;
    const passDone = !!d.passenger_airbag_replaced || !!d.pass_airbag_inspected;

    if (drivConcerned && passConcerned) {
      if (drivDone && passDone) return { label: 'Terminé', color: 'var(--color-success-dark)', bg: 'light-dark(#dcfce7, var(--color-success-light))' };
      if (drivDone || passDone) return { label: 'Partiel', color: '#d97706', bg: 'light-dark(#fef3c7, var(--color-warning-light))' };
      return { label: 'A faire', color: 'light-dark(#dc2626, var(--color-error-dark))', bg: 'light-dark(#fee2e2, var(--color-error-light))' };
    }
    if (drivConcerned) {
      if (drivDone) return { label: 'Terminé', color: 'var(--color-success-dark)', bg: 'light-dark(#dcfce7, var(--color-success-light))' };
      return { label: 'A faire', color: 'light-dark(#dc2626, var(--color-error-dark))', bg: 'light-dark(#fee2e2, var(--color-error-light))' };
    }
    if (passConcerned) {
      if (passDone) return { label: 'Terminé', color: 'var(--color-success-dark)', bg: 'light-dark(#dcfce7, var(--color-success-light))' };
      return { label: 'A faire', color: 'light-dark(#dc2626, var(--color-error-dark))', bg: 'light-dark(#fee2e2, var(--color-error-light))' };
    }
    // Aucun airbag concerné
    return { label: 'A faire', color: 'light-dark(#dc2626, var(--color-error-dark))', bg: 'light-dark(#fee2e2, var(--color-error-light))' };
  };

  const sortDossiers = (list) => {
    const sorted = [...list];
    sorted.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      // Champ composé constructeur/modèle
      if (sortCol === 'marque_modele') {
        va = [a.marque, a.modele].filter(Boolean).join(' ');
        vb = [b.marque, b.modele].filter(Boolean).join(' ');
      }
      // Nombre
      if (sortCol === 'nb_contacts') {
        return sortDir === 'asc' ? (Number(va || 0) - Number(vb || 0)) : (Number(vb || 0) - Number(va || 0));
      }
      // Statut campagne (Terminé > Partiel > A faire)
      if (sortCol === 'statut_campagne') {
        const order = { 'Terminé': 2, 'Partiel': 1, 'A faire': 0 };
        const na = order[getStatutCampagne(a).label] || 0;
        const nb = order[getStatutCampagne(b).label] || 0;
        return sortDir === 'asc' ? na - nb : nb - na;
      }
      // Booléen
      if (sortCol === 'driv_airbag_replaced' || sortCol === 'passenger_airbag_replaced') {
        const na = va ? 1 : 0, nb = vb ? 1 : 0;
        return sortDir === 'asc' ? na - nb : nb - na;
      }
      // Tri par date facture
      if (sortCol === 'facture_date') {
        const fa = facturesMap[a.vin], fb = facturesMap[b.vin];
        const da = fa?.date_facture ? new Date(fa.date_facture).getTime() : 0;
        const db = fb?.date_facture ? new Date(fb.date_facture).getTime() : 0;
        return sortDir === 'asc' ? da - db : db - da;
      }
      // Dates
      if (['date_first_letter', 'date_last_letter', 'dernier_contact', 'date_premier_contact', 'prochain_rdv', 'dernier_rdv', 'date_production', 'updated_at'].includes(sortCol)) {
        const da = va ? new Date(va).getTime() : 0, db = vb ? new Date(vb).getTime() : 0;
        return sortDir === 'asc' ? da - db : db - da;
      }
      // String par défaut
      const sa = String(va || '').toLowerCase(), sb = String(vb || '').toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return sorted;
  };

  // Droit spécifique : lever le verrou du marquage « 1er contact », normalement
  // unique et définitif. Le backend applique la même règle (aCapacite).
  const peutModifierPremierContact = capacites.includes('premier_contact_modifier');

  const isAdmin = user?.role === 'super-admin' || user?.role === 'admin' || user?.username === 'lia.bonnemain';
  const isSuperAdmin = user?.role === 'super-admin' || user?.username === 'lia.bonnemain';

  // Boutons d'action dans le header
  useEffect(() => {
    if (!setHeaderAction) return;
    const actions = {};
    actions.primary = { label: '+ Nouveau dossier', onClick: () => setShowCreateModal(true) };
    actions.secondary = [
      { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Import Maileva</>, onClick: () => setShowImportMailevaModal(true) },
      { label: <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export</>, onClick: () => setShowExportChoiceModal(true) },
    ];
    setHeaderAction(actions);
  }, [setHeaderAction]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadConcessions();
    if (isAdmin) loadUsers();
    loadFacturesRecentes();
    loadAllContacts();
    // Chargement des concessions autorisées pour l'utilisateur courant
    takataApi.getConfigMe().then(r => {
      const ac = r.data.allowed_concessions;
      setAllowedConcessions(ac); // null = SA, [] = aucune, [ids] = restreint
      setCapacites(r.data.capacites || []);
      if (ac && ac.length > 0) {
        // Auto-appliquer le filtre de concession dès le chargement
        setFiltreConcession(ac.map(String));
      }
    }).catch(() => {}); // silencieux (l'utilisateur peut ne pas avoir accès)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mainTab === 'dossiers' || mainTab === 'a_rappeler' || mainTab === 'echanges') loadDossiers();
  }, [filtreConstructeur, filtreStatut, filtreConcession, search, mainTab, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aucun filtre de l'onglet Dossiers dans les dépendances : une sélection faite
  // là-bas ne doit ni relancer ni restreindre le Tableau de bord et le Reporting.
  useEffect(() => {
    if (mainTab === 'dashboard' || mainTab === 'reporting') loadDossiersGlobaux();
  }, [mainTab, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDossiers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtreConstructeur.length) params.marque = filtreConstructeur.join(',');
      if (filtreStatut.length) params.statut = filtreStatut.join(',');
      if (filtreConcession.length) params.concession_id = filtreConcession.join(',');
      if (search) params.search = search;

      const [dossiersRes, statsRes] = await Promise.all([
        takataApi.getDossiers(params),
        takataApi.getStats(filtreConcession.length ? { concession_id: filtreConcession.join(',') } : {}),
      ]);
      setDossiers(dossiersRes.data.dossiers);
      setStats(statsRes.data);
      setSelection(new Set());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filtreConstructeur, filtreStatut, filtreConcession, search]);

  // Même requête que loadDossiers, sans le paramètre `search` : le Tableau de bord
  // et le Reporting constructeur restent sur le périmètre complet (les filtres de
  // concession restent appliqués — c'est le périmètre d'accès de l'utilisateur).
  // Jeu de données du Tableau de bord et du Reporting constructeur : TOUS les
  // dossiers accessibles, sans les filtres de l'onglet Dossiers (constructeur,
  // statut, concession, recherche). Ces filtres n'y sont pas visibles et chaque
  // vue a ses propres filtres ; les appliquer ici restreignait le Tableau de bord
  // à une marque sans qu'aucune sélection n'y apparaisse (2026-10-13). Le
  // cloisonnement par concession reste appliqué côté serveur (droits d'accès).
  const loadDossiersGlobaux = useCallback(async () => {
    setLoadingGlobaux(true);
    try {
      const res = await takataApi.getDossiers({});
      setDossiersGlobaux(res.data.dossiers);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingGlobaux(false);
    }
  }, []);

  const loadConcessions = async () => {
    try { const res = await concessionsApi.getAll(); setConcessions(res.data.concessions || []); }
    catch (err) { console.error(err); }
  };

  const loadUsers = async () => {
    try { const res = await usersApi.getAll(); setUsers(res.data.users || []); }
    catch (err) { console.error(err); }
  };

  const loadFacturesRecentes = async () => {
    try {
      const r = await takataApi.getFacturesRecentes();
      setFacturesMap(r.data.factures || {});
    } catch (e) { console.error(e); }
  };

  const loadAllContacts = async () => {
    try {
      const r = await takataApi.getAllContacts();
      setAllContactsMap(r.data.contacts || {});
    } catch (e) { console.error(e); }
  };

  // Ouverture directe depuis un deep-link (?page=takata&dossier=<id>)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialDossierId) return;
    takataApi.getDossier(initialDossierId)
      .then(r => { setSelectedDossier(r.data.dossier); setShowDetailModal(true); })
      .catch(() => {});
  }, []);

  const openDetail = (dossier) => {
    setSelectedDossier(dossier);
    setShowDetailModal(true);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 'takata');
    params.set('dossier', String(dossier.id));
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedDossier(null);
    const params = new URLSearchParams(window.location.search);
    params.delete('dossier');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const handleDossierCreated = (dossier) => {
    setShowCreateModal(false);
    loadDossiers();
    openDetail(dossier);
  };

  const handleGeler = async () => {
    if (!selectedDossier || !window.confirm('Confirmer le gel de ce dossier (accident signalé) ?')) return;
    try {
      const res = await takataApi.gelerDossier(selectedDossier.id);
      setSelectedDossier(res.data.dossier);
      loadDossiers();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du gel');
    }
  };

  // Accès refusé : utilisateur non super-admin sans aucune concession configurée
  if (allowedConcessions !== null && allowedConcessions.length === 0) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: 'var(--text-primary)', marginBottom: 8 }}>Accès non autorisé</h2>
          <p>Votre compte n'est rattaché à aucune concession Takata.<br />Contactez un administrateur pour obtenir les accès.</p>
        </div>
      </div>
    );
  }


  return (
    <div className="page-container">
      {/* ── Onglets principaux (responsive overflow menu) ── */}
      <TakataTabBar activeTab={mainTab} onTabChange={setMainTab} />

      {/* ══ TABLEAU DE BORD ══ */}
      {mainTab === 'dashboard' && (
        <>
          <TakataKpiDashboard />
          {(loadingGlobaux && dossiersGlobaux.length === 0)
            ? <div className="takata-loading">Chargement…</div>
            : <TakataDashboard dossiers={dossiersGlobaux} getStatutCampagne={getStatutCampagne} />}
        </>
      )}

      {/* ══ DOSSIERS / A RAPPELER ══ */}
      {(mainTab === 'dossiers' || mainTab === 'a_rappeler') && (
        <>
          {stats && <StatsBar stats={stats} />}
          <div className="page-toolbar">
            <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
            <MultiSelectFilter
              label="Tous constructeurs"
              options={CONSTRUCTEURS.map(c => ({ value: c, label: c }))}
              selected={filtreConstructeur}
              onChange={setFiltreConstructeur}
            />
            <MultiSelectFilter
              label="Tous statuts"
              options={STATUTS.map(s => ({ value: s.value, label: s.label }))}
              selected={filtreStatut}
              onChange={setFiltreStatut}
            />
            {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
            <MultiSelectFilter
              label="Toutes locations"
              options={[...new Set(dossiers.map(d => d.location).filter(Boolean))].sort().map(loc => ({ value: loc, label: loc }))}
              selected={filtreLocation}
              onChange={setFiltreLocation}
            />
            <MultiSelectFilter
              label="Campagne — tous"
              options={[
                { value: 'Terminé', label: '✅ Terminé' },
                { value: 'Partiel', label: '⚠️ Partiel' },
                { value: 'A faire', label: '❌ A faire' },
              ]}
              selected={filtreCampagne}
              onChange={setFiltreCampagne}
            />
            <button
              className={`btn-filter${filtreFacture ? ' active' : ''}`}
              onClick={() => setFiltreFacture(v => !v)}
              title="Afficher uniquement les dossiers avec une facture atelier récente (≤ 12 mois)"
            >
              🧾 Factures récentes
            </button>
          </div>
          {showExportModal && (
            <ExportModal
              columns={EXPORT_COLUMNS}
              dossiers={dossiers}
              facturesMap={facturesMap}
              allContactsMap={allContactsMap}
              getStatutCampagne={getStatutCampagne}
              filters={{ filtreConstructeur, filtreStatut, filtreConcession, filtreLocation, filtreFacture, filtreCampagne, search }}
              onClose={() => setShowExportModal(false)}
            />
          )}

          {editMode && isSuperAdmin && selection.size > 0 && (
            <div className="takata-bulk-bar">
              <span className="takata-bulk-count">
                {selection.size} dossier{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}
              </span>

              {/* ── Modifier le statut ── */}
              <div className="takata-bulk-group">
                <select
                  className="takata-bulk-select"
                  value={bulkStatut}
                  onChange={e => setBulkStatut(e.target.value)}
                >
                  <option value="">— Changer le statut —</option>
                  {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button
                  className="btn-primary btn-sm"
                  disabled={!bulkStatut || bulkEditing}
                  onClick={async () => {
                    const ids = [...selection];
                    const label = STATUTS.find(s => s.value === bulkStatut)?.label || bulkStatut;
                    if (!window.confirm(`Passer ${ids.length} dossier${ids.length > 1 ? 's' : ''} en statut "${label}" ?`)) return;
                    setBulkEditing(true);
                    try {
                      await takataApi.updateStatutBulk(ids, bulkStatut);
                      setBulkStatut('');
                      setRefreshTrigger(t => t + 1);
                    } catch (err) {
                      alert(err.response?.data?.error || 'Erreur lors de la modification');
                    } finally { setBulkEditing(false); }
                  }}
                >
                  {bulkEditing ? 'Modification…' : '✏️ Modifier statut'}
                </button>
              </div>

              {/* ── Modifier champs en masse ── */}
              <div className="takata-bulk-fields">
                <input placeholder="Type Approval Number" value={bulkFields.type_approval_number} onChange={e => setBulkFields(f => ({ ...f, type_approval_number: e.target.value }))} />
                <input placeholder="Location" value={bulkFields.location} onChange={e => setBulkFields(f => ({ ...f, location: e.target.value }))} />
                <input type="date" title="Date 1st Letter" value={bulkFields.date_first_letter} onChange={e => setBulkFields(f => ({ ...f, date_first_letter: e.target.value }))} />
                {/* Date Last Letter retirée de l'édition en masse : champ dérivé des courriers
                    (le back la refuse aussi, elle n'est plus dans la whitelist ALLOWED). */}
                <input placeholder="Constructeur" value={bulkFields.marque} onChange={e => setBulkFields(f => ({ ...f, marque: e.target.value }))} />
                <input placeholder="Modèle" value={bulkFields.modele} onChange={e => setBulkFields(f => ({ ...f, modele: e.target.value }))} />
                <input placeholder="Recall Code" value={bulkFields.recall_code} onChange={e => setBulkFields(f => ({ ...f, recall_code: e.target.value }))} />
                <button
                  className="btn-primary btn-sm"
                  disabled={bulkApplying || !Object.values(bulkFields).some(v => v !== '')}
                  onClick={async () => {
                    const ids = [...selection];
                    const fields = {};
                    for (const [k, v] of Object.entries(bulkFields)) { if (v !== '') fields[k] = v; }
                    if (!Object.keys(fields).length) return;
                    const fieldNames = Object.keys(fields).join(', ');
                    if (!window.confirm(`Modifier ${fieldNames} sur ${ids.length} dossier${ids.length > 1 ? 's' : ''} ?`)) return;
                    setBulkApplying(true);
                    try {
                      await takataApi.updateBulk(ids, fields);
                      setBulkFields({ type_approval_number: '', location: '', date_first_letter: '', marque: '', modele: '', recall_code: '' });
                      setRefreshTrigger(t => t + 1);
                    } catch (err) {
                      alert(err.response?.data?.error || 'Erreur lors de la modification en masse');
                    } finally { setBulkApplying(false); }
                  }}
                >
                  {bulkApplying ? 'Application…' : '✏️ Appliquer'}
                </button>
              </div>

              {/* ── Supprimer ── */}
              <button className="btn-danger btn-sm" disabled={bulkDeleting} onClick={async () => {
                const ids = [...selection];
                if (!window.confirm(`Supprimer définitivement ${ids.length} dossier${ids.length > 1 ? 's' : ''} ?\n\nCette action est irréversible.`)) return;
                setBulkDeleting(true);
                try {
                  await takataApi.deleteDossiersBulk(ids);
                  setRefreshTrigger(t => t + 1);
                } catch (err) {
                  alert(err.response?.data?.error || 'Erreur lors de la suppression');
                } finally { setBulkDeleting(false); }
              }}>
                {bulkDeleting ? 'Suppression…' : `🗑 Supprimer`}
              </button>

              <button className="btn-secondary btn-sm" onClick={() => { setSelection(new Set()); setBulkStatut(''); setBulkFields({ type_approval_number: '', location: '', date_first_letter: '', marque: '', modele: '', recall_code: '' }); }}>Annuler</button>
            </div>
          )}

          {loading ? <div className="takata-loading">Chargement…</div> : (() => {
            const dossiersAffiches = dossiers.filter(d => {
              if (mainTab === 'a_rappeler' && !d.a_rappeler) return false;
              if (filtreFacture && !facturesMap[d.vin]) return false;
              if (filtreLocation.length && !filtreLocation.includes(d.location)) return false;
              if (filtreCampagne.length && !filtreCampagne.includes(getStatutCampagne(d).label)) return false;
              return true;
            });
            const sorted = sortDossiers(dossiersAffiches);
            filteredDossiersRef.current = sorted;
            const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
            const safePage = Math.min(currentPage, totalPages);
            const paginated = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
            return (
            <>
            <div className="table-container">
              <table className="takata-table">
                <thead>
                  <tr>
                    {editMode && isSuperAdmin && (
                      <th className="takata-th-check" onClick={e => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={dossiersAffiches.length > 0 && selection.size === dossiersAffiches.length}
                          ref={el => { if (el) el.indeterminate = selection.size > 0 && selection.size < dossiersAffiches.length; }}
                          onChange={e => setSelection(e.target.checked ? new Set(dossiersAffiches.map(d => d.id)) : new Set())}
                        />
                      </th>
                    )}
                    <th className="takata-th-sort" onClick={() => toggleSort('vin')}>VIN / Immat{sortIndicator('vin')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('marque_modele')}>Constructeur / Modèle{sortIndicator('marque_modele')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('location')}>Location{sortIndicator('location')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('statut')}>Statut{sortIndicator('statut')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('driv_airbag_replaced')} title="Driver airbag replaced">DRIV.{sortIndicator('driv_airbag_replaced')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('passenger_airbag_replaced')} title="Passenger airbag replaced">PASS.{sortIndicator('passenger_airbag_replaced')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('statut_campagne')}>Statut Campagne{sortIndicator('statut_campagne')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('date_premier_contact')}>1er contact{sortIndicator('date_premier_contact')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('dernier_contact')}>Dernier contact{sortIndicator('dernier_contact')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('dernier_rdv')}>Date RDV{sortIndicator('dernier_rdv')}</th>
                    <th className="takata-th-sort" onClick={() => toggleSort('facture_date')}>Facture{sortIndicator('facture_date')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {dossiersAffiches.length === 0 && <tr><td colSpan={(editMode && isSuperAdmin) ? 14 : 13} className="takata-empty">Aucun dossier</td></tr>}
                  {paginated.map(d => {
                    const st = statutInfo(d.statut);
                    const checked = selection.has(d.id);
                    const facture = facturesMap[d.vin];
                    return (
                      <tr key={d.id} className={`takata-row${checked ? ' takata-row--selected' : ''}`} onClick={() => openDetail(d)}>
                        {editMode && isSuperAdmin && (
                          <td className="takata-td-check" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={checked} onChange={e => {
                              setSelection(prev => {
                                const next = new Set(prev);
                                e.target.checked ? next.add(d.id) : next.delete(d.id);
                                return next;
                              });
                            }} />
                          </td>
                        )}
                        <td className="takata-vin-cell">
                          <div className="takata-vin"><Copyable value={d.vin} titre="Copier le VIN">{d.vin}</Copyable></div>
                          <div className="takata-immat"><Copyable value={d.immat} titre="Copier l'immatriculation">{d.immat || ''}</Copyable></div>
                        </td>
                        <td>{[d.marque, d.modele].filter(Boolean).join(' / ') || '—'}</td>
                        <td>{d.location || '—'}</td>
                        <td><span className="takata-badge" style={{ background: fondStatut(st.color), color: texteTheme(st.color) }}>{d.statut === 'gele' && '🔒 '}{st.label}</span></td>
                        <td className="takata-center">
                          {d.driv_airbag_concerned
                            ? <input type="checkbox" checked={!!d.driv_airbag_replaced} readOnly style={{ accentColor: '#22c55e', cursor: 'default' }} />
                            : '—'}
                        </td>
                        <td className="takata-center">
                          {d.pass_airbag_concerned
                            ? <input type="checkbox" checked={!!d.passenger_airbag_replaced} readOnly style={{ accentColor: '#22c55e', cursor: 'default' }} />
                            : '—'}
                        </td>
                        <td className="takata-center">
                          {(() => { const sc = getStatutCampagne(d); return <span className="takata-badge" style={{ background: sc.bg, color: sc.color, fontWeight: 600 }}>{sc.label}</span>; })()}
                        </td>
                        <td>{formatDate(d.date_premier_contact)}</td>
                        <td>{formatDate(d.dernier_contact)}</td>
                        <td>{formatDate(d.dernier_rdv)}</td>
                        <td className="takata-fact-cell" onClick={e => e.stopPropagation()}>
                          {facture
                            ? <span className="takata-fact-inline">
                                <span className="takata-fact-date">{facture.date_facture ? formatDate(facture.date_facture) : '—'}</span>
                                <button className="takata-fact-btn" onClick={() => setFactureModal({ facture, dossier: d })} title={`Facture ${facture.num_facture}`}>🧾</button>
                              </span>
                            : null}
                        </td>
                        <td className="takata-actions" onClick={e => e.stopPropagation()}>
                          <button className="btn-icon" onClick={() => openDetail(d)} title="Voir le détail">👁️</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="takata-pagination">
                <button disabled={safePage <= 1} onClick={() => setCurrentPage(1)}>⏮</button>
                <button disabled={safePage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>◀</button>
                <span className="takata-pagination-info">
                  Page {safePage} / {totalPages} — <strong>{dossiersAffiches.length}</strong> résultat{dossiersAffiches.length > 1 ? 's' : ''}
                </span>
                <button disabled={safePage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>▶</button>
                <button disabled={safePage >= totalPages} onClick={() => setCurrentPage(totalPages)}>⏭</button>
              </div>
            )}
            {totalPages <= 1 && dossiersAffiches.length > 0 && (
              <div className="takata-pagination">
                <span className="takata-pagination-info"><strong>{dossiersAffiches.length}</strong> résultat{dossiersAffiches.length > 1 ? 's' : ''}</span>
              </div>
            )}
            </>
          );
          })()}
        </>
      )}

      {/* ══ STOCKS ══ */}
      {mainTab === 'stocks' && <StocksView user={user} concessions={concessions} isAdmin={isAdmin} />}

      {/* ══ BUDGET ══ */}
      {mainTab === 'budget' && <BudgetView concessions={concessions} isAdmin={isAdmin} user={user} />}

      {/* ══ WEBAPP DETECTIONS ══ */}
      {mainTab === 'webapp' && <WebappDetectionsView onOpenDossier={(d) => { setSelectedDossier(d); setShowDetailModal(true); }} />}

      {/* ══ COURRIERS GLOBAL ══ */}
      {mainTab === 'courriers_global' && <CourriersGlobalView user={user} onOpenDossier={(d) => { setSelectedDossier(d); setShowDetailModal(true); }} onFiltersChange={setCourriersFilters} />}

      {/* ══ REPORTING CONSTRUCTEUR ══ */}
      {mainTab === 'reporting' && (
        (loadingGlobaux && dossiersGlobaux.length === 0)
          ? <div className="takata-loading">Chargement…</div>
          : <ReportingConstructeurView dossiers={dossiersGlobaux} />
      )}

      {/* ══ ECHANGES INTERNES ══ */}
      {mainTab === 'echanges' && <EchangesInternesView dossiers={dossiers} user={user} users={users} />}

      {/* ══ CONFIGURATION ══ */}
      {mainTab === 'config' && <TakataConfigView user={user} allUsers={users} />}


      {/* ══ IMPORT ══ */}
      {mainTab === 'import' && <ImportView concessions={concessions} isAdmin={isAdmin} onImported={() => {
        setFiltreConstructeur('');
        setFiltreStatut('');
        setFiltreConcession('');
        setSearch('');
        setMainTab('dossiers');
        setRefreshTrigger(t => t + 1);
      }} />}

      {/* ══ CASSES ══ */}
      {mainTab === 'casses' && <CassesView user={user} editMode={editMode} onOpenDossier={(id) => takataApi.getDossier(id).then(r => openDetail(r.data.dossier)).catch(() => {})} onImported={() => setRefreshTrigger(t => t + 1)} />}

      {/* ── Modales ── */}
      {showExportModal && (
        <ExportModal
          columns={EXPORT_COLUMNS}
          dossiers={dossiers}
          facturesMap={facturesMap}
          getStatutCampagne={getStatutCampagne}
          filters={{ filtreConstructeur, filtreStatut, filtreConcession, filtreLocation, filtreFacture, filtreCampagne, search }}
          onClose={() => setShowExportModal(false)}
        />
      )}
      {showExportChoiceModal && (
        <ExportChoiceModal
          dossiers={dossiers}
          filters={{ filtreConstructeur, filtreStatut, filtreConcession, search }}
          courriersFilters={courriersFilters}
          onClose={() => setShowExportChoiceModal(false)}
        />
      )}
      {showCreateModal && (
        <CreateDossierModal concessions={concessions} onClose={() => setShowCreateModal(false)} onCreated={handleDossierCreated} />
      )}
      {showDetailModal && selectedDossier && (
        <DetailDossierModal
          key={selectedDossier.id}
          dossier={selectedDossier}
          user={user}
          isAdmin={isAdmin}
          concessions={concessions}
          users={users}
          dossiers={filteredDossiersRef.current}
          peutModifierPremierContact={peutModifierPremierContact}
          onNavigate={(d) => openDetail(d)}
          onClose={closeDetail}
          onGeler={handleGeler}
          onDossierUpdated={(d) => { setSelectedDossier(d); loadDossiers(); }}
          onDeleted={() => { closeDetail(); setRefreshTrigger(t => t + 1); }}
        />
      )}
      {showImportMailevaModal && (
        <ImportMailevaModal onClose={() => setShowImportMailevaModal(false)} onImported={() => setRefreshTrigger(t => t + 1)} />
      )}
      {factureModal && (
        <FactureModal
          facture={factureModal.facture}
          dossier={factureModal.dossier}
          dossiers={filteredDossiersRef.current}
          facturesMap={facturesMap}
          getStatutCampagne={getStatutCampagne}
          onClose={() => setFactureModal(null)}
          onNavigate={(d) => setFactureModal({ facture: facturesMap[d.vin], dossier: d })}
          onDossierPatched={(id, fields) => {
            setDossiers(prev => prev.map(d => d.id === id ? { ...d, ...fields } : d));
            setFactureModal(prev => prev ? { ...prev, dossier: { ...prev.dossier, ...fields } } : null);
          }}
        />
      )}
    </div>
  );
}

// ─── Modal aperçu facture atelier ─────────────────────────────────────────────

function FactureModal({ facture, dossier, dossiers, facturesMap, getStatutCampagne, onClose, onNavigate, onDossierPatched }) {
  // Liste navigable : dossiers filtrés (depuis le tableau) qui ont une facture
  const navigable = React.useMemo(() => {
    return (dossiers || []).filter(d => {
      if (!facturesMap[d.vin]) return false;
      return true;
    });
  }, [dossiers, facturesMap, getStatutCampagne]);

  const currentIdx = navigable.findIndex(d => d.id === dossier.id);
  const prevDossier = currentIdx > 0 ? navigable[currentIdx - 1] : null;
  const nextDossier = currentIdx >= 0 && currentIdx < navigable.length - 1 ? navigable[currentIdx + 1] : null;

  const lignes = Array.isArray(facture.lignes_json) ? facture.lignes_json : [];
  const dateStr = facture.date_facture ? formatDate(facture.date_facture) : '—';
  const montant = facture.montant_ht != null
    ? Number(facture.montant_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    : '—';

  const [airbag, setAirbag] = useState({
    driv_airbag_replaced:    !!dossier.driv_airbag_replaced,
    driv_airbag_inspected:   !!dossier.driv_airbag_inspected,
    replacement_date_driv:   dossier.replacement_date_driv ? dossier.replacement_date_driv.split('T')[0] : '',
    passenger_airbag_replaced: !!dossier.passenger_airbag_replaced,
    pass_airbag_inspected:   !!dossier.pass_airbag_inspected,
    replacement_date_pass:   dossier.replacement_date_pass ? dossier.replacement_date_pass.split('T')[0] : '',
    a_rappeler:              !!dossier.a_rappeler,
  });
  const [saving, setSaving] = useState(false);

  // Reset state quand on navigue vers un autre dossier
  React.useEffect(() => {
    setAirbag({
      driv_airbag_replaced:      !!dossier.driv_airbag_replaced,
      driv_airbag_inspected:     !!dossier.driv_airbag_inspected,
      replacement_date_driv:     dossier.replacement_date_driv ? dossier.replacement_date_driv.split('T')[0] : '',
      passenger_airbag_replaced: !!dossier.passenger_airbag_replaced,
      pass_airbag_inspected:     !!dossier.pass_airbag_inspected,
      replacement_date_pass:     dossier.replacement_date_pass ? dossier.replacement_date_pass.split('T')[0] : '',
      a_rappeler:                !!dossier.a_rappeler,
    });
  }, [dossier.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (fields) => {
    setSaving(true);
    try {
      await takataApi.patchAirbag(dossier.id, fields);
      onDossierPatched(dossier.id, fields);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  // Date par défaut = date de la facture si disponible, sinon hier
  const defaultDate = facture.date_facture ? facture.date_facture.split('T')[0] : yesterday();

  const toggleDriv = (val) => {
    const date = val && !airbag.replacement_date_driv ? defaultDate : airbag.replacement_date_driv;
    setAirbag(p => ({ ...p, driv_airbag_replaced: val, driv_airbag_inspected: val ? false : p.driv_airbag_inspected, replacement_date_driv: date }));
    patch({ driv_airbag_replaced: val, driv_airbag_inspected: val ? false : airbag.driv_airbag_inspected, replacement_date_driv: date || null });
  };

  const toggleDrivInspected = (val) => {
    const date = val && !airbag.replacement_date_driv ? defaultDate : airbag.replacement_date_driv;
    setAirbag(p => ({ ...p, driv_airbag_inspected: val, driv_airbag_replaced: val ? false : p.driv_airbag_replaced, replacement_date_driv: date }));
    patch({ driv_airbag_inspected: val, driv_airbag_replaced: val ? false : airbag.driv_airbag_replaced, replacement_date_driv: date || null });
  };

  const togglePass = (val) => {
    const date = val && !airbag.replacement_date_pass ? defaultDate : airbag.replacement_date_pass;
    setAirbag(p => ({ ...p, passenger_airbag_replaced: val, pass_airbag_inspected: val ? false : p.pass_airbag_inspected, replacement_date_pass: date }));
    patch({ passenger_airbag_replaced: val, pass_airbag_inspected: val ? false : airbag.pass_airbag_inspected, replacement_date_pass: date || null });
  };

  const togglePassInspected = (val) => {
    const date = val && !airbag.replacement_date_pass ? defaultDate : airbag.replacement_date_pass;
    setAirbag(p => ({ ...p, pass_airbag_inspected: val, passenger_airbag_replaced: val ? false : p.passenger_airbag_replaced, replacement_date_pass: date }));
    patch({ pass_airbag_inspected: val, passenger_airbag_replaced: val ? false : airbag.passenger_airbag_replaced, replacement_date_pass: date || null });
  };

  const hasDriv = dossier.driv_airbag_concerned;
  const hasPass = dossier.pass_airbag_concerned;

  return (
    <div className="modal-overlay">
      <div className="modal-content takata-modal--facture takata-modal--facture-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <button className="takata-nav-btn" disabled={!prevDossier} onClick={() => prevDossier && onNavigate(prevDossier)} title="Dossier précédent">◀</button>
          <span className="takata-modal-title">🧾 Facture {facture.num_facture || '—'} — {dossier.vin}{navigable.length > 0 ? ` (${currentIdx + 1}/${navigable.length})` : ''}</span>
          <button className="takata-nav-btn" disabled={!nextDossier} onClick={() => nextDossier && onNavigate(nextDossier)} title="Dossier suivant">▶</button>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="fact-modal-body">

          {/* ── Gauche : contenu facture ── */}
          <div className="fact-left">
            <div className="fact-summary fact-summary--wide">
              <div><span className="fact-lbl">Date</span><span className="fact-val">{dateStr}</span></div>
              <div><span className="fact-lbl">N° Facture</span><span className="fact-val">{facture.num_facture || '—'}</span></div>
              <div><span className="fact-lbl">N° OR</span><span className="fact-val">{facture.num_or || '—'}</span></div>
              <div><span className="fact-lbl">Client</span><span className="fact-val">{facture.nom_client || '—'}</span></div>
              <div><span className="fact-lbl">Téléphone</span><span className="fact-val">{dossier.client_telephone || '—'}</span></div>
              <div><span className="fact-lbl">E-mail</span><span className="fact-val">{dossier.client_email || '—'}</span></div>
              <div><span className="fact-lbl">Immat</span><span className="fact-val">{facture.immatriculation || dossier.immat || '—'}</span></div>
              <div><span className="fact-lbl">VIN</span><span className="fact-val" style={{ fontSize: '0.75rem' }}>{facture.vin || dossier.vin || '—'}</span></div>
              <div><span className="fact-lbl">Marque / Modèle</span><span className="fact-val">{[dossier.marque, dossier.modele].filter(Boolean).join(' ') || '—'}</span></div>
              <div><span className="fact-lbl">Montant HT</span><span className="fact-val">{montant}</span></div>
            </div>
            {lignes.length > 0 && (
              <div style={{ overflowX: 'auto', padding: '0 16px 16px' }}>
                <table className="fact-lines-table">
                  <thead>
                    <tr>
                      <th>Prestation</th>
                      <th>Désignation</th>
                      <th>Réf</th>
                      <th>Qté</th>
                      <th>Montant HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((l, i) => (
                      <tr key={i}>
                        <td>{l.prestation || l.type || '—'}</td>
                        <td>{l.designation || l.libelle || l.description || '—'}</td>
                        <td>{l.reference || l.ref || '—'}</td>
                        <td>{l.quantite ?? l.qte ?? '—'}</td>
                        <td>{l.montant_ht != null ? Number(l.montant_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {lignes.length === 0 && (
              <p style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Aucune ligne de prestation disponible.</p>
            )}
          </div>

          {/* ── Droite : airbags ── */}
          <div className="fact-airbag-panel">
            <div className="fact-airbag-title">
              🛡 Airbags
              {saving && <span className="fact-saving">enregistrement…</span>}
            </div>

            {/* ── Conducteur ── */}
            <div className="fact-airbag-section">
              <div className="fact-airbag-lbl">Conducteur</div>
              <div className="fact-airbag-meta">
                <span className={`airbag-status-tag ${hasDriv ? 'tag-yes' : 'tag-no'}`}>
                  {hasDriv ? '✓ Concerné' : '— Non concerné'}
                </span>
                {dossier.recall_code_driv && (
                  <span className="recall-badge">{dossier.recall_code_driv}</span>
                )}
              </div>
              {hasDriv && (
                <>
                  <label className="fact-airbag-check">
                    <input type="checkbox" checked={airbag.driv_airbag_replaced}
                      onChange={e => toggleDriv(e.target.checked)} />
                    Airbag remplacé
                  </label>
                  <label className="fact-airbag-check">
                    <input type="checkbox" checked={airbag.driv_airbag_inspected}
                      onChange={e => toggleDrivInspected(e.target.checked)} />
                    Airbag inspecté
                  </label>
                  {(airbag.driv_airbag_replaced || airbag.driv_airbag_inspected) && (
                    <div className="fact-airbag-date">
                      <span className="fact-lbl">Date remplacement</span>
                      <input type="date" className="fact-date-input"
                        value={airbag.replacement_date_driv}
                        onChange={e => {
                          const val = e.target.value;
                          setAirbag(p => ({ ...p, replacement_date_driv: val }));
                          patch({ replacement_date_driv: val || null });
                        }} />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Passager ── */}
            <div className="fact-airbag-section">
              <div className="fact-airbag-lbl">Passager</div>
              <div className="fact-airbag-meta">
                <span className={`airbag-status-tag ${hasPass ? 'tag-yes' : 'tag-no'}`}>
                  {hasPass ? '✓ Concerné' : '— Non concerné'}
                </span>
                {dossier.recall_code_pass && (
                  <span className="recall-badge">{dossier.recall_code_pass}</span>
                )}
              </div>
              {hasPass && (
                <>
                  <label className="fact-airbag-check">
                    <input type="checkbox" checked={airbag.passenger_airbag_replaced}
                      onChange={e => togglePass(e.target.checked)} />
                    Airbag remplacé
                  </label>
                  <label className="fact-airbag-check">
                    <input type="checkbox" checked={airbag.pass_airbag_inspected}
                      onChange={e => togglePassInspected(e.target.checked)} />
                    Airbag inspecté
                  </label>
                  {(airbag.passenger_airbag_replaced || airbag.pass_airbag_inspected) && (
                    <div className="fact-airbag-date">
                      <span className="fact-lbl">Date remplacement</span>
                      <input type="date" className="fact-date-input"
                        value={airbag.replacement_date_pass}
                        onChange={e => {
                          const val = e.target.value;
                          setAirbag(p => ({ ...p, replacement_date_pass: val }));
                          patch({ replacement_date_pass: val || null });
                        }} />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── A rappeler ── */}
            <div className="fact-airbag-section" style={{ borderTop: '2px solid var(--border-default)', paddingTop: 12, marginTop: 8 }}>
              <label className="fact-airbag-check" style={{ fontSize: '0.95rem', fontWeight: 600 }}>
                <input type="checkbox" checked={airbag.a_rappeler}
                  style={{ accentColor: '#f59e0b', width: 18, height: 18 }}
                  onChange={e => {
                    const val = e.target.checked;
                    setAirbag(p => ({ ...p, a_rappeler: val }));
                    patch({ a_rappeler: val });
                  }} />
                📞 A rappeler
              </label>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Barre de stats ───────────────────────────────────────────────────────────

function StatsBar({ stats }) {
  return (
    <div className="takata-stats-bar">
      <div className="takata-stat"><span className="takata-stat-val">{(stats.total || 0).toLocaleString('fr-FR')}</span><span className="takata-stat-lbl">Total</span></div>
      <div className="takata-stat takata-stat--warn"><span className="takata-stat-val">{(stats.sans_contact || 0).toLocaleString('fr-FR')}</span><span className="takata-stat-lbl">Sans contact</span></div>
      <div className="takata-stat takata-stat--alert"><span className="takata-stat-val">{(stats.depassement_15j || 0).toLocaleString('fr-FR')}</span><span className="takata-stat-lbl">Dépassement 15j</span></div>
      <div className="takata-stat takata-stat--danger"><span className="takata-stat-val">{(stats.depassement_60j || 0).toLocaleString('fr-FR')}</span><span className="takata-stat-lbl">Dépassement 60j</span></div>
      <div className="takata-stats-breakdown">
        {(stats.par_statut || []).map(s => {
          const info = statutInfo(s.statut);
          return <span key={s.statut} className="takata-stat-pill" style={{ background: fondStatut(info.color), color: texteTheme(info.color) }}>{info.label} : <strong>{(s.nb || 0).toLocaleString('fr-FR')}</strong></span>;
        })}
      </div>
    </div>
  );
}

// ─── Modal création dossier ───────────────────────────────────────────────────

function CreateDossierModal({ concessions, onClose, onCreated, initialImmat = '', initialVin = '' }) {
  const [form, setForm] = useState({
    vin: initialVin || '', immat: initialImmat || '', marque: '', modele: '', concession_id: '',
    date_production: '',
    type_approval_number: '', location: '', recall_code: '',
    // Pas de date_last_letter : champ dérivé des courriers du dossier (cf. EditDossierForm).
    stop_drive: false, date_first_letter: '',
    driv_airbag_concerned: false, driv_airbag_replaced: false,
    replacement_date_driv: '', recall_code_driv: '',
    pass_airbag_concerned: false, passenger_airbag_replaced: false,
    replacement_date_pass: '', recall_code_pass: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vin.trim()) { setError('Le VIN est obligatoire.'); return; }
    if (!form.concession_id) { setError('La Concession est obligatoire.'); return; }
    if (!form.location) { setError('La Location (971 / 972) est obligatoire.'); return; }
    setSaving(true); setError('');
    try {
      const res = await takataApi.createDossier({ ...form, vin: form.vin.trim().toUpperCase(), concession_id: form.concession_id || null });
      onCreated(res.data.dossier);
    } catch (err) {
      if (err.response?.status === 409) setError('Un dossier existe déjà pour ce VIN (ID #' + err.response.data.dossier_id + ').');
      else setError(err.response?.data?.error || 'Erreur lors de la création.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="takata-modal-title">Nouveau dossier TAKATA</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body takata-modal-body">
          {error && <div className="takata-error">{error}</div>}

          <div className="takata-section-hd">Véhicule</div>
          <div className="takata-form-grid">
            <div className="takata-fg takata-fg--wide"><label>VIN <span className="req">*</span></label><input value={form.vin} onChange={e => set('vin', e.target.value)} placeholder="17 caractères" maxLength={17} /></div>
            <div className="takata-fg"><label>Immatriculation</label><input value={form.immat} onChange={e => set('immat', normalizeImmat(e.target.value))} placeholder="Ex : AB123CD" /></div>
            <div className="takata-fg"><label>Marque</label><select value={form.marque} onChange={e => set('marque', e.target.value)}><option value="">— sélectionner —</option>{CONSTRUCTEURS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div className="takata-fg"><label>Modèle</label><input value={form.modele} onChange={e => set('modele', e.target.value)} /></div>
            <div className="takata-fg"><label>Concession <span className="req">*</span></label><select value={form.concession_id} onChange={e => set('concession_id', e.target.value)}><option value="">— sélectionner —</option>{concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}</select></div>
            <div className="takata-fg"><label>Date de production</label><input type="date" value={form.date_production} onChange={e => set('date_production', e.target.value)} /></div>
            <div className="takata-fg"><label>Type Approval Number</label><input value={form.type_approval_number} onChange={e => set('type_approval_number', e.target.value)} /></div>
            <div className="takata-fg"><label>Location <span className="req">*</span></label><select value={form.location} onChange={e => set('location', e.target.value)}><option value="">— sélectionner —</option><option value="971">971</option><option value="972">972</option></select></div>
            <div className="takata-fg"><label>Date 1st Letter</label><input type="date" value={form.date_first_letter} onChange={e => set('date_first_letter', e.target.value)} /></div>
            {/* Date Last Letter : champ dérivé des courriers, non saisissable à la création. */}
            <div className="takata-fg takata-fg--checkbox"><label>Stop Drive</label><input type="checkbox" checked={form.stop_drive} onChange={e => set('stop_drive', e.target.checked)} /></div>
          </div>

          <div className="takata-section-hd">Airbags</div>
          <div className="takata-airbag-cols">
            {/* ── Conducteur ── */}
            <div className="takata-airbag-col">
              <div className="takata-airbag-col-hd">Conducteur</div>
              <div className="takata-fg takata-fg--checkbox">
                <label>Concerné par la campagne</label>
                <input type="checkbox" checked={form.driv_airbag_concerned} onChange={e => set('driv_airbag_concerned', e.target.checked)} />
              </div>
              <div className="takata-fg">
                <label>Recall Code</label>
                <input value={form.recall_code_driv} onChange={e => set('recall_code_driv', e.target.value)} placeholder="Ex : CAMP_A" disabled={!form.driv_airbag_concerned} />
              </div>
              <div className="takata-fg takata-fg--checkbox">
                <label>Airbag Replaced</label>
                <input type="checkbox" checked={form.driv_airbag_replaced} disabled={!form.driv_airbag_concerned} onChange={e => {
                  const val = e.target.checked;
                  setForm(p => ({ ...p, driv_airbag_replaced: val, replacement_date_driv: val && !p.replacement_date_driv ? yesterday() : p.replacement_date_driv }));
                }} />
              </div>
              <div className="takata-fg">
                <label>Date remplacement</label>
                <input type="date" value={form.replacement_date_driv} onChange={e => set('replacement_date_driv', e.target.value)} disabled={!form.driv_airbag_concerned || !form.driv_airbag_replaced} />
              </div>
            </div>
            {/* ── Passager ── */}
            <div className="takata-airbag-col">
              <div className="takata-airbag-col-hd">Passager</div>
              <div className="takata-fg takata-fg--checkbox">
                <label>Concerné par la campagne</label>
                <input type="checkbox" checked={form.pass_airbag_concerned} onChange={e => set('pass_airbag_concerned', e.target.checked)} />
              </div>
              <div className="takata-fg">
                <label>Recall Code</label>
                <input value={form.recall_code_pass} onChange={e => set('recall_code_pass', e.target.value)} placeholder="Ex : CAMP_B" disabled={!form.pass_airbag_concerned} />
              </div>
              <div className="takata-fg takata-fg--checkbox">
                <label>Airbag Replaced</label>
                <input type="checkbox" checked={form.passenger_airbag_replaced} disabled={!form.pass_airbag_concerned} onChange={e => {
                  const val = e.target.checked;
                  setForm(p => ({ ...p, passenger_airbag_replaced: val, replacement_date_pass: val && !p.replacement_date_pass ? yesterday() : p.replacement_date_pass }));
                }} />
              </div>
              <div className="takata-fg">
                <label>Date remplacement</label>
                <input type="date" value={form.replacement_date_pass} onChange={e => set('replacement_date_pass', e.target.value)} disabled={!form.pass_airbag_concerned || !form.passenger_airbag_replaced} />
              </div>
            </div>
          </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Création…' : 'Créer le dossier'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal détail dossier ─────────────────────────────────────────────────────

// Ligne de l'en-tête de dossier. `value` est déjà la chaîne affichée : c'est
// elle qui est copiée, et son absence suffit à masquer le bouton (pas de
// bouton de copie sur un « — »).
function SummaryItem({ label, value }) {
  return (
    <div className="takata-summary-item">
      <span>{label}</span>
      <strong><Copyable value={value} titre={`Copier : ${label}`}>{value || '—'}</Copyable></strong>
    </div>
  );
}

function DetailDossierModal({ dossier, user, isAdmin, concessions, users, dossiers = [], onNavigate, onClose, onGeler, onDossierUpdated, onDeleted, restrictedMode = false, peutModifierPremierContact = false }) {
  const [activeTab, setActiveTab] = useState('journal');
  const [deleting, setDeleting] = useState(false);
  const isGele = dossier.statut === 'gele';

  // Navigation précédent / suivant dans l'ordre de la liste filtrée + triée (cf. FactureModal)
  const navigable = dossiers || [];
  const currentIdx = navigable.findIndex(d => d.id === dossier.id);
  const prevDossier = currentIdx > 0 ? navigable[currentIdx - 1] : null;
  const nextDossier = currentIdx >= 0 && currentIdx < navigable.length - 1 ? navigable[currentIdx + 1] : null;
  const canNavigate = !!onNavigate && currentIdx >= 0;
  const isSuperAdmin = user?.role === 'super-admin' || user?.username === 'lia.bonnemain';

  const [counts, setCounts] = useState({
    journal:   parseInt(dossier.nb_contacts)  || 0,
    clients:   parseInt(dossier.nb_clients)   || 0,
    rdv:       parseInt(dossier.nb_rdv)       || 0,
    documents: parseInt(dossier.nb_documents) || 0,
    pret:      parseInt(dossier.nb_prets)     || 0,
    courriers: parseInt(dossier.nb_courriers) || 0,
    escalade:  parseInt(dossier.nb_escalades) || 0,
  });
  const bumpCount = (tab, delta) => setCounts(prev => ({ ...prev, [tab]: Math.max(0, (prev[tab] || 0) + delta) }));

  // Fiches client du dossier — état partagé : l'onglet Journal les saisit,
  // l'onglet Clients les affiche en lecture seule, et l'en-tête en tire
  // l'identité du propriétaire actuel. `null` = pas encore chargées.
  const [clients, setClients] = useState(null);
  // `fraiche` = la fiche telle que renvoyée par l'écriture qui vient d'aboutir
  // (RETURNING * côté backend). Elle prime sur ce que renvoie le GET : ce
  // dernier pouvait réafficher l'ancienne valeur (téléphone modifié qui restait
  // à l'ancien numéro sur la fiche et dans le journal, signalé le 2026-09-23),
  // l'état écrit étant alors perdu au profit d'une lecture périmée.
  const reloadClients = useCallback(async (fraiche) => {
    const appliquer = (list) => {
      if (!fraiche?.id) return list;
      return list.some(c => c.id === fraiche.id)
        ? list.map(c => (c.id === fraiche.id ? { ...c, ...fraiche } : c))
        : [...list, fraiche];
    };
    try {
      const r = await takataApi.getClients(dossier.id);
      const list = appliquer(r.data.clients || []);
      setClients(list);
      return list;
    } catch {
      // Le rechargement a échoué : on garde au moins la fiche qu'on vient
      // d'enregistrer plutôt que de vider la liste affichée.
      let fallback = [];
      setClients(prev => { fallback = appliquer(prev || []); return fallback; });
      return fallback;
    }
  }, [dossier.id]);
  useEffect(() => { reloadClients(); }, [reloadClients]);

  // Identité affichée en tête de dossier : le client explicitement coché
  // « principal » fait foi ; à défaut seulement, les champs issus de l'import
  // constructeur (takata_dossiers.client_*), qui ne sont pas tenus à jour.
  // Tant que la liste n'est pas chargée, on s'appuie sur les alias `principal_*`
  // renvoyés par le backend, pour que l'en-tête soit juste dès l'ouverture.
  const principal = clients !== null
    ? (clients.find(c => c.is_principal) || null)
    : (dossier.principal_nom || dossier.principal_prenom || dossier.principal_telephone || dossier.principal_email
        ? { nom: dossier.principal_nom, prenom: dossier.principal_prenom, telephone: dossier.principal_telephone, email: dossier.principal_email }
        : null);
  const clientAffiche = principal || {
    nom: dossier.client_nom, prenom: dossier.client_prenom,
    telephone: dossier.client_telephone, email: dossier.client_email,
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement le dossier #${dossier.id} (VIN : ${dossier.vin}) ?\n\nCette action est irréversible et supprime tous les contacts, RDV, documents et courriers associés.`)) return;
    setDeleting(true);
    try {
      await takataApi.deleteDossier(dossier.id);
      onDeleted(dossier.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
      setDeleting(false);
    }
  };
  const st = statutInfo(dossier.statut);

  const HIDDEN_TABS_RESTRICTED = new Set(['documents', 'courriers', 'escalade', 'infos']);
  const tc = (n) => (n > 0 ? ` (${n})` : '');
  const TABS = [
    { id: 'journal',   label: `📋 Journal${tc(counts.journal)}` },
    { id: 'clients',   label: `👥 Clients${tc(clients !== null ? clients.length : counts.clients)}` },
    { id: 'rdv',       label: `📅 RDV${tc(counts.rdv)}` },
    { id: 'documents', label: `📎 Documents${tc(counts.documents)}` },
    { id: 'pret',      label: `🚗 Prêt VH${tc(counts.pret)}` },
    { id: 'courriers', label: `📬 Courriers${tc(counts.courriers)}` },
    { id: 'escalade',  label: `🚨 Escalade${tc(counts.escalade)}` },
    { id: 'infos',     label: '✏️ Infos' },
  ].filter(t => !restrictedMode || !HIDDEN_TABS_RESTRICTED.has(t.id));

  return (
    <div className="modal-overlay">
      <div className="modal-content large takata-modal--large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="takata-modal-hd-left">
            {canNavigate && <button className="takata-nav-btn" disabled={!prevDossier} onClick={() => prevDossier && onNavigate(prevDossier)} title="Dossier précédent">◀</button>}
            <span className="takata-modal-title">Dossier #{dossier.id}</span>
            {canNavigate && <span className="takata-modal-counter">{currentIdx + 1}/{navigable.length}</span>}
            <span className="takata-vin-chip">{dossier.vin}</span>
            <span className="takata-badge" style={{ background: fondStatut(st.color), color: texteTheme(st.color) }}>{isGele && '🔒 '}{st.label}</span>
          </div>
          <div className="takata-modal-hd-right">
            {canNavigate && <button className="takata-nav-btn" disabled={!nextDossier} onClick={() => nextDossier && onNavigate(nextDossier)} title="Dossier suivant">▶</button>}
            {!restrictedMode && !isGele && <button className="btn-danger-outline btn-sm" onClick={onGeler}>🔒 Accident signalé</button>}
            {!restrictedMode && isSuperAdmin && (
              <button className="btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                {deleting ? '…' : '🗑 Supprimer'}
              </button>
            )}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="takata-detail-summary">
          {/* Valeurs copiables : le bouton n'apparaît que sur les champs
              renseignés, et copie ce qui est affiché (dates comprises). */}
          <div className="takata-summary-col">
            <SummaryItem label="Marque" value={dossier.marque} />
            <SummaryItem label="Modèle" value={dossier.modele} />
            <SummaryItem label="Immat" value={dossier.immat} />
          </div>
          <div className="takata-summary-col">
            <SummaryItem label="Client" value={[clientAffiche.nom, clientAffiche.prenom].filter(Boolean).join(' ')} />
            <SummaryItem label="Tél" value={clientAffiche.telephone} />
            <SummaryItem label="Email" value={clientAffiche.email} />
          </div>
          <div className="takata-summary-col">
            <SummaryItem label="Concession" value={dossier.concession_nom} />
            <SummaryItem label="Créé le" value={formatDate(dossier.created_at)} />
            <SummaryItem label="1er contact" value={dossier.date_premier_contact ? formatDate(dossier.date_premier_contact) : ''} />
            <SummaryItem label="Assigné à" value={dossier.assigne_prenom ? `${dossier.assigne_prenom} ${dossier.assigne_nom}` : ''} />
          </div>
        </div>

        <div className="takata-detail-tabs">
          {TABS.map(t => <button key={t.id} className={activeTab === t.id ? 'active' : ''} onClick={() => setActiveTab(t.id)}>{t.label}</button>)}
        </div>

        <div className="takata-detail-body">
          {activeTab === 'journal'   && <JournalTab dossierId={dossier.id} dossier={dossier} isGele={isGele} isAdmin={isAdmin} peutModifierPremierContact={peutModifierPremierContact} hasPremierContact={!!dossier.premier_contact_id} onTotalChange={(n) => setCounts(prev => ({ ...prev, journal: n }))} onPremierContactChange={(date, id, statut) => onDossierUpdated && onDossierUpdated({ ...dossier, date_premier_contact: date, premier_contact_id: id, ...(statut ? { statut } : {}) })} onDossierStatut={(statut) => onDossierUpdated && onDossierUpdated({ ...dossier, statut })} clients={clients} onClientsChanged={reloadClients} />}
          {activeTab === 'clients'  && <ClientsTab clients={clients || []} loading={clients === null} />}
          {activeTab === 'rdv'       && <RdvTab dossierId={dossier.id} isGele={isGele} isAdmin={isAdmin} onDossierStatut={(statut) => onDossierUpdated && onDossierUpdated({ ...dossier, statut })} onCountChange={(d) => bumpCount('rdv', d)} />}
          {activeTab === 'documents' && <DocumentsTab dossierId={dossier.id} isGele={isGele} isAdmin={isAdmin} onCountChange={(d) => bumpCount('documents', d)} />}
          {activeTab === 'pret'      && <PretTab dossierId={dossier.id} isGele={isGele} isAdmin={isAdmin} onCountChange={(d) => bumpCount('pret', d)} />}
          {activeTab === 'courriers' && (
            <CourriersTab
              dossierId={dossier.id}
              isGele={isGele}
              isAdmin={isAdmin}
              onCountChange={(d) => bumpCount('courriers', d)}
              /* « Date Last Letter » est dérivée des courriers : on répercute la nouvelle
                 valeur sur le dossier ouvert (onglet Infos, et exports lancés dans la
                 foulée). Le test d'égalité est indispensable — sans lui, chaque rendu
                 relancerait onDossierUpdated. */
              onLastLetterChange={(date) => {
                if ((date || null) !== (dossier.date_last_letter || null)) {
                  onDossierUpdated && onDossierUpdated({ ...dossier, date_last_letter: date });
                }
              }}
            />
          )}
          {activeTab === 'escalade'  && <EscaladeTab dossierId={dossier.id} users={users} isAdmin={isAdmin} onCountChange={(d) => bumpCount('escalade', d)} />}
          {activeTab === 'infos'     && <EditDossierForm dossier={dossier} concessions={concessions} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} isGele={isGele} onUpdated={onDossierUpdated} />}
        </div>
      </div>
    </div>
  );
}

// ─── Contrôle de cohérence « propriétaire actuel » ↔ notes ────────────────────

// Deux formulaires demandent d'affirmer qu'on a affaire au propriétaire ACTUEL
// du véhicule (case « 1er contact » du journal, choix « Client principal » de la
// fiche client) et offrent à côté un champ Notes en texte libre. Rien ne les
// rapprochait : on pouvait cocher « propriétaire actuel » et écrire « a vendu le
// véhicule il y a 2 ans ». L'incohérence partait en base sans alerte et
// contaminait ensuite exports, alertes email et portail constructeur.
//
// L'analyse est faite côté serveur (POST /api/ai/coherence-proprietaire).
// ⚠️ Elle ne bloque JAMAIS : en cas de panne, de lenteur ou de doute, la réponse
// vaut « cohérent » et l'enregistrement suit son cours normalement.
// `nomClient` n'est pas analysé : il sert au scrub côté serveur, qui remplace ce
// nom par un jeton dans la note avant tout envoi à l'IA.
async function verifierCoherenceNotes(notes, estProprietaireActuel, nomClient) {
  if (!notes || !notes.trim()) return null;
  try {
    const { data } = await aiApi.verifierCoherenceProprietaire(notes, estProprietaireActuel, nomClient);
    return data?.coherent === false ? (data.explication || '') : null;
  } catch {
    return null; // le contrôle est un garde-fou, pas une condition
  }
}

// Fenêtre d'alerte — reprend le pattern du rappel « 1er contact » et les classes
// partagées de shared.css. Les boutons portent type="button" : ce composant est
// rendu dans un <form>, sans quoi ils déclencheraient une soumission.
function ModalIncoherence({ explication, choix, extraitNote, onModifier, onContinuer }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Incohérence détectée</h2>
          <button type="button" className="modal-close" onClick={onModifier}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>{explication}</p>
          <div style={{ background: 'var(--bg-muted)', border: '1px solid var(--border-default)', borderRadius: 8, padding: 10, fontSize: '0.85rem' }}>
            <div style={{ color: 'var(--text-secondary)' }}>
              Propriétaire actuel : <strong style={{ color: 'var(--text-primary)' }}>{choix}</strong>
            </div>
            <div style={{ color: 'var(--text-secondary)', marginTop: 6 }}>
              Note saisie : <em style={{ color: 'var(--text-primary)' }}>« {extraitNote} »</em>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onModifier}>Modifier la fiche</button>
          <button type="button" className="btn-primary" onClick={onContinuer}>Enregistrer quand même</button>
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Clients connus ────────────────────────────────────────────────────

// `is_principal` est volontairement à null et non à false : c'est un choix à trois
// états (null = pas encore répondu), pour qu'un « Non » explicite se distingue
// d'une absence de réponse. La saisie est bloquée tant qu'il vaut null.
const EMPTY_CLIENT = { civilite: '', nom: '', prenom: '', telephone: '', email: '', adresse: '', complement: '', code_postal: '', ville: '', notes: '', is_principal: null };

// Autocomplétion d'adresse via la Base Adresse Nationale (api-adresse.data.gouv.fr,
// API publique gratuite du gouvernement, sans clé). Résultats restreints aux Antilles
// (codes postaux 971 Guadeloupe / 972 Martinique) — filtrage côté client, l'API n'offrant
// pas de filtre départemental sur /search. Normalise les libellés de ville → évite les
// doublons/typos (« Fort De France » vs « Fort-de-france »). Saisie manuelle toujours possible.
const ANTILLES_CP_PREFIXES = ['971', '972'];
const isAntilles = (feat) => ANTILLES_CP_PREFIXES.some(p => (feat.properties?.postcode || '').startsWith(p));

function AdresseAutocomplete({ id, value, onChange, onSelect, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [noResult, setNoResult] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runQuery = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 3) { setSuggestions([]); setNoResult(false); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        // limit élevé : on récupère large puis on ne garde que les Antilles (971/972).
        const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=15&autocomplete=1`);
        const data = await res.json();
        const antilles = (data.features || []).filter(isAntilles).slice(0, 8);
        setSuggestions(antilles);
        setNoResult(antilles.length === 0);
        setOpen(true);
        setHighlight(-1);
      } catch { setSuggestions([]); setNoResult(false); setOpen(false); }
    }, 250);
  };

  const choose = (feat) => {
    const p = feat.properties || {};
    let adresse = p.name || '';
    // La BAN ne référence pas les numéros de toutes les voies. Résultat « housenumber »
    // → `name` porte déjà le numéro (« 5 Rue Victor Hugo »). Résultat « street » (ex.
    // « Rue Moi Laminaire » à Fort-de-France) → aucun numéro : sélectionner la suggestion
    // effaçait celui que l'utilisateur venait de taper. On le réinjecte depuis la saisie.
    // Même logique que la webapp de détection terrain (webapp-detection/index.html).
    if (p.type === 'street' && !/^\d/.test(adresse)) {
      const m = String(value || '').trim().match(/^(\d+)\s*(bis|ter|quater)?\b/i);
      if (m) adresse = `${m[1]}${m[2] ? ' ' + m[2].toLowerCase() : ''} ${adresse}`;
    }
    onSelect({ adresse, code_postal: p.postcode || '', ville: p.city || '' });
    setOpen(false);
    setSuggestions([]);
    setNoResult(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); choose(suggestions[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        id={id}
        value={value}
        onChange={e => { onChange(e.target.value); runQuery(e.target.value); }}
        onFocus={() => { if (suggestions.length || noResult) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (suggestions.length > 0 || noResult) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, marginTop: 2, boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.12))', maxHeight: 240, overflowY: 'auto' }}>
          {suggestions.map((s, i) => (
            <div
              key={s.properties?.id || i}
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setHighlight(i)}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '0.83rem', background: i === highlight ? 'var(--bg-hover)' : 'transparent', borderBottom: i < suggestions.length - 1 ? '1px solid var(--border-default)' : 'none' }}
            >
              <div style={{ color: 'var(--text-primary)' }}>{s.properties?.name}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{[s.properties?.postcode, s.properties?.city].filter(Boolean).join(' ')}{s.properties?.context ? ` · ${s.properties.context}` : ''}</div>
            </div>
          ))}
          {noResult && (
            <div style={{ padding: '8px 10px', fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Aucune adresse trouvée aux Antilles — saisissez l'adresse manuellement (Adresse, Code postal et Ville).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Communes (couple Code postal / Ville) ───────────────────────────────────
// Même source que l'autocomplétion d'adresse ci-dessus (BAN publique, sans clé),
// mais sur `type=municipality` : on interroge les communes et non les voies, ce
// qui permet de partir du seul code postal — ou d'un nom de ville mal
// orthographié — sans que l'utilisateur ait à taper la rue.
//
// `lat`/`lon` au centre de l'arc antillais : /search n'offre pas de filtre
// départemental, et sans cette priorisation géographique une saisie courte
// (« sain ») est noyée sous les communes métropolitaines — aucune commune
// antillaise ne remonterait dans la limite. Le filtre `isAntilles` reste la
// garantie finale du périmètre 971/972.
const BAN_SEARCH_URL = 'https://api-adresse.data.gouv.fr/search/';
const ANTILLES_LAT = 14.9;
const ANTILLES_LON = -61.2;

async function rechercherCommunes(q) {
  const url = `${BAN_SEARCH_URL}?q=${encodeURIComponent(q)}&type=municipality&limit=20`
    + `&lat=${ANTILLES_LAT}&lon=${ANTILLES_LON}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Service adresse indisponible');
  const data = await res.json();
  const vues = new Set();
  return (data.features || [])
    .filter(isAntilles)
    .map(f => ({
      ville: f.properties?.city || f.properties?.name || '',
      code_postal: f.properties?.postcode || '',
      context: f.properties?.context || '',
    }))
    .filter(c => {
      const cle = `${c.ville}|${c.code_postal}`;
      if (!c.ville || vues.has(cle)) return false;
      vues.add(cle);
      return true;
    });
}

// Comparaison « même commune » insensible aux accents, tirets, casse et
// ligatures : « fort de france », « Fort-De-France » et « Fort-de-France » se
// valent, ce qui permet de réécrire la saisie dans l'orthographe officielle.
// `œ`/`æ` sont traités à part, NFD ne les décompose pas (cf. Schœlcher).
const normaliserCommune = (s) => String(s || '')
  .replace(/œ/gi, 'oe').replace(/æ/gi, 'ae')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Liste de suggestions — même présentation que celle d'AdresseAutocomplete
// (tokens uniquement, aucune couleur en dur).
function SuggestionsCommunes({ items, highlight, onPick, onHover, message }) {
  return (
    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, marginTop: 2, boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.12))', maxHeight: 240, overflowY: 'auto' }}>
      {items.map((c, i) => (
        <div
          key={`${c.ville}-${c.code_postal}`}
          onMouseDown={(e) => { e.preventDefault(); onPick(c); }}
          onMouseEnter={() => onHover(i)}
          style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '0.83rem', background: i === highlight ? 'var(--bg-hover)' : 'transparent', borderBottom: i < items.length - 1 ? '1px solid var(--border-default)' : 'none' }}
        >
          <div style={{ color: 'var(--text-primary)' }}>{c.ville}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{c.code_postal}{c.context ? ` · ${c.context}` : ''}</div>
        </div>
      ))}
      {message && (
        <div style={{ padding: '8px 10px', fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{message}</div>
      )}
    </div>
  );
}

// Couple Code postal / Ville à autocomplétion croisée :
// - 5 chiffres dans le code postal → commune(s) correspondante(s) : une seule
//   remplit la ville d'office, plusieurs ouvrent une liste, aucune laisse la
//   saisie libre ;
// - saisie dans Ville → suggestions tolérantes aux fautes ; choisir impose
//   l'orthographe officielle ET renseigne le code postal ;
// - au blur de Ville, une saisie qui désigne une commune connue est réécrite
//   dans son orthographe officielle, pour que « fort de france » et
//   « Fort-de-France » ne cohabitent pas en base.
// Le service peut être indisponible : toute erreur est silencieuse et laisse la
// saisie manuelle intacte — ces champs ne bloquent jamais le formulaire.
function CodePostalVilleFields({ codePostal, ville, onChange, disabled, idPrefix = 'client' }) {
  const [cpItems, setCpItems] = useState([]);
  const [cpOpen, setCpOpen] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);
  const [villeItems, setVilleItems] = useState([]);
  const [villeOpen, setVilleOpen] = useState(false);
  const [villeLoading, setVilleLoading] = useState(false);
  const [villeNoResult, setVilleNoResult] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const cpBoxRef = useRef(null);
  const villeBoxRef = useRef(null);
  const debounceRef = useRef(null);
  // Dernier code postal déjà résolu : évite de relancer la même recherche au
  // blur juste après l'avoir lancée à la 5e frappe.
  const dernierCpRef = useRef('');

  useEffect(() => {
    const onDoc = (e) => {
      if (cpBoxRef.current && !cpBoxRef.current.contains(e.target)) setCpOpen(false);
      if (villeBoxRef.current && !villeBoxRef.current.contains(e.target)) setVilleOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // ── Code postal → ville(s) ──
  const resoudreCodePostal = async (cp) => {
    if (!/^\d{5}$/.test(cp) || dernierCpRef.current === cp) return;
    dernierCpRef.current = cp;
    setCpLoading(true);
    try {
      const communes = (await rechercherCommunes(cp)).filter(c => c.code_postal === cp);
      if (communes.length === 1) {
        onChange({ ville: communes[0].ville });
        setCpItems([]); setCpOpen(false);
      } else if (communes.length > 1) {
        // Plusieurs communes partagent ce code postal : on ne devine pas, on
        // laisse choisir et on ne préremplit rien d'ici là.
        setCpItems(communes); setCpOpen(true); setHighlight(-1);
      } else {
        setCpItems([]); setCpOpen(false);
      }
    } catch {
      setCpItems([]); setCpOpen(false);
    } finally { setCpLoading(false); }
  };

  const onCpChange = (v) => {
    // Le champ reste libre (DOM-TOM, saisie partielle) : on n'impose aucun
    // format, on se contente de réagir quand 5 chiffres sont réunis.
    onChange({ code_postal: v });
    if (!/^\d{5}$/.test(v)) { dernierCpRef.current = ''; setCpItems([]); setCpOpen(false); }
    else resoudreCodePostal(v);
  };

  // ── Ville → suggestions + code postal ──
  const chercherVille = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setVilleItems([]); setVilleNoResult(false); setVilleOpen(false); return; }
    setVilleLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const communes = (await rechercherCommunes(q)).slice(0, 8);
        setVilleItems(communes);
        setVilleNoResult(communes.length === 0);
        setVilleOpen(true);
        setHighlight(-1);
      } catch {
        setVilleItems([]); setVilleNoResult(false); setVilleOpen(false);
      } finally { setVilleLoading(false); }
    }, 250);
  };

  const choisirCommune = (c) => {
    onChange({ ville: c.ville, code_postal: c.code_postal || codePostal });
    dernierCpRef.current = c.code_postal || '';
    setVilleItems([]); setVilleOpen(false); setVilleNoResult(false);
    setCpItems([]); setCpOpen(false);
  };

  // Réécriture dans l'orthographe officielle : uniquement si la saisie désigne
  // sans ambiguïté une commune connue. Sinon on ne touche à rien — une ville
  // hors périmètre BAN doit rester saisissable telle quelle.
  const normaliserSaisieVille = async () => {
    const saisie = (ville || '').trim();
    if (!saisie) return;
    try {
      const communes = await rechercherCommunes(saisie);
      const cible = normaliserCommune(saisie);
      const exactes = communes.filter(c => normaliserCommune(c.ville) === cible);
      if (exactes.length === 0) return;
      const maj = {};
      if (exactes[0].ville !== saisie) maj.ville = exactes[0].ville;
      // Code postal renseigné dans la foulée, sauf si plusieurs communes
      // homonymes existent (ex. Saint-Pierre) : on ne devine pas laquelle.
      const cps = [...new Set(exactes.map(c => c.code_postal).filter(Boolean))];
      if (!codePostal && cps.length === 1) { maj.code_postal = cps[0]; dernierCpRef.current = cps[0]; }
      if (Object.keys(maj).length) onChange(maj);
    } catch { /* service indisponible : la saisie manuelle reste telle quelle */ }
  };

  const onVilleKeyDown = (e) => {
    if (!villeOpen || villeItems.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, villeItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); choisirCommune(villeItems[highlight]); }
    else if (e.key === 'Escape') { setVilleOpen(false); }
  };

  return (
    <>
      <div className="takata-fg" ref={cpBoxRef} style={{ position: 'relative' }}>
        <label htmlFor={`${idPrefix}-cp`}>Code postal{cpLoading ? ' — recherche…' : ''}</label>
        <input
          id={`${idPrefix}-cp`}
          value={codePostal}
          onChange={e => onCpChange(e.target.value)}
          onBlur={e => resoudreCodePostal(e.target.value.trim())}
          disabled={disabled}
          autoComplete="off"
        />
        {cpOpen && cpItems.length > 0 && (
          <SuggestionsCommunes
            items={cpItems}
            highlight={highlight}
            onPick={choisirCommune}
            onHover={setHighlight}
            message="Plusieurs communes partagent ce code postal — choisissez la bonne."
          />
        )}
      </div>
      <div className="takata-fg" ref={villeBoxRef} style={{ position: 'relative' }}>
        <label htmlFor={`${idPrefix}-ville`}>Ville{villeLoading ? ' — recherche…' : ''}</label>
        <input
          id={`${idPrefix}-ville`}
          value={ville}
          onChange={e => { onChange({ ville: e.target.value }); chercherVille(e.target.value); }}
          onFocus={() => { if (villeItems.length || villeNoResult) setVilleOpen(true); }}
          onKeyDown={onVilleKeyDown}
          onBlur={normaliserSaisieVille}
          disabled={disabled}
          placeholder="Commencez à taper — l'orthographe est corrigée"
          autoComplete="off"
        />
        {villeOpen && (villeItems.length > 0 || villeNoResult) && (
          <SuggestionsCommunes
            items={villeItems}
            highlight={highlight}
            onPick={choisirCommune}
            onHover={setHighlight}
            message={villeNoResult ? 'Aucune commune trouvée aux Antilles — saisie libre.' : null}
          />
        )}
      </div>
    </>
  );
}

// Sous-formulaire d'adresse partagé (Adresse + Complément + Code postal + Ville),
// commun à la fiche client (ClientForm) et au bloc « Coordonnées du propriétaire »
// du formulaire d'action du journal (AddContactForm). Rendu en fragment : le
// parent fournit la grille `.takata-form-grid`. `onChange` reçoit un patch aux
// clés neutres { adresse, complement, code_postal, ville } — au parent de le
// mapper sur ses propres noms de champs. Choisir une suggestion d'adresse
// remplit rue + code postal + ville (un CP/ville absent de la suggestion
// n'efface pas la saisie) ; le complément n'est jamais touché (la BAN n'en
// fournit pas). `idPrefix` évite des id en double si deux formulaires sont
// ouverts en même temps.
function AdresseFields({ adresse, complement, codePostal, ville, onChange, idPrefix = 'client', disabled }) {
  return (
    <>
      <div className="takata-fg takata-fg--wide">
        <label htmlFor={`${idPrefix}-adresse`}>Adresse</label>
        <AdresseAutocomplete
          id={`${idPrefix}-adresse`}
          value={adresse}
          onChange={v => onChange({ adresse: v })}
          onSelect={(sel) => onChange({
            adresse: sel.adresse,
            ...(sel.code_postal ? { code_postal: sel.code_postal } : {}),
            ...(sel.ville ? { ville: sel.ville } : {}),
          })}
          placeholder="N° et rue — commencez à taper"
        />
      </div>
      <div className="takata-fg takata-fg--wide">
        <label htmlFor={`${idPrefix}-complement`}>Complément</label>
        <input id={`${idPrefix}-complement`} value={complement} onChange={e => onChange({ complement: e.target.value })} placeholder="Résidence, bâtiment, lieu-dit" disabled={disabled} />
      </div>
      <CodePostalVilleFields codePostal={codePostal} ville={ville} onChange={onChange} disabled={disabled} idPrefix={idPrefix} />
    </>
  );
}

// Formulaire de fiche client (« contact »), extrait de ClientsTab le 2026-09-09
// pour être utilisé depuis le Journal, désormais seul endroit où l'on saisit.
// Monté avec une `key` distincte par cible (ajout / client édité) : l'état du
// formulaire est initialisé une fois et n'a pas à se resynchroniser.
function ClientForm({ dossierId, dossier, editingClient, isFirstClient, onSaved, onCancel }) {
  const [form, setForm] = useState(() => {
    if (editingClient) {
      // Un client déjà enregistré porte forcément un booléen : on le reprend tel
      // quel, sans le ramener à « pas de réponse ».
      const c = editingClient;
      return { civilite: c.civilite || '', nom: c.nom || '', prenom: c.prenom || '', telephone: c.telephone || '', email: c.email || '', adresse: c.adresse || '', complement: c.complement || '', code_postal: c.code_postal || '', ville: c.ville || '', notes: c.notes || '', is_principal: !!c.is_principal };
    }
    // Pré-remplir avec le client du dossier s'il n'y a pas encore de client enregistré
    if (isFirstClient && dossier) {
      return {
        ...EMPTY_CLIENT,
        nom: dossier.client_nom || '',
        prenom: dossier.client_prenom || '',
        telephone: dossier.client_telephone || '',
        email: dossier.client_email || '',
        adresse: dossier.client_adresse || '',
        // Pas de pré-réponse, même pour le premier client du dossier : la
        // question doit être tranchée explicitement par l'utilisateur.
        is_principal: null,
      };
    }
    return { ...EMPTY_CLIENT };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [incoherence, setIncoherence] = useState(null); // null = aucune alerte

  const set = (k, v) => { setError(''); setForm(prev => ({ ...prev, [k]: v })); };

  // Découpage volontaire, comme dans AddContactForm : handleSave ne fait que
  // valider, doSave porte l'effet. C'est ce qui permet d'intercaler le contrôle
  // de cohérence et de reprendre l'enregistrement après confirmation.
  const doSave = async () => {
    setSaving(true);
    try {
      // La fiche enregistrée est remontée telle que la renvoie le backend : elle
      // sert à rafraîchir la liste sans dépendre d'une relecture qui pourrait
      // être périmée (cf. reloadClients).
      const res = editingClient
        ? await takataApi.updateClient(dossierId, editingClient.id, form)
        : await takataApi.addClient(dossierId, form);
      onSaved(!editingClient, res?.data || null);
    } catch (err) {
      // Avant, l'échec était avalé en silence : le formulaire restait ouvert
      // sans explication et l'utilisateur pouvait croire à un enregistrement.
      setError(err.response?.data?.error || "L'enregistrement a échoué. Réessayez.");
    }
    finally { setSaving(false); }
  };

  const poursuivreEnregistrement = async () => {
    setVerifying(true);
    const explication = await verifierCoherenceNotes(
      form.notes, form.is_principal, `${form.prenom} ${form.nom}`.trim()
    );
    setVerifying(false);
    if (explication !== null) { setIncoherence(explication); return; }
    await doSave();
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { setError('Le nom est obligatoire.'); return; }
    if (form.is_principal !== true && form.is_principal !== false) {
      setError('Merci d\'indiquer si ce client est le client principal (propriétaire actuel) : cochez « Oui » ou « Non ».');
      return;
    }
    setError('');
    await poursuivreEnregistrement();
  };

  return (
    <div className="takata-client-form">
          <div className="takata-client-form-title">{editingClient ? 'Modifier le contact' : 'Nouveau contact'}</div>

          {/* Première question du formulaire : elle conditionne la lecture de
              tout le reste (qui est le propriétaire actuel du véhicule). */}
          <div className="takata-form-section takata-principal-block">
            <div className="takata-section-hd">
              Client principal (propriétaire actuel) <span className="req">*</span>
            </div>
            <div className="takata-principal-choices" role="radiogroup" aria-label="Client principal (propriétaire actuel)">
              <label className={`takata-principal-choice${form.is_principal === true ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="client-is-principal"
                  checked={form.is_principal === true}
                  onChange={() => set('is_principal', true)}
                />
                Oui
              </label>
              <label className={`takata-principal-choice${form.is_principal === false ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="client-is-principal"
                  checked={form.is_principal === false}
                  onChange={() => set('is_principal', false)}
                />
                Non
              </label>
            </div>
          </div>

          <div className="takata-form-section">
            <div className="takata-section-hd">Identité</div>
            <div className="takata-form-grid">
              <div className="takata-fg">
                <label htmlFor="client-civilite">Civilité</label>
                <select id="client-civilite" value={form.civilite} onChange={e => set('civilite', e.target.value)} style={{ width: '100%' }}>
                  <option value="">—</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                  <option value="Société">Société</option>
                </select>
              </div>
              <div className="takata-fg"><label htmlFor="client-nom">Nom <span className="req">*</span></label><input id="client-nom" value={form.nom} onChange={e => set('nom', e.target.value)} /></div>
              <div className="takata-fg"><label htmlFor="client-prenom">Prénom</label><input id="client-prenom" value={form.prenom} onChange={e => set('prenom', e.target.value)} /></div>
            </div>
          </div>

          <div className="takata-form-section">
            <div className="takata-section-hd">Coordonnées</div>
            <div className="takata-form-grid">
              <div className="takata-fg"><label htmlFor="client-telephone">Téléphone</label><input id="client-telephone" type="tel" value={form.telephone} onChange={e => set('telephone', e.target.value)} /></div>
              <div className="takata-fg"><label htmlFor="client-email">Email</label><input id="client-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            </div>
          </div>

          <div className="takata-form-section">
            <div className="takata-section-hd">Adresse</div>
            <div className="takata-form-grid">
              <AdresseFields
                adresse={form.adresse}
                complement={form.complement}
                codePostal={form.code_postal}
                ville={form.ville}
                onChange={(maj) => { setError(''); setForm(prev => ({ ...prev, ...maj })); }}
              />
            </div>
          </div>

          <div className="takata-form-section">
            <div className="takata-section-hd">Notes</div>
            <div className="takata-form-grid">
              <div className="takata-fg takata-fg--wide">
                <label htmlFor="client-notes">Notes</label>
                <textarea id="client-notes" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Lien avec le véhicule, contexte..." />
              </div>
            </div>
          </div>

          {error && <div className="takata-error">{error}</div>}

          <div className="takata-form-actions">
            <button className="btn-secondary btn-sm" onClick={onCancel}>Annuler</button>
            {/* Le bouton reste actif quand la question est sans réponse : le
                message explique ce qui manque, là où un bouton grisé laisse
                l'utilisateur chercher. */}
            <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving || verifying}>{saving || verifying ? '…' : editingClient ? 'Enregistrer' : 'Ajouter'}</button>
          </div>

          {incoherence !== null && (
            <ModalIncoherence
              explication={incoherence || 'La note saisie semble contredire la réponse donnée.'}
              choix={form.is_principal ? 'Oui' : 'Non'}
              extraitNote={form.notes.trim()}
              onModifier={() => setIncoherence(null)}
              onContinuer={() => { setIncoherence(null); doSave(); }}
            />
          )}
    </div>
  );
}

// Onglet Clients — LECTURE SEULE depuis le 2026-09-09. Il ne sert plus qu'à
// visualiser les propriétaires successifs du véhicule ; toute saisie (ajout,
// modification, suppression d'un contact) se fait dans l'onglet Journal.
function ClientsTab({ clients, loading }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{clients.length} client{clients.length !== 1 ? 's' : ''} connu{clients.length !== 1 ? 's' : ''}</span>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Lecture seule — la saisie se fait dans l'onglet 📋 Journal</span>
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Chargement...</div>}

      {!loading && clients.length === 0 && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 30 }}>
          Aucun client enregistré. Utilisez « + Ajouter un contact » dans l'onglet 📋 Journal.
        </div>
      )}

      {!loading && clients.map(c => (
        <div key={c.id} className={`takata-client-card${c.is_principal ? ' takata-client-card--principal' : ''}`}>
          <div>
            <div className="takata-client-card-name">
              {c.civilite && <span className="takata-client-civilite">{c.civilite}</span>}
              {c.nom} {c.prenom}
              {c.is_principal
                ? <span className="status-info">Propriétaire actuel</span>
                : <span className="status-warning">Ancien propriétaire</span>}
            </div>
            <div className="takata-client-card-contact">
              <Copyable value={c.telephone} titre="Copier le téléphone">{c.telephone || ''}</Copyable>
              {c.telephone && c.email ? ' — ' : ''}
              <Copyable value={c.email} titre="Copier l'e-mail">{c.email || ''}</Copyable>
            </div>
            {(c.adresse || c.code_postal || c.ville) && (
              <div className="takata-client-card-address">
                <Copyable titre="Copier l'adresse">
                  {[c.adresse, c.complement, [c.code_postal, c.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                </Copyable>
              </div>
            )}
            {c.notes && <div className="takata-client-card-notes">{c.notes}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Onglet Journal contacts ──────────────────────────────────────────────────

// Normalisation côté front pour reproduire l'aperçu exact qui sera envoyé
// (le service backend strippe les accents pour Mobbiz).
function stripAccentsForPreview(s) {
  if (!s) return '';
  return String(s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[œŒ]/g, m => m === 'œ' ? 'oe' : 'OE')
    .replace(/[æÆ]/g, m => m === 'æ' ? 'ae' : 'AE')
    .replace(/ç/g, 'c').replace(/Ç/g, 'C')
    .replace(/[^a-zA-Z0-9 .,;:!?'"()\-\n/€@+]/g, '');
}

// Rendu des variables d'un modèle de SMS — miroir EXACT du backend
// (renderSmsModele + formatDateRdvFr). Même correspondance, même format de date,
// pour que l'aperçu affiché soit ce qui part réellement.
function smsFormatDateRdv(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} à ${p(d.getHours())}h${p(d.getMinutes())}`;
}
function renderSmsModele(corps, dossier) {
  if (!dossier) return corps || '';
  const map = {
    immat: dossier.immat, prenom: dossier.client_prenom, nom: dossier.client_nom,
    marque: dossier.marque, modele: dossier.modele, concession: dossier.concession_nom,
    ville: dossier.ville, date_rdv: smsFormatDateRdv(dossier.date_rdv2),
  };
  return String(corps || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, cle) => {
    const val = map[cle];
    return val == null ? '' : String(val).trim();
  });
}
// Segments SMS (GSM-7 : 160 pour un seul, 153 par segment concaténé). Le calcul
// se fait sur le texte SANS accents, tel qu'il part chez Mobbiz.
function smsSegments(text) {
  const len = (text || '').length;
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
}

// Destinataires possibles d'un SMS : le numéro du dossier + ceux de ses clients
// (`takata_clients`). Dédupliqués par numéro normalisé, le client principal en
// tête. Sur les vieux dossiers, `client_telephone` est vide et le numéro vit
// uniquement dans un client — d'où l'importance de proposer les deux sources.
function normPhone(s) { return String(s || '').replace(/[^0-9+]/g, ''); }
function buildSmsRecipients(dossier, clients) {
  const out = [];
  const seen = new Set();
  const add = (tel, nom, prenom, ville, isPrincipal) => {
    const t = (tel || '').trim();
    if (!t) return;
    const key = normPhone(t);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const who = [prenom, nom].filter(Boolean).join(' ').trim();
    out.push({ telephone: t, nom, prenom, ville, isPrincipal: !!isPrincipal, label: `${who || 'Client'} — ${t}` });
  };
  (clients || []).forEach(c => add(c.telephone, c.nom, c.prenom, c.ville, c.is_principal));
  if (dossier) add(dossier.client_telephone, dossier.client_nom, dossier.client_prenom, dossier.ville, false);
  return out;
}
// Objet dossier « vu du destinataire » : le nom/prénom/ville du client choisi
// prennent le pas (les variables {{prenom}}/{{nom}} suivent le vrai destinataire).
function dossierPourRendu(dossier, recipient) {
  if (!recipient) return dossier;
  return {
    ...dossier,
    client_nom: recipient.nom || dossier.client_nom,
    client_prenom: recipient.prenom || dossier.client_prenom,
    ville: recipient.ville || dossier.ville,
  };
}

const FIXED_DOM_TOM_PREFIXES = ['0590', '0596', '0594', '+590', '+596', '+594', '00590', '00596', '00594'];
function isFixedDomTom(raw) {
  const cleaned = (raw || '').replace(/[\s\-.()]/g, '');
  if (!cleaned) return false;
  return FIXED_DOM_TOM_PREFIXES.some(p => cleaned.startsWith(p));
}

// Libellés des champs d'une fiche client, pour raconter une modification dans le
// Journal (« Téléphone : 0696… → 0696… »). L'ordre fixe ces lignes.
const CLIENT_LOG_FIELDS = {
  civilite: 'Civilité',
  nom: 'Nom',
  prenom: 'Prénom',
  telephone: 'Téléphone',
  email: 'Email',
  adresse: 'Adresse',
  complement: 'Complément',
  code_postal: 'Code postal',
  ville: 'Ville',
  notes: 'Notes',
  is_principal: 'Propriétaire actuel',
};

const valeurClientLisible = (champ, v) => {
  if (champ === 'is_principal') return v ? 'Oui' : 'Non';
  const s = (v === null || v === undefined) ? '' : String(v).trim();
  return s || '—';
};

// Champs réellement modifiés entre les deux instantanés stockés par logAction.
// Comparaison sur la chaîne trimée : une valeur passée de NULL à '' n'est pas
// une modification et n'a pas à polluer le journal.
function diffClient(avant, apres) {
  if (!avant || !apres) return [];
  return Object.keys(CLIENT_LOG_FIELDS).filter(champ => {
    if (champ === 'is_principal') return !!avant[champ] !== !!apres[champ];
    return String(avant[champ] ?? '').trim() !== String(apres[champ] ?? '').trim();
  });
}

const nomClientLisible = (c) => [c?.civilite, c?.nom, c?.prenom].filter(Boolean).join(' ').trim() || 'Contact';

function JournalTab({ dossierId, dossier, isGele, isAdmin, hasPremierContact, peutModifierPremierContact, onTotalChange, onPremierContactChange, onDossierStatut, clients, onClientsChanged }) {
  const [contacts, setContacts] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  // null = formulaire contact fermé ; { client: null } = ajout ; { client } = édition
  const [contactForm, setContactForm] = useState(null);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [smsModeles, setSmsModeles] = useState([]);
  const [smsModeleId, setSmsModeleId] = useState(null);
  const [smsText, setSmsText] = useState('');
  const [smsClients, setSmsClients] = useState(null);   // null = pas encore chargé
  const [smsTelephone, setSmsTelephone] = useState('');

  useEffect(() => {
    takataApi.getContacts(dossierId).then(r => { setContacts(r.data.contacts); setLoading(false); }).catch(() => setLoading(false));
  }, [dossierId]);

  // À la première ouverture de la modale : modèles actifs + clients du dossier.
  useEffect(() => {
    if (!showSmsModal) return;
    if (smsModeles.length === 0) {
      takataApi.getSmsModeles().then(r => setSmsModeles((r.data.modeles || []).filter(m => m.actif))).catch(() => {});
    }
    if (smsClients === null) {
      takataApi.getClients(dossierId).then(r => setSmsClients(r.data.clients || [])).catch(() => setSmsClients([]));
    }
  }, [showSmsModal, dossierId, smsModeles.length, smsClients]);

  // Destinataires possibles : numéro du dossier + numéros de ses clients.
  const smsRecipients = useMemo(
    () => (dossier ? buildSmsRecipients(dossier, smsClients || []) : []),
    [dossier, smsClients]
  );
  const smsRecipient = smsRecipients.find(r => r.telephone === smsTelephone) || null;

  // Défaut destinataire (client principal) dès que les clients sont chargés.
  useEffect(() => {
    if (!showSmsModal || smsClients === null || smsTelephone) return;
    const rcp = smsRecipients.find(r => r.isPrincipal) || smsRecipients[0] || null;
    if (rcp) setSmsTelephone(rcp.telephone);
  }, [showSmsModal, smsClients, smsRecipients, smsTelephone]);

  // Défaut modèle (premier actif) dès que les modèles sont chargés.
  useEffect(() => {
    if (!showSmsModal || smsModeleId || smsModeles.length === 0) return;
    setSmsModeleId(smsModeles[0].id);
  }, [showSmsModal, smsModeles, smsModeleId]);

  // Recompose le texte à chaque changement de modèle ou de destinataire — mais
  // PAS à la frappe (l'édition manuelle ne touche ni le modèle ni le numéro).
  useEffect(() => {
    const m = smsModeles.find(x => x.id === smsModeleId);
    if (!m) return;
    setSmsText(stripAccentsForPreview(renderSmsModele(m.corps, dossierPourRendu(dossier, smsRecipient))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsModeleId, smsTelephone]);

  const loadHistorique = useCallback(() => {
    takataApi.getClientsHistorique(dossierId)
      .then(r => setHistorique(r.data.historique || []))
      .catch(() => setHistorique([]));
  }, [dossierId]);
  useEffect(loadHistorique, [loadHistorique]);

  // Le compteur de l'onglet est piloté par onTotalChange (il compte toutes les
  // entrées du journal, pas seulement les actions) : pas de bump ici.
  // Le serveur renvoie le statut du dossier après l'ajout : une action « 1er
  // contact » fait passer un « Non contacté » en « Contacté », un « joint » en
  // « En cours ». L'en-tête et la liste se recalent sans rechargement manuel.
  const handleAdded = (c, statut) => {
    setContacts(prev => [c, ...prev]);
    setShowForm(false);
    if (c.est_premier_contact) onPremierContactChange && onPremierContactChange(c.date_heure, c.id, statut);
    else if (statut && statut !== dossier?.statut) onDossierStatut && onDossierStatut(statut);
  };

  // Après un ajout / une modification / une suppression de contact : la liste des
  // fiches est rechargée par le parent (elle sert aussi à l'en-tête et à l'onglet
  // Clients) et l'historique local est relu pour faire apparaître la trace.
  const handleContactSaved = async (_isCreate, fiche) => {
    setContactForm(null);
    await onClientsChanged(fiche);
    loadHistorique();
  };

  const handleContactDelete = async (client) => {
    if (!window.confirm(`Supprimer le contact « ${nomClientLisible(client)} » ?\n\nLa suppression restera tracée dans le journal.`)) return;
    try {
      await takataApi.deleteClient(dossierId, client.id);
      await onClientsChanged();
      loadHistorique();
    } catch { /* */ }
  };

  // Pose / retire le marquage « 1er contact » sur une entrée existante.
  // Réservé aux porteurs du droit spécifique ; le backend revérifie.
  const handlePremierContact = async (c) => {
    const marquer = !c.est_premier_contact;
    const message = marquer
      ? 'Marquer cette entrée comme « 1er contact » ?\n\nLe marquage actuel du dossier, s\'il existe, sera déplacé sur celle-ci.'
      : 'Retirer le marquage « 1er contact » de cette entrée ?\n\nLe dossier n\'aura plus de date de premier contact.';
    if (!window.confirm(message)) return;
    try {
      const { data } = await takataApi.setPremierContact(c.id, marquer);
      // Rechargement complet : le drapeau a pu changer sur DEUX entrées (celle-ci
      // et l'ancienne), une mise à jour locale les désynchroniserait.
      const r = await takataApi.getContacts(dossierId);
      setContacts(r.data.contacts);
      // L'en-tête du dossier affiche la date de 1er contact : on la recale.
      // Le statut suit (Non contacté ⇄ Contacté, règle serveur).
      onPremierContactChange && onPremierContactChange(
        marquer ? c.date_heure : null,
        marquer ? c.id : null,
        data?.dossier_statut
      );
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  // Journal unifié : actions (takata_contacts), fiches contact (takata_clients,
  // à leur date de création) et traces de modification / suppression, le tout
  // trié du plus récent au plus ancien.
  const entrees = useMemo(() => {
    const items = [];
    for (const c of contacts) items.push({ key: `a${c.id}`, kind: 'action', date: c.date_heure, data: c });
    for (const c of (clients || [])) items.push({ key: `c${c.id}`, kind: 'contact', date: c.created_at, data: c });
    for (const l of historique) {
      // Seul le basculement « propriétaire actuel » ⇄ « ancien propriétaire »
      // est montré au journal (dans les deux sens). Les autres modifications de
      // fiche (téléphone, adresse, notes…) l'alourdissaient sans rien apprendre :
      // la fiche contact elle-même porte déjà la valeur à jour, la trace faisait
      // doublon (décision utilisateur du 2026-09-23). ⚠️ Filtrage à l'AFFICHAGE
      // seulement : le backend continue de tout journaliser dans
      // `takata_actions_log`, l'audit reste donc complet et réversible.
      // Les suppressions restent affichées : la fiche disparaissant de la liste,
      // sans cette trace la disparition serait invisible.
      const champs = l.action === 'DELETE'
        ? []
        : diffClient(l.ancienne_valeur, l.nouvelle_valeur).filter(c => c === 'is_principal');
      if (l.action !== 'DELETE' && champs.length === 0) continue;
      items.push({ key: `l${l.id}`, kind: 'log', date: l.date_heure, data: l, champs });
    }
    return items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [contacts, clients, historique]);

  useEffect(() => { onTotalChange && onTotalChange(entrees.length); }, [entrees.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calcul du dernier SMS Takata pour appliquer la même règle anti-spam que le backend (48h)
  const effectiveHasPremierContact = hasPremierContact || contacts.some(c => c.est_premier_contact);

  // Le bouton n'est bloqué que si le dossier est gelé : l'absence de numéro sur
  // le dossier ne bloque plus l'ouverture (la modale propose de choisir parmi
  // les clients). La validité du numéro CHOISI est gérée dans la modale.
  const smsBlockedReason = (dossier && isGele && !isAdmin) ? 'Dossier gelé' : null;
  const smsNumeroInvalide = smsRecipients.length === 0
    ? "Aucun numéro enregistré pour ce dossier (ajoutez-en un dans l'onglet Clients)"
    : !smsTelephone ? 'Sélectionnez un destinataire'
    : isFixedDomTom(smsTelephone) ? 'Numéro fixe DOM-TOM (envoi SMS impossible)'
    : null;

  const handleSendSms = async () => {
    setSmsSending(true); setSmsError(null);
    try {
      const res = await takataApi.sendSmsTakata(dossierId, { modele_id: smsModeleId, telephone: smsTelephone, message: smsText });
      // Le contact retourné est inséré en tête de liste comme pour handleAdded ;
      // le compteur de l'onglet se recale seul (useEffect sur entrees.length).
      setContacts(prev => [res.data.contact, ...prev]);
      setShowSmsModal(false);
    } catch (err) {
      setSmsError(err.response?.data?.error || 'Erreur lors de l\'envoi du SMS');
    } finally {
      setSmsSending(false);
    }
  };

  return (
    <div className="takata-journal">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{entrees.length} entrée{entrees.length !== 1 ? 's' : ''}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary btn-sm"
            onClick={() => { setSmsError(null); setShowSmsModal(true); }}
            disabled={!!smsBlockedReason}
            title={smsBlockedReason || 'Envoyer un SMS au client'}
          >
            📱 SMS
          </button>
          {/* Deux saisies distinctes : un « contact » est une personne (fiche
              client, modifiable), une « action » est un événement daté du suivi
              (appel, visite, courrier… — figé une fois enregistré). */}
          {!isGele && (
            <button
              className="btn-secondary btn-sm"
              onClick={() => { setShowForm(false); setContactForm(prev => (prev && !prev.client) ? null : { client: null }); }}
            >
              {contactForm && !contactForm.client ? '— Annuler' : '+ Ajouter un contact'}
            </button>
          )}
          {(!isGele || isAdmin) && (
            <button
              className="btn-primary btn-sm"
              onClick={() => { setContactForm(null); setShowForm(v => !v); }}
            >
              {showForm ? '— Annuler' : '+ Ajouter une action'}
            </button>
          )}
        </div>
      </div>
      {contactForm && (
        <ClientForm
          key={contactForm.client ? `edit-${contactForm.client.id}` : 'add'}
          dossierId={dossierId}
          dossier={dossier}
          editingClient={contactForm.client}
          isFirstClient={(clients || []).length === 0}
          onSaved={handleContactSaved}
          onCancel={() => setContactForm(null)}
        />
      )}
      {showForm && <AddContactForm dossierId={dossierId} onAdded={handleAdded} hasPremierContact={effectiveHasPremierContact} peutModifierPremierContact={peutModifierPremierContact} />}
      {showSmsModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📱 Envoi d'un SMS</h2>
              <button className="modal-close" onClick={() => !smsSending && setShowSmsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 14px', marginBottom: 12, alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)' }}>De</span>
                <strong>{dossier?.concession_nom || '—'} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85em' }}>(via Mobbiz)</span></strong>
                <span style={{ color: 'var(--text-secondary)' }}>À</span>
                {smsRecipients.length > 0 ? (
                  <select
                    value={smsTelephone}
                    onChange={e => setSmsTelephone(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: '0.9rem' }}
                  >
                    {smsRecipients.map(r => (
                      <option key={r.telephone} value={r.telephone}>{r.label}{r.isPrincipal ? ' ★' : ''}</option>
                    ))}
                  </select>
                ) : (
                  <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontSize: '0.9rem' }}>Aucun numéro — à ajouter dans l'onglet Clients</span>
                )}
                <span style={{ color: 'var(--text-secondary)' }}>Modèle</span>
                {smsModeles.length > 0 ? (
                  <select
                    value={smsModeleId || ''}
                    onChange={e => setSmsModeleId(parseInt(e.target.value, 10))}
                    style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: '0.9rem' }}
                  >
                    {smsModeles.map(m => <option key={m.id} value={m.id}>{m.libelle}</option>)}
                  </select>
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Aucun modèle configuré — saisissez le texte ci-dessous.</span>
                )}
              </div>
              <textarea
                value={smsText}
                onChange={e => setSmsText(e.target.value)}
                rows={4}
                placeholder="Texte du SMS…"
                style={{ width: '100%', resize: 'vertical', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-default)', fontSize: '0.9rem', lineHeight: 1.4, fontFamily: 'inherit' }}
              />
              {(() => {
                const envoye = stripAccentsForPreview(smsText);
                const seg = smsSegments(envoye);
                return (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 6 }}>
                    {envoye.length} caractères · {seg} SMS{seg > 1 ? ' (message long)' : ''}. Les accents sont retirés automatiquement (limitation Mobbiz).
                  </div>
                );
              })()}
              {smsNumeroInvalide && (
                <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>⚠️ {smsNumeroInvalide}</div>
              )}
              {smsError && (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--color-error-light)', color: 'light-dark(var(--color-error), var(--color-error-dark))', borderRadius: 6, fontSize: '0.9rem' }}>
                  ❌ {smsError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSmsModal(false)} disabled={smsSending}>Annuler</button>
              <button className="btn-primary" onClick={handleSendSms} disabled={smsSending || !smsText.trim() || !!smsNumeroInvalide}>
                {smsSending ? 'Envoi…' : '📤 Envoyer le SMS'}
              </button>
            </div>
          </div>
        </div>
      )}
      {loading ? <div className="takata-loading">Chargement…</div> : entrees.length === 0 ? <div className="takata-empty-journal">Aucune entrée au journal.</div> : (
        <div className="takata-timeline">
          {entrees.map(entree => {
            // ── Fiche contact : la seule entrée modifiable du journal ──
            if (entree.kind === 'contact') {
              const c = entree.data;
              return (
                <div key={entree.key} className="takata-timeline-item takata-timeline-item--contact">
                  <div className="takata-timeline-dot" style={{ background: 'var(--color-info-light)' }}>👤</div>
                  <div className="takata-timeline-content">
                    <div className="takata-timeline-hd">
                      <span className="takata-timeline-type">Contact</span>
                      {c.is_principal
                        ? <span className="status-info">Propriétaire actuel</span>
                        : <span className="status-warning">Ancien propriétaire</span>}
                      {/* marginLeft auto : c'est .takata-timeline-agent qui joue
                          ce rôle sur les actions, absent ici (une fiche contact
                          n'a pas d'auteur affiché). */}
                      <span className="takata-timeline-date" style={{ marginLeft: 'auto' }}>{formatDateTime(c.created_at)}</span>
                      {!isGele && (
                        <div className="action-buttons">
                          <button className="btn-icon" title="Modifier" onClick={() => { setShowForm(false); setContactForm({ client: c }); }}>✏️</button>
                          {isAdmin && <button className="btn-icon delete" title="Supprimer" onClick={() => handleContactDelete(c)}>🗑️</button>}
                        </div>
                      )}
                    </div>
                    <div className="takata-timeline-notes">
                      <strong>{nomClientLisible(c)}</strong>
                      {[c.telephone, c.email].filter(Boolean).length > 0 && (
                        <div>{[c.telephone, c.email].filter(Boolean).join(' — ')}</div>
                      )}
                      {(c.adresse || c.code_postal || c.ville) && (
                        <div>{[c.adresse, c.complement, [c.code_postal, c.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</div>
                      )}
                      {c.notes && <div>{linkifyText(c.notes)}</div>}
                    </div>
                  </div>
                </div>
              );
            }

            // ── Trace de modification / suppression d'une fiche contact ──
            if (entree.kind === 'log') {
              const l = entree.data;
              const estSuppression = l.action === 'DELETE';
              const cible = estSuppression ? l.ancienne_valeur : l.nouvelle_valeur;
              const champs = entree.champs;
              return (
                <div key={entree.key} className="takata-timeline-item takata-timeline-item--log">
                  <div className="takata-timeline-dot" style={{ background: estSuppression ? 'var(--color-error-light)' : 'var(--bg-muted)' }}>{estSuppression ? '🗑️' : '✏️'}</div>
                  <div className="takata-timeline-content">
                    <div className="takata-timeline-hd">
                      <span className="takata-timeline-type">{estSuppression ? 'Contact supprimé' : 'Contact modifié'}</span>
                      <span className="takata-timeline-agent">{[l.user_prenom, l.user_nom].filter(Boolean).join(' ') || 'Système'}</span>
                      <span className="takata-timeline-date">{formatDateTime(l.date_heure)}</span>
                    </div>
                    <div className="takata-timeline-notes">
                      <strong>{nomClientLisible(cible)}</strong>
                      {champs.map(champ => (
                        <div key={champ}>
                          {CLIENT_LOG_FIELDS[champ]} : {valeurClientLisible(champ, l.ancienne_valeur[champ])} → {valeurClientLisible(champ, l.nouvelle_valeur[champ])}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            // ── Action du suivi : jamais modifiable ──
            const c = entree.data;
            const typeInfo = TYPES_CONTACT.find(t => t.value === c.type) || { picto: '📋', label: c.type };
            const resultatInfo = RESULTATS_CONTACT.find(r => r.value === c.resultat) || { label: c.resultat, color: 'var(--text-muted)' };
            const isCourrier = c.type === 'courrier';
            return (
              <div key={entree.key} className={`takata-timeline-item takata-timeline-item--action${isCourrier ? ' takata-timeline-item--courrier' : ''}`}>
                <div className="takata-timeline-dot" style={{ background: `color-mix(in srgb, ${resultatInfo.color} 20%, transparent)` }}>{typeInfo.picto}</div>
                <div className="takata-timeline-content">
                  <div className="takata-timeline-hd">
                    <span className="takata-timeline-type">{typeInfo.label}</span>
                    <span className="takata-timeline-resultat" style={{ color: texteTheme(resultatInfo.color) }}>{resultatInfo.label}</span>
                    {c.est_premier_contact && <span className="takata-badge" style={{ background: 'var(--color-success-light)', color: 'light-dark(var(--color-success), var(--color-success-dark))', fontSize: '0.75rem' }}>🤝 Premier contact</span>}
                    {/* Droit spécifique « Modifier la case 1er contact » : seule
                        façon de RETIRER le marquage, la création d'une action ne
                        sait que le déplacer. */}
                    {peutModifierPremierContact && !isCourrier && (
                      <button
                        className="btn-xs"
                        title={c.est_premier_contact ? 'Retirer le marquage « 1er contact »' : 'Marquer cette entrée comme « 1er contact »'}
                        onClick={() => handlePremierContact(c)}
                      >
                        {c.est_premier_contact ? '🤝 Retirer' : '🤝 Marquer'}
                      </button>
                    )}
                    {isCourrier && <span className="takata-badge-courrier">via Courriers</span>}
                    <span className="takata-timeline-agent">{[c.user_prenom, c.user_nom].filter(Boolean).join(' ') || 'Système'}</span>
                    <span className="takata-timeline-date">{formatDateTime(c.date_heure)}</span>
                  </div>
                  {c.notes && <div className="takata-timeline-notes">{linkifyText(c.notes)}</div>}
                  {(() => {
                    const docs = c.documents?.length > 0
                      ? c.documents
                      : c.document_id ? [{ id: c.document_id, nom: c.nom_fichier }] : [];
                    if (!docs.length) return null;
                    return (
                      <div className="documents-grid" style={{ marginTop: 8 }}>
                        {docs.map(d => <TakataThumbnail key={d.id} docId={d.id} nom={d.nom} canRemove={false} />)}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddContactForm({ dossierId, onAdded, hasPremierContact, peutModifierPremierContact = false }) {
  const [form, setForm] = useState({ type: '', resultat: '', notes: '', est_premier_contact: false, client_nom: '', client_prenom: '', client_telephone: '', client_email: '', client_adresse: '', client_complement: '', client_code_postal: '', client_ville: '', source_info: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState([]); // [{ s3_key, nom, taille }]
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showReminderPopup, setShowReminderPopup] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [incoherence, setIncoherence] = useState(null); // null = aucune alerte
  const fileRef = useRef();
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // Le marquage « 1er contact » est unique et définitif — sauf pour les porteurs
  // du droit spécifique, qui peuvent le déplacer sur une nouvelle entrée.
  const caseVerrouillee = hasPremierContact && !peutModifierPremierContact;

  const uploadFile = async (file) => {
    setUploading(true);
    setError('');
    try {
      const presign = await takataApi.presignDocument(dossierId, { nom: file.name, type_mime: file.type, taille: file.size });
      await fetch(presign.data.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setUploadedDocs(prev => [...prev, { s3_key: presign.data.s3_key, nom: file.name, taille: file.size }]);
    } catch {
      setError('Erreur lors de l\'upload du fichier.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleRemoveDoc = (idx) => setUploadedDocs(prev => prev.filter((_, i) => i !== idx));
  const handleRenameDoc = (idx, nom) => setUploadedDocs(prev => prev.map((d, i) => i === idx ? { ...d, nom } : d));

  const doSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await takataApi.addContact(dossierId, { ...form, documents: uploadedDocs });
      onAdded(res.data.contact, res.data.dossier_statut);
      setForm({ type: '', resultat: '', notes: '', est_premier_contact: false, client_nom: '', client_prenom: '', client_telephone: '', client_email: '', client_adresse: '', client_complement: '', client_code_postal: '', client_ville: '', source_info: '' });
      setUploadedDocs([]);
    }
    catch (err) { setError(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  // Contrôle de cohérence entre la note et la case « propriétaire actuel ».
  // Passage obligé avant doSave : le popup de rappel ci-dessous s'y raccroche
  // aussi, sans quoi son « Enregistrer quand même » échapperait au contrôle.
  const poursuivreEnregistrement = async () => {
    setVerifying(true);
    const explication = await verifierCoherenceNotes(
      form.notes, form.est_premier_contact, `${form.client_prenom} ${form.client_nom}`.trim()
    );
    setVerifying(false);
    if (explication !== null) { setIncoherence(explication); return; }
    await doSave();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type) { setError('Le type est obligatoire.'); return; }
    if (!hasPremierContact && !form.est_premier_contact) { setShowReminderPopup(true); return; }
    await poursuivreEnregistrement();
  };

  return (
    <form className="takata-contact-form" onSubmit={handleSubmit}>
      {error && <div className="takata-error">{error}</div>}
      {/* Type — ligne unique, boutons égaux */}
      <div className="takata-fg">
        <label>Type <span className="req">*</span></label>
        <div className="takata-type-picker">
          {TYPES_CONTACT.filter(t => t.value !== 'courrier').map(t => (
            <button key={t.value} type="button" className={`takata-type-btn${form.type === t.value ? ' selected' : ''}`} onClick={() => set('type', t.value)}>
              <span className="takata-type-picto">{t.picto}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Action — en dessous. Le champ s'appelle toujours `resultat` en base et
          dans l'API : seul l'intitulé affiché a changé (2026-09-09), le renommer
          orphelinerait l'historique et les exports. */}
      <div className="takata-fg">
        <label>Action</label>
        <div className="takata-resultat-picker">
          {RESULTATS_CONTACT.filter(r => !['messagerie', 'pas_de_reponse', 'accepte_rdv', 'refuse', 'courrier_envoye', 'distribue', 'npai', 'non_reclame'].includes(r.value)).map(r => {
            const selected = form.resultat === r.value;
            return (
              <span key={r.value} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <button type="button" className={`takata-resultat-btn${selected ? ' selected' : ''}`} style={selected ? { borderColor: r.color, background: `color-mix(in srgb, ${r.color} 9%, transparent)`, color: texteTheme(r.color) } : {}} onClick={() => set('resultat', selected ? '' : r.value)}>{r.label}</button>
                {selected && ACTION_HELP_TEXT[r.value] && <ActionHelpTooltip text={ACTION_HELP_TEXT[r.value]} />}
              </span>
            );
          })}
        </div>
      </div>
      {/* Premier contact */}
      <div className="takata-fg">
        {/* Verrou levé pour les porteurs du droit « Modifier la case 1er contact » :
            cocher ici DÉPLACE le marquage sur la nouvelle entrée (le backend
            retire le drapeau de l'ancienne). */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: caseVerrouillee ? 'not-allowed' : 'pointer', opacity: caseVerrouillee ? 0.5 : 1 }}>
          <input
            type="checkbox"
            checked={form.est_premier_contact}
            onChange={e => set('est_premier_contact', e.target.checked)}
            disabled={caseVerrouillee}
            style={{ accentColor: 'var(--color-success)' }}
          />
          {/* Libellé seul : la case pilote toujours `est_premier_contact`, et
              rien d'autre n'a changé (marquage unique et immuable par dossier,
              popup de rappel, date de premier contact du dossier). */}
          🤝 contact réussi avec le propriétaire <strong style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>actuel</strong> ou le conducteur du véhicule
          {hasPremierContact && (
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 400 }}>
              {caseVerrouillee ? '— déjà enregistré' : '— déjà enregistré ailleurs : cocher déplacera le marquage'}
            </span>
          )}
        </label>
        {/* Aide contextuelle non bloquante : rappelle de vérifier les doublons
            dans le journal juste en dessous avant de saisir un nouveau contact. */}
        {form.est_premier_contact && (
          <div style={{ background: 'var(--color-info-light)', color: 'var(--color-info-dark)', padding: '8px 12px', borderRadius: 8, marginTop: 8, fontSize: '0.85rem' }}>
            ℹ️ Vérifier si ce client existe déjà dans le journal ci-dessous. Si oui, mettre à jour sa fiche contact en cliquant sur le crayon pour ne pas créer de doublon.
          </div>
        )}
      </div>
      {form.est_premier_contact && (
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: 12, marginBottom: 8, background: 'var(--bg-muted)' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Coordonnées du propriétaire</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
            <div className="takata-fg" style={{ margin: 0 }}>
              <label>Nom</label>
              <input value={form.client_nom} onChange={e => set('client_nom', e.target.value)} placeholder="Nom" />
            </div>
            <div className="takata-fg" style={{ margin: 0 }}>
              <label>Prénom</label>
              <input value={form.client_prenom} onChange={e => set('client_prenom', e.target.value)} placeholder="Prénom" />
            </div>
            <div className="takata-fg" style={{ margin: 0 }}>
              <label>Téléphone</label>
              <input value={form.client_telephone} onChange={e => set('client_telephone', e.target.value)} placeholder="Téléphone" />
            </div>
            <div className="takata-fg" style={{ margin: 0 }}>
              <label>E-mail</label>
              <input value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder="E-mail" />
            </div>
          </div>
          <div className="takata-form-grid" style={{ marginTop: 8 }}>
            <AdresseFields
              idPrefix="premier-contact"
              adresse={form.client_adresse}
              complement={form.client_complement}
              codePostal={form.client_code_postal}
              ville={form.client_ville}
              onChange={(maj) => setForm(p => ({
                ...p,
                ...Object.fromEntries(Object.entries(maj).map(([k, v]) => [`client_${k}`, v])),
              }))}
            />
          </div>
          <div className="takata-fg" style={{ margin: '8px 0 0' }}>
            <label>Origine de l'info</label>
            <select value={form.source_info} onChange={e => set('source_info', e.target.value)}>
              <option value="">—</option>
              {ORIGINES_INFO.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      )}
      {/* Notes + Documents — même ligne */}
      <div className="takata-notes-doc-row">
        <div className="takata-fg" style={{ flex: 1 }}>
          <label>Notes</label>
          <textarea
            className="takata-notes-tall"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            placeholder="Observations…"
          />
        </div>
        <div className="takata-fg takata-docs-col">
          <label>Documents <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optionnel)</span></label>
          {uploadedDocs.length > 0 && (
            <div className="takata-uploaded-list">
              {uploadedDocs.map((d, i) => (
                <div key={i} className="takata-uploaded-item">
                  <span>{/\.(jpg|jpeg|png|gif|webp)$/i.test(d.nom) ? '🖼️' : '📄'}</span>
                  <input
                    className="takata-uploaded-nom-input"
                    value={d.nom}
                    onChange={e => handleRenameDoc(i, e.target.value)}
                    title="Cliquer pour renommer"
                  />
                  <button type="button" className="takata-attachment-remove" onClick={() => handleRemoveDoc(i)} title="Retirer">✕</button>
                </div>
              ))}
            </div>
          )}
          <div
            className={`takata-drop-zone${dragging ? ' dragging' : ''}${uploading ? ' uploading' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              style={{ display: 'none' }}
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => { if (e.target.files[0]) uploadFile(e.target.files[0]); }}
              disabled={uploading}
            />
            <div className="takata-drop-zone-content">
              <span className="takata-drop-icon">{uploading ? '⏳' : '📤'}</span>
              <span>{uploading ? 'Upload en cours…' : 'Glisser-déposer\nou cliquer'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="takata-contact-footer">
        <button type="submit" className="btn-primary" disabled={saving || uploading || verifying}>{saving ? 'Enregistrement…' : verifying ? 'Vérification…' : 'Enregistrer l\'action'}</button>
        <span className="takata-immuable-note">⚠ Enregistrement immuable — horodatage serveur</span>
      </div>
      {showReminderPopup && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rappel</h2>
              {/* type="button" indispensable : ces boutons vivent dans un <form>
                  et déclencheraient sinon une soumission, qui rouvrirait ce même
                  popup au lieu de poursuivre l'enregistrement. */}
              <button type="button" className="modal-close" onClick={() => setShowReminderPopup(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Si c'est un premier contact réussi, n'oubliez pas de cocher la case <strong>« 1er contact »</strong>.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setShowReminderPopup(false)}>Retour</button>
              {/* Passe par poursuivreEnregistrement, pas doSave : ce chemin doit
                  lui aussi être soumis au contrôle de cohérence. */}
              <button type="button" className="btn-primary" onClick={() => { setShowReminderPopup(false); poursuivreEnregistrement(); }}>Enregistrer quand même</button>
            </div>
          </div>
        </div>
      )}
      {incoherence !== null && (
        <ModalIncoherence
          explication={incoherence || 'La note saisie semble contredire la réponse donnée.'}
          choix={form.est_premier_contact ? 'Oui (case « 1er contact » cochée)' : 'Non (case non cochée)'}
          extraitNote={form.notes.trim()}
          onModifier={() => setIncoherence(null)}
          onContinuer={() => { setIncoherence(null); doSave(); }}
        />
      )}
    </form>
  );
}


// ─── Onglet RDV ───────────────────────────────────────────────────────────────

function RdvTab({ dossierId, isGele, isAdmin, onDossierStatut, onCountChange }) {
  const [rdvList, setRdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date_heure: '', type: 'atelier', secteur_geo: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    takataApi.getRdv(dossierId).then(r => { setRdvList(r.data.rdv); setLoading(false); }).catch(() => setLoading(false));
  }, [dossierId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.date_heure) { setError('Date obligatoire.'); return; }
    if (new Date(form.date_heure) < new Date() && !window.confirm('Vous êtes en train de créer un RDV à une date antérieure à la date du jour. Voulez-vous continuer ?')) return;
    setSaving(true); setError('');
    try {
      const res = await takataApi.createRdv(dossierId, form);
      setRdvList(prev => [...prev, res.data.rdv]);
      setShowForm(false);
      setForm({ date_heure: '', type: 'atelier', secteur_geo: '', notes: '' });
      onCountChange && onCountChange(1);
      if (res.data.dossier_statut && onDossierStatut) onDossierStatut(res.data.dossier_statut);
    } catch (err) { setError(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  const handleStatut = async (rdvId, statut) => {
    try {
      const res = await takataApi.updateRdvStatut(rdvId, { statut });
      setRdvList(prev => prev.map(r => r.id === rdvId ? res.data.rdv : r));
      if (res.data.dossier_statut && onDossierStatut) onDossierStatut(res.data.dossier_statut);
      // Reporter, c'est convenir d'un autre créneau : le formulaire de création
      // s'ouvre dans la foulée, pré-rempli du type et du secteur du RDV reporté
      // (seule la date est à ressaisir).
      if (statut === 'reporte') {
        const ancien = rdvList.find(r => r.id === rdvId);
        setForm({
          date_heure: '',
          type: ancien?.type || 'atelier',
          secteur_geo: ancien?.secteur_geo || '',
          notes: '',
        });
        setError('');
        setShowForm(true);
      }
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  return (
    <div className="takata-tab-body">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{rdvList.length} RDV</span>
        {(!isGele || isAdmin) && <button className="btn-primary btn-sm" onClick={() => setShowForm(v => !v)}>{showForm ? '— Annuler' : '+ Nouveau RDV'}</button>}
      </div>

      {showForm && (
        <form className="takata-contact-form" onSubmit={handleCreate}>
          {error && <div className="takata-error">{error}</div>}
          <div className="takata-form-grid">
            <div className="takata-fg"><label>Date et heure <span className="req">*</span></label><input type="datetime-local" value={form.date_heure} onChange={e => setForm(p => ({ ...p, date_heure: e.target.value }))} /></div>
            <div className="takata-fg"><label>Type</label><select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}><option value="atelier">Atelier</option><option value="domicile">Domicile</option></select></div>
            <div className="takata-fg"><label>Secteur géographique</label><input value={form.secteur_geo} onChange={e => setForm(p => ({ ...p, secteur_geo: e.target.value }))} /></div>
            <div className="takata-fg"><label>Notes</label><input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <div className="takata-contact-footer">
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Création…' : 'Créer le RDV'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="takata-loading">Chargement…</div> : rdvList.length === 0 ? <div className="takata-empty-journal">Aucun RDV.</div> : (
        <div className="takata-rdv-list">
          {rdvList.map(r => {
            const st = rdvStatutInfo(r.statut);
            return (
              <div key={r.id} className="takata-rdv-item">
                <div className="takata-rdv-date">{formatDateTime(r.date_heure)}</div>
                <div className="takata-rdv-meta">
                  <span>{r.type === 'atelier' ? '🏭 Atelier' : '🏠 Domicile'}</span>
                  {r.secteur_geo && <span>📍 {r.secteur_geo}</span>}
                  {r.notes && <span>💬 {r.notes}</span>}
                </div>
                <div className="takata-rdv-statut">
                  {/* Le statut est l'information de la carte : seul élément
                      coloré, mis en avant et séparé des actions par un filet. */}
                  <span
                    className="takata-rdv-badge"
                    style={{ background: st.color + '1a', color: texteTheme(st.color), borderColor: st.color + '66' }}
                  >
                    {st.label}
                  </span>
                  {(!isGele || isAdmin) && (
                    <div className="takata-rdv-actions" role="group" aria-label="Actions sur le rendez-vous">
                      {/* Tous les boutons partagent .takata-rdv-action : même
                          géométrie, mêmes états, aucune couleur sémantique — la
                          couleur appartient au badge de statut. L'action du
                          statut courant n'est pas rendue : le badge la dit déjà. */}
                      {ACTIONS_RDV.filter(a => a.visible(r.statut)).map(a => (
                        <button
                          key={a.value}
                          className="takata-rdv-action"
                          title={a.titre}
                          onClick={() => handleStatut(r.id, a.value)}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Aperçu miniature d'un document ──────────────────────────────────────────

// ─── Onglet Documents ─────────────────────────────────────────────────────────

function DocumentsTab({ dossierId, isGele, isAdmin, onCountChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    takataApi.getDocuments(dossierId)
      .then(r => {
        const sorted = [...r.data.documents].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setDocs(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [dossierId]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const presign = await takataApi.presignDocument(dossierId, { nom: file.name, type_mime: file.type, taille: file.size });
      await fetch(presign.data.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const confirm = await takataApi.confirmDocument(dossierId, { s3_key: presign.data.s3_key, nom: file.name, type_doc: 'autre', taille: file.size });
      setDocs(prev => [...prev, confirm.data.document].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      onCountChange && onCountChange(1);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    Array.from(e.dataTransfer.files).forEach(f => uploadFile(f));
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await takataApi.deleteDocument(dossierId, docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
      onCountChange && onCountChange(-1);
    } catch (err) { alert(err.response?.data?.error || 'Erreur suppression'); }
  };

  const handleRename = async (docId, newNom) => {
    try {
      await takataApi.renameDocument(docId, newNom);
      setDocs(prev => prev.map(d => d.id === docId ? { ...d, nom: newNom } : d));
    } catch { alert('Erreur lors du renommage'); }
  };

  return (
    <div className="takata-tab-body">
      <div className="takata-journal-count" style={{ marginBottom: 12 }}>{docs.length} document{docs.length !== 1 ? 's' : ''}</div>
      {loading ? (
        <div className="takata-loading">Chargement…</div>
      ) : (
        <div className="documents-grid">
          {docs.map(d => (
            <TakataThumbnail
              key={d.id}
              docId={d.id}
              nom={d.nom}
              canRemove={isAdmin}
              onRemove={isAdmin ? handleDelete : undefined}
              onRename={(!isGele || isAdmin) ? handleRename : undefined}
            />
          ))}
          {(!isGele || isAdmin) && (
            <div
              className={`drop-zone${dragging ? ' dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => { Array.from(e.target.files).forEach(f => uploadFile(f)); e.target.value = ''; }}
                disabled={uploading}
              />
              <div className="drop-zone-content">
                <span className="drop-icon">{uploading ? '⏳' : '📤'}</span>
                <span>{uploading ? 'Upload en cours…' : 'Glisser-déposer\nou cliquer'}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Prêt VH ───────────────────────────────────────────────────────────

function PretTab({ dossierId, isGele, isAdmin, onCountChange }) {
  const [prets, setPrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ date_debut: '', date_fin: '' });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const canEdit = !isGele || isAdmin;

  useEffect(() => {
    takataApi.getPrets(dossierId).then(r => { setPrets(r.data.prets); setLoading(false); }).catch(() => setLoading(false));
  }, [dossierId]);

  // « Accepté » : ouvre le formulaire réduit (dates de début / fin)
  const openAccepteForm = (pret = null) => {
    if (pret) {
      setForm({ date_debut: pret.date_debut ? pret.date_debut.slice(0, 10) : '', date_fin: pret.date_fin ? pret.date_fin.slice(0, 10) : '' });
      setEditingId(pret.id);
    } else {
      setForm({ date_debut: '', date_fin: '' });
      setEditingId(null);
    }
    setShowForm(true);
  };

  const submitAccepte = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { propose: true, date_debut: form.date_debut || null, date_fin: form.date_fin || null };
      if (editingId) {
        const res = await takataApi.updatePret(dossierId, editingId, payload);
        setPrets(prev => prev.map(p => p.id === editingId ? res.data.pret : p));
      } else {
        const res = await takataApi.createPret(dossierId, payload);
        setPrets(prev => [res.data.pret, ...prev]);
        onCountChange && onCountChange(1);
      }
      setShowForm(false);
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  // « Refusé » : enregistrement direct (pas de formulaire, pas de date)
  const handleRefus = async () => {
    if (!window.confirm('Enregistrer un refus de véhicule de prêt ?')) return;
    setSaving(true);
    try {
      const res = await takataApi.createPret(dossierId, { propose: false });
      setPrets(prev => [res.data.pret, ...prev]);
      onCountChange && onCountChange(1);
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="takata-tab-body">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{prets.length} entrée{prets.length !== 1 ? 's' : ''}</span>
        {canEdit && !showForm && (
          <div className="action-buttons" style={{ alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Le client veut-il une voiture de prêt ?</span>
            <button className="btn-primary btn-sm" onClick={() => openAccepteForm()}>✅ Accepté</button>
            <button className="btn-secondary btn-sm" onClick={handleRefus} disabled={saving}>❌ Refusé</button>
          </div>
        )}
      </div>

      {showForm && (
        <form className="takata-contact-form" onSubmit={submitAccepte}>
          <div className="takata-form-grid">
            <div className="takata-fg"><label>Date de début</label><input type="date" value={form.date_debut} onChange={e => set('date_debut', e.target.value)} /></div>
            <div className="takata-fg"><label>Date de fin</label><input type="date" value={form.date_fin} onChange={e => set('date_fin', e.target.value)} /></div>
          </div>
          <div className="takata-contact-footer">
            <button type="button" className="btn-secondary btn-sm" onClick={() => setShowForm(false)}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Enregistrer'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="takata-loading">Chargement…</div> : prets.length === 0 ? <div className="takata-empty-journal">Aucun prêt enregistré.</div> : (
        <div className="takata-list-cards">
          {prets.map(p => (
            <div key={p.id} className="takata-card">
              <div className="takata-card-hd">
                <span>{p.propose ? `🚗 Véhicule de prêt proposé le ${formatDateTime(p.created_at)}` : '🚫 Véhicule de prêt refusé'}</span>
                {p.propose && canEdit && <button className="btn-icon" onClick={() => openAccepteForm(p)}>✏️</button>}
              </div>
              {p.propose && (p.date_debut || p.date_fin) && (
                <div className="takata-card-row"><span>Du</span><strong>{formatDate(p.date_debut)}</strong><span>au</span><strong>{formatDate(p.date_fin)}</strong></div>
              )}
              {!p.propose && <div className="takata-card-meta">Refusé le {formatDate(p.created_at)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Courriers ─────────────────────────────────────────────────────────

// Date d'envoi du courrier le plus récent, au format YYYY-MM-DD (ou null).
// ⚠️ Ne pas se contenter du 1er élément de la liste : le back trie bien
// `ORDER BY date_envoi DESC`, mais date_envoi est NULLable et PostgreSQL place les
// NULL EN PREMIER en tri DESC — un courrier sans date d'envoi masquerait donc la
// vraie dernière date. On prend le max explicite, en ignorant les valeurs vides.
// Comparaison lexicographique sur YYYY-MM-DD : sûre, et évite tout décalage de
// fuseau (les utilisateurs sont aux Antilles, UTC-4, cf. formatDate/parseLocalDate).
const maxDateEnvoi = (courriers) =>
  (courriers || [])
    .map(c => (c.date_envoi ? String(c.date_envoi).slice(0, 10) : null))
    .filter(Boolean)
    .reduce((max, d) => (max === null || d > max ? d : max), null);

function CourriersTab({ dossierId, isGele, isAdmin, onCountChange, onLastLetterChange }) {
  const [courriers, setCourriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: '1er_envoi', date_envoi: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    takataApi.getCourriers(dossierId).then(r => { setCourriers(r.data.courriers); setLoading(false); }).catch(() => setLoading(false));
  }, [dossierId]);

  // « Date Last Letter » (onglet Infos + exports) est dérivée des courriers. On remonte
  // la valeur au dossier à chaque chargement ou mutation pour que l'onglet Infos se recale
  // sans recharger la page. Le parent compare avant de propager → pas de boucle de rendu.
  useEffect(() => {
    if (loading || !onLastLetterChange) return;
    onLastLetterChange(maxDateEnvoi(courriers));
  }, [courriers, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await takataApi.createCourrier(dossierId, form);
      setCourriers(prev => [res.data.courrier, ...prev]);
      setShowForm(false);
      onCountChange && onCountChange(1);
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  const handleRetour = async (courrierId, statut_retour) => {
    try {
      const res = await takataApi.updateCourrier(dossierId, courrierId, { statut_retour, date_retour: new Date().toISOString().slice(0, 10) });
      setCourriers(prev => prev.map(c => c.id === courrierId ? res.data.courrier : c));
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  return (
    <div className="takata-tab-body">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{courriers.length} courrier{courriers.length !== 1 ? 's' : ''}</span>
        {(!isGele || isAdmin) && <button className="btn-primary btn-sm" onClick={() => setShowForm(v => !v)}>{showForm ? '— Annuler' : '+ Enregistrer un courrier'}</button>}
      </div>

      {showForm && (
        <form className="takata-contact-form" onSubmit={handleCreate}>
          <div className="takata-form-grid">
            <div className="takata-fg"><label>Type</label><select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}><option value="1er_envoi">1er envoi</option><option value="relance">Relance</option></select></div>
            <div className="takata-fg"><label>Date d'envoi</label><input type="date" value={form.date_envoi} onChange={e => setForm(p => ({ ...p, date_envoi: e.target.value }))} /></div>
          </div>
          <div className="takata-contact-footer">
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="takata-loading">Chargement…</div> : courriers.length === 0 ? <div className="takata-empty-journal">Aucun courrier.</div> : (
        <div className="takata-list-cards">
          {courriers.map(c => {
            const retour = retourInfo(c.statut_retour);
            const isMaileva = !!c.ref_maileva;
            return (
              <div key={c.id} className="takata-card">
                <div className="takata-card-hd">
                  <span>📬 {c.type === '1er_envoi' ? '1er envoi' : 'Relance'}{c.mode_envoi && <span className="takata-badge" style={{ marginLeft: 6, background: c.mode_envoi === 'recommande' ? '#8b5cf622' : '#6366f122', color: c.mode_envoi === 'recommande' ? 'light-dark(#8b5cf6, var(--color-decision))' : 'light-dark(#6366f1, var(--color-info-dark))', fontSize: 10 }}>{c.mode_envoi === 'recommande' ? 'RAR' : 'Simple'}</span>}{isMaileva && <span className="takata-badge-courrier" style={{ marginLeft: 6 }}>Maileva</span>}</span>
                  <span className="takata-badge" style={{ background: retour.color + '22', color: texteTheme(retour.color) }}>{retour.label}</span>
                </div>
                {isMaileva && c.destinataire_nom && (
                  <div className="takata-card-row"><span>Destinataire</span><strong>{c.destinataire_nom}</strong></div>
                )}
                {isMaileva && c.immat_destinataire && (
                  <div className="takata-card-row"><span>Immat</span><strong>{c.immat_destinataire}</strong></div>
                )}
                {isMaileva && c.destinataire_adresse && (
                  <div className="takata-card-row"><span>Adresse</span><span>{c.destinataire_adresse}{c.destinataire_cp_ville ? `, ${c.destinataire_cp_ville}` : ''}</span></div>
                )}
                <div className="takata-card-row"><span>Envoyé le</span><strong>{formatDate(c.date_envoi)}</strong></div>
                {c.date_retour && <div className="takata-card-row"><span>Retour le</span><strong>{formatDate(c.date_retour)}</strong></div>}
                {isMaileva && c.statut_laposte && (
                  <div className="takata-card-row"><span>Statut La Poste</span><strong>{c.statut_laposte}</strong></div>
                )}
                {isMaileva && c.date_premiere_presentation && (
                  <div className="takata-card-row"><span>1ère présentation</span><strong>{formatDate(c.date_premiere_presentation)}</strong></div>
                )}
                {isMaileva && c.cause_non_distribution && (
                  <div className="takata-card-row"><span>Cause</span><span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontSize: 12 }}>{c.cause_non_distribution}</span></div>
                )}
                {isMaileva && c.nom_envoi && (
                  <div className="takata-card-row"><span>Lot</span><span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.nom_envoi}</span></div>
                )}
                {c.statut_retour === 'en_attente' && !isMaileva && (
                  <div className="takata-retour-btns">
                    <span className="takata-card-meta">Enregistrer le retour :</span>
                    {STATUTS_RETOUR.filter(s => s.value !== 'en_attente').map(s => (
                      <button key={s.value} className="btn-xs" style={{ borderColor: s.color, color: texteTheme(s.color) }} onClick={() => handleRetour(c.id, s.value)}>{s.label}</button>
                    ))}
                  </div>
                )}
                {c.statut_retour === 'en_attente' && isMaileva && (
                  <div className="takata-retour-btns">
                    <span className="takata-card-meta">Corriger le retour :</span>
                    {STATUTS_RETOUR.filter(s => s.value !== 'en_attente').map(s => (
                      <button key={s.value} className="btn-xs" style={{ borderColor: s.color, color: texteTheme(s.color) }} onClick={() => handleRetour(c.id, s.value)}>{s.label}</button>
                    ))}
                  </div>
                )}
                <div className="takata-card-meta">
                  {isMaileva ? <>Réf. {c.ref_maileva}{c.statut_ar && <> · AR : {c.statut_ar}</>}</> : <>Par {c.created_prenom} {c.created_nom}</>}
                  {' · '}{formatDate(c.created_at)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Escalade ──────────────────────────────────────────────────────────

function EscaladeTab({ dossierId, users, isAdmin, onCountChange }) {
  const [escalades, setEscalades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ user_notifie: '', motif: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    takataApi.getEscalades(dossierId).then(r => { setEscalades(r.data.escalades); setLoading(false); }).catch(() => setLoading(false));
  }, [dossierId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.motif.trim()) { setError('Motif obligatoire.'); return; }
    setSaving(true); setError('');
    try {
      const res = await takataApi.createEscalade(dossierId, { ...form, user_notifie: form.user_notifie || null });
      setEscalades(prev => [res.data.escalade, ...prev]);
      setShowForm(false);
      setForm({ user_notifie: '', motif: '' });
      onCountChange && onCountChange(1);
    } catch (err) { setError(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="takata-tab-body">
      <div className="takata-journal-toolbar">
        <span className="takata-journal-count">{escalades.length} escalade{escalades.length !== 1 ? 's' : ''}</span>
        <button className="btn-primary btn-sm" style={{ background: '#dc2626' }} onClick={() => setShowForm(v => !v)}>{showForm ? '— Annuler' : '🚨 Signaler une escalade'}</button>
      </div>

      {showForm && (
        <form className="takata-contact-form" style={{ borderColor: 'light-dark(#fecaca, var(--border-strong))', background: 'light-dark(#fff5f5, var(--bg-muted))' }} onSubmit={handleSubmit}>
          {error && <div className="takata-error">{error}</div>}
          <div className="takata-form-grid">
            <div className="takata-fg">
              <label>Notifier un utilisateur</label>
              <select value={form.user_notifie} onChange={e => setForm(p => ({ ...p, user_notifie: e.target.value }))}>
                <option value="">— optionnel —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
              </select>
            </div>
            <div className="takata-fg takata-fg--wide">
              <label>Motif <span className="req">*</span></label>
              <textarea value={form.motif} onChange={e => setForm(p => ({ ...p, motif: e.target.value }))} rows={3} placeholder="Décrivez le blocage ou le problème…" />
            </div>
          </div>
          <div className="takata-contact-footer">
            <button type="submit" className="btn-primary" style={{ background: '#dc2626' }} disabled={saving}>{saving ? 'Enregistrement…' : 'Escalader'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="takata-loading">Chargement…</div> : escalades.length === 0 ? <div className="takata-empty-journal">Aucune escalade.</div> : (
        <div className="takata-list-cards">
          {escalades.map(e => (
            <div key={e.id} className="takata-card takata-card--danger">
              <div className="takata-card-hd"><span>🚨 Escalade</span><span className="takata-timeline-date">{formatDateTime(e.date_heure)}</span></div>
              {e.notifie_prenom && <div className="takata-card-row"><span>Notifié</span><strong>{e.notifie_prenom} {e.notifie_nom}</strong></div>}
              <div className="takata-timeline-notes" style={{ marginTop: 6 }}>{e.motif}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Infos dossier ─────────────────────────────────────────────────────

function EditDossierForm({ dossier, concessions, isAdmin, isSuperAdmin, isGele, onUpdated }) {
  const [form, setForm] = useState({
    vin: dossier.vin || '',
    immat: dossier.immat || '', marque: dossier.marque || '', modele: dossier.modele || '',
    concession_id: dossier.concession_id || '', statut: dossier.statut || '',
    demande_info_seulement: dossier.demande_info_seulement || false,
    date_production: dossier.date_production ? dossier.date_production.split('T')[0] : '',
    type_approval_number: dossier.type_approval_number || '',
    location: dossier.location || '',
    recall_code: dossier.recall_code || '',
    recall_code_driv: dossier.recall_code_driv || '',
    recall_code_pass: dossier.recall_code_pass || '',
    stop_drive: dossier.stop_drive || false,
    date_first_letter: dossier.date_first_letter ? dossier.date_first_letter.split('T')[0] : '',
    driv_airbag_concerned: dossier.driv_airbag_concerned || false,
    driv_airbag_replaced: dossier.driv_airbag_replaced || false,
    driv_airbag_inspected: dossier.driv_airbag_inspected || false,
    replacement_date_driv: dossier.replacement_date_driv ? dossier.replacement_date_driv.split('T')[0] : '',
    pass_airbag_concerned: dossier.pass_airbag_concerned || false,
    passenger_airbag_replaced: dossier.passenger_airbag_replaced || false,
    pass_airbag_inspected: dossier.pass_airbag_inspected || false,
    replacement_date_pass: dossier.replacement_date_pass ? dossier.replacement_date_pass.split('T')[0] : '',
    // date_last_letter est volontairement absent du formulaire : le champ est DÉRIVÉ
    // de l'onglet Courriers (date d'envoi du courrier le plus récent) et n'est plus
    // saisissable. Il est affiché en lecture seule plus bas depuis `dossier`.
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const locked = isGele && !isAdmin;
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Le VIN est la clé du rappel (matching des imports constructeur, courriers
    // Maileva et factures) → double garde avant d'envoyer une correction.
    const vinChanged = form.vin !== dossier.vin;
    if (vinChanged) {
      if (!VIN_REGEX.test(form.vin)) {
        setError('Le VIN doit contenir exactement 17 caractères alphanumériques, sans les lettres I, O et Q.');
        return;
      }
      const ok = window.confirm(
        `Corriger le VIN de ce dossier ?\n\n${dossier.vin}\n→ ${form.vin}\n\n` +
        'Le VIN sert à rattacher les imports constructeur, les courriers Maileva et les factures. ' +
        'Ne le modifiez que pour corriger une erreur de saisie.'
      );
      if (!ok) return;
    }

    setSaving(true); setError(''); setSaved(false);
    try {
      const res = await takataApi.updateDossier(dossier.id, { ...form, concession_id: form.concession_id || null });
      onUpdated(res.data.dossier);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { setError(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  const statutColor = statutInfo(form.statut).color;

  return (
    <form className="takata-edit-form" onSubmit={handleSubmit}>
      {locked && <div className="takata-warn">Dossier gelé — modification réservée aux administrateurs.</div>}
      {error && <div className="takata-error">{error}</div>}
      {saved && <div className="takata-success">✓ Dossier mis à jour.</div>}

      {/* ─── Carte Véhicule ─── */}
      <div className="takata-form-card">
        <div className="takata-section-hd">🚗 Véhicule</div>
        <div className="takata-form-grid">
          <div className="takata-fg">
            <label>VIN</label>
            {isSuperAdmin && !locked ? (
              <>
                <input value={form.vin} onChange={e => set('vin', normalizeImmat(e.target.value))} maxLength={17} className="takata-vin-field" />
                <small className="takata-field-hint">Clé du rappel — à modifier uniquement pour corriger une erreur de saisie.</small>
              </>
            ) : (
              <input value={dossier.vin} disabled className="takata-vin-field" />
            )}
          </div>
          <div className="takata-fg"><label>Immatriculation</label><input value={form.immat} onChange={e => set('immat', normalizeImmat(e.target.value))} disabled={locked} placeholder="Ex : AB123CD" /></div>
          <div className="takata-fg"><label>Marque</label><select value={form.marque} onChange={e => set('marque', e.target.value)} disabled={locked}><option value="">— sélectionner —</option>{CONSTRUCTEURS.map(c => <option key={c} value={c}>{c}</option>)}{form.marque && !CONSTRUCTEURS.includes(form.marque) && <option key="_raw" value={form.marque}>{form.marque}</option>}</select></div>
          <div className="takata-fg"><label>Modèle</label><input value={form.modele} onChange={e => set('modele', e.target.value)} disabled={locked} /></div>
          <div className="takata-fg"><label>Concession</label><select value={form.concession_id} onChange={e => set('concession_id', e.target.value)} disabled={locked}><option value="">— sélectionner —</option>{concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}</select></div>
          <div className="takata-fg">
            <label>Statut</label>
            <select value={form.statut} onChange={e => set('statut', e.target.value)} disabled={locked} style={{ borderLeftColor: statutColor, borderLeftWidth: 3 }}>
              {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="takata-fg"><label>Date de production</label><input type="date" value={form.date_production} onChange={e => set('date_production', e.target.value)} disabled={locked} /></div>
          <div className="takata-fg"><label>Type Approval Number</label><input value={form.type_approval_number} onChange={e => set('type_approval_number', e.target.value)} disabled={locked} /></div>
          <div className="takata-fg"><label>Location <span className="req">*</span></label><select value={form.location} onChange={e => set('location', e.target.value)} disabled={locked}><option value="">— sélectionner —</option><option value="971">971</option><option value="972">972</option>{form.location && !['971','972'].includes(form.location) && <option value={form.location}>{form.location} (existant)</option>}</select></div>
          <div className="takata-fg"><label>Date 1st Letter</label><input type="date" value={form.date_first_letter} onChange={e => set('date_first_letter', e.target.value)} disabled={locked} /></div>
          <div className="takata-fg">
            <label>Date Last Letter</label>
            {/* Champ calculé : date d'envoi du courrier le plus récent (onglet Courriers).
                Type texte et non `date` — un <input type="date"> n'accepte pas « — » et
                afficherait « jj/mm/aaaa » sur les dossiers sans courrier. */}
            <input type="text" value={formatDate(dossier.date_last_letter)} disabled readOnly />
            <small className="takata-field-hint">Calculé depuis l'onglet Courriers — date d'envoi du courrier le plus récent.</small>
          </div>
          <div className="takata-fg takata-fg--checkbox"><label>Stop Drive</label><input type="checkbox" checked={!!form.stop_drive} onChange={e => set('stop_drive', e.target.checked)} disabled={locked} /></div>
        </div>
      </div>

      {/* ─── Carte Airbags ─── */}
      <div className="takata-form-card">
        <div className="takata-section-hd">🛡️ Airbags</div>
        <div className="takata-airbag-cols">
          {/* ── Conducteur ── */}
          <div className="takata-airbag-col">
            <div className="takata-airbag-col-hd">Conducteur</div>
            <div className="takata-fg takata-fg--checkbox">
              <label>Concerné par la campagne</label>
              <input type="checkbox" checked={!!form.driv_airbag_concerned} onChange={e => set('driv_airbag_concerned', e.target.checked)} disabled={locked} />
            </div>
            <div className="takata-fg">
              <label>Recall Code</label>
              <input value={form.recall_code_driv} onChange={e => set('recall_code_driv', e.target.value)} disabled={locked || !form.driv_airbag_concerned} placeholder="Ex : CAMP_A" />
            </div>
            <div className="takata-fg takata-fg--checkbox">
              <label>Airbag Replaced</label>
              <input type="checkbox" checked={!!form.driv_airbag_replaced} disabled={locked || !form.driv_airbag_concerned} onChange={e => {
                const val = e.target.checked;
                setForm(p => ({ ...p, driv_airbag_replaced: val, replacement_date_driv: val && !p.replacement_date_driv ? yesterday() : p.replacement_date_driv }));
              }} />
            </div>
            <div className="takata-fg">
              <label>Date remplacement</label>
              <input type="date" value={form.replacement_date_driv} onChange={e => set('replacement_date_driv', e.target.value)} disabled={locked || !form.driv_airbag_concerned || !form.driv_airbag_replaced} />
            </div>
            <div className="takata-fg takata-fg--checkbox">
              <label>Airbag Inspected</label>
              <input type="checkbox" checked={!!form.driv_airbag_inspected} onChange={e => set('driv_airbag_inspected', e.target.checked)} disabled={locked || !form.driv_airbag_concerned} />
            </div>
          </div>
          {/* ── Passager ── */}
          <div className="takata-airbag-col">
            <div className="takata-airbag-col-hd">Passager</div>
            <div className="takata-fg takata-fg--checkbox">
              <label>Concerné par la campagne</label>
              <input type="checkbox" checked={!!form.pass_airbag_concerned} onChange={e => set('pass_airbag_concerned', e.target.checked)} disabled={locked} />
            </div>
            <div className="takata-fg">
              <label>Recall Code</label>
              <input value={form.recall_code_pass} onChange={e => set('recall_code_pass', e.target.value)} disabled={locked || !form.pass_airbag_concerned} placeholder="Ex : CAMP_B" />
            </div>
            <div className="takata-fg takata-fg--checkbox">
              <label>Airbag Replaced</label>
              <input type="checkbox" checked={!!form.passenger_airbag_replaced} disabled={locked || !form.pass_airbag_concerned} onChange={e => {
                const val = e.target.checked;
                setForm(p => ({ ...p, passenger_airbag_replaced: val, replacement_date_pass: val && !p.replacement_date_pass ? yesterday() : p.replacement_date_pass }));
              }} />
            </div>
            <div className="takata-fg">
              <label>Date remplacement</label>
              <input type="date" value={form.replacement_date_pass} onChange={e => set('replacement_date_pass', e.target.value)} disabled={locked || !form.pass_airbag_concerned || !form.passenger_airbag_replaced} />
            </div>
            <div className="takata-fg takata-fg--checkbox">
              <label>Airbag Inspected</label>
              <input type="checkbox" checked={!!form.pass_airbag_inspected} onChange={e => set('pass_airbag_inspected', e.target.checked)} disabled={locked || !form.pass_airbag_concerned} />
            </div>
          </div>
        </div>
        <label className="takata-checkbox-row takata-checkbox-row--highlight">
          <input type="checkbox" checked={form.demande_info_seulement} onChange={e => set('demande_info_seulement', e.target.checked)} disabled={locked} />
          Demande d'information seulement
        </label>
      </div>

      {!locked && <div className="takata-edit-footer"><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer les modifications'}</button></div>}
    </form>
  );
}

// ─── Reporting Constructeur ──────────────────────────────────────────────────

const ORIGINES_INFO = [
  'Affichage 4x3',
  'Autre',
  'Autorités',
  'Bouche à oreille',
  'Casse / garage',
  'Concession',
  'Contrôle technique',
  'Courrier',
  'Grande surface',
  'Pièces détachées',
  'Porte à porte',
  'Presse',
  'Radio',
  'Réseaux sociaux',
  'Site web',
  'Station-service',
  'TV',
];

// ─── Onglet Échanges internes ─────────────────────────────────────────────────

const ECHANGE_STATUTS = [
  { value: 'en_attente', label: 'En attente', color: '#f97316' },
  { value: 'en_cours',   label: 'En cours',   color: '#3b82f6' },
  { value: 'termine',    label: 'Terminé',     color: '#22c55e' },
  { value: 'annule',     label: 'Annulé',      color: 'var(--text-muted)' },
];

function EchangesInternesView({ dossiers, user, users }) {
  const [echanges, setEchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [vinSearch, setVinSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreAssigne, setFiltreAssigne] = useState('');
  const [form, setForm] = useState({ dossier_id: '', demande: '', assigne_a: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    takataApi.getEchanges().then(r => { setEchanges(r.data.echanges || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  // VIN autocomplete
  const vinInput = form._vinInput || '';
  const vinSuggestions = vinInput.length >= 3
    ? dossiers.filter(d => matchesSearch(vinInput, d.vin, d.immat)).slice(0, 8)
    : [];

  const selectDossier = (d) => {
    setForm(prev => ({ ...prev, dossier_id: d.id, _vinInput: `${d.vin}${d.immat ? ' — ' + d.immat : ''}` }));
  };

  const handleSave = async () => {
    if (!form.dossier_id || !form.demande.trim()) return;
    setSaving(true);
    try {
      await takataApi.createEchange({ dossier_id: form.dossier_id, demande: form.demande.trim(), assigne_a: form.assigne_a || null });
      setForm({ dossier_id: '', demande: '', assigne_a: '', _vinInput: '' });
      setShowForm(false);
      load();
    } catch { /* */ }
    finally { setSaving(false); }
  };

  const updateStatut = async (id, statut) => {
    try { await takataApi.updateEchange(id, { statut }); load(); } catch { /* */ }
  };

  const updateReponse = async (id, reponse) => {
    try { await takataApi.updateEchange(id, { reponse }); } catch { /* */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try { await takataApi.deleteEchange(id); load(); } catch { /* */ }
  };

  const filtered = echanges.filter(e => {
    if (filtreStatut && e.statut !== filtreStatut) return false;
    if (filtreAssigne && String(e.assigne_a) !== filtreAssigne) return false;
    return true;
  });

  const isSuperAdmin = user?.role === 'super-admin' || user?.username === 'lia.bonnemain';

  return (
    <div className="page-container">
      <div className="page-toolbar">
        <select className={`page-select${filtreStatut ? ' active-filter' : ''}`} value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {ECHANGE_STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className={`page-select${filtreAssigne ? ' active-filter' : ''}`} value={filtreAssigne} onChange={e => setFiltreAssigne(e.target.value)}>
          <option value="">Tous les assignés</option>
          {users.filter(u => u.actif !== false).map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>{filtered.length} échange{filtered.length !== 1 ? 's' : ''}</span>
        <button className="btn-primary btn-sm" onClick={() => setShowForm(true)} style={{ marginLeft: 8 }}>+ Nouvelle demande</button>
      </div>

      {showForm && (
        <div style={{ border: '1px solid var(--color-accent)', borderRadius: 8, padding: 16, marginBottom: 12, background: 'var(--bg-muted)' }}>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Nouvelle demande d'action</div>
          <div className="takata-form-grid">
            <div className="takata-fg takata-fg--wide" style={{ position: 'relative' }}>
              <label>VIN / Immatriculation *</label>
              <input
                value={vinInput}
                onChange={e => setForm(prev => ({ ...prev, _vinInput: e.target.value, dossier_id: '' }))}
                placeholder="Saisir un VIN ou une immat..."
              />
              {vinSuggestions.length > 0 && !form.dossier_id && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: 200, overflowY: 'auto' }}>
                  {vinSuggestions.map(d => (
                    <div key={d.id} onClick={() => selectDossier(d)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid var(--border-default)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <strong>{d.vin}</strong>
                      {d.immat && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{d.immat}</span>}
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>— {d.client_nom} {d.client_prenom} · {d.marque}</span>
                    </div>
                  ))}
                </div>
              )}
              {form.dossier_id && <div style={{ fontSize: '0.75rem', color: 'light-dark(var(--color-accent), var(--color-accent-dark))', marginTop: 2 }}>Dossier #{form.dossier_id} sélectionné</div>}
            </div>
            <div className="takata-fg">
              <label>Assigné à</label>
              <select value={form.assigne_a} onChange={e => setForm(prev => ({ ...prev, assigne_a: e.target.value }))}>
                <option value="">— Non assigné —</option>
                {users.filter(u => u.actif !== false).map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
              </select>
            </div>
            <div className="takata-fg takata-fg--wide">
              <label>Demande / Action à mener *</label>
              <textarea value={form.demande} onChange={e => setForm(prev => ({ ...prev, demande: e.target.value }))} rows={3} style={{ width: '100%', resize: 'vertical', borderRadius: 6, border: '1px solid var(--border-default)', padding: 8, fontFamily: 'inherit', fontSize: '0.85rem' }} placeholder="Décrire l'action demandée..." />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="btn-secondary btn-sm" onClick={() => setShowForm(false)}>Annuler</button>
            <button className="btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.dossier_id || !form.demande.trim()}>{saving ? '…' : 'Créer la demande'}</button>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Chargement...</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucun échange interne</div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>VIN / Immat</th>
                <th>Client</th>
                <th>Demande</th>
                <th>Assigné à</th>
                <th>Statut</th>
                <th>Réponse</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const st = ECHANGE_STATUTS.find(s => s.value === e.statut) || ECHANGE_STATUTS[0];
                return (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.83rem' }}>
                      {new Date(e.created_at).toLocaleDateString('fr-FR')}
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{e.created_prenom} {e.created_nom}</div>
                    </td>
                    <td>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{e.vin}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{e.immat || ''}</div>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{[e.client_nom, e.client_prenom].filter(Boolean).join(' ') || '—'}</td>
                    <td style={{ fontSize: '0.85rem', maxWidth: 300 }}>{e.demande}</td>
                    <td style={{ fontSize: '0.85rem', fontWeight: 600 }}>{e.assigne_prenom ? `${e.assigne_prenom} ${e.assigne_nom}` : '—'}</td>
                    <td>
                      <select className="page-select" style={{ height: 28, fontSize: '0.8rem' }} value={e.statut} onChange={ev => updateStatut(e.id, ev.target.value)}>
                        {ECHANGE_STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={{ maxWidth: 250 }}>
                      <input
                        className="takata-col-filter"
                        style={{ width: '100%', fontSize: '0.83rem', height: 28 }}
                        defaultValue={e.reponse || ''}
                        placeholder="Réponse..."
                        onBlur={ev => { if (ev.target.value !== (e.reponse || '')) updateReponse(e.id, ev.target.value); }}
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        {isSuperAdmin && <button className="btn-icon delete" title="Supprimer" onClick={() => handleDelete(e.id)}>🗑️</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Casses (import de fichiers de véhicules accidentés) ──────────────────────
// Upload d'un lot de fichiers (Excel/CSV ou PDF) → extraction des VIN → passage en
// « Cassé » des dossiers rapprochés exactement (journal + fichier joint aux
// Documents), matchs partiels à valider. Chaque import est conservé dans
// l'historique. Import et validation : droit « modifier » sur la page Takata.
const CASSES_TYPES = [
  { id: 'exact',   label: 'Match exact', badge: 'status-success' },
  { id: 'partiel', label: 'Match partiel', badge: 'status-warning' },
  { id: 'aucun',   label: 'Non trouvé', badge: 'status-info' },
  { id: 'erreur',  label: 'Erreur', badge: 'status-error' },
];
const CASSES_ACTIONS = {
  passe_casse:      { label: 'Passé en « Cassé » + document joint', badge: 'status-success' },
  deja_casse:       { label: 'Déjà « Cassé » : document joint', badge: 'status-info' },
  gele_non_modifie: { label: 'Dossier gelé : document joint, statut inchangé', badge: 'status-warning' },
  doublon:          { label: 'Doublon dans le fichier : déjà traité', badge: null },
  a_valider:        { label: '⏳ À valider', badge: 'status-warning' },
  echec:            { label: 'Échec de la mise à jour', badge: 'status-error' },
  en_attente:       { label: 'En attente', badge: null },
};
const CASSES_STATUTS_IMPORT = {
  en_cours:             { label: 'En cours', badge: 'status-info' },
  termine:              { label: 'Terminé', badge: 'status-success' },
  termine_avec_erreurs: { label: 'Terminé avec erreurs', badge: 'status-warning' },
  echec:                { label: 'Échec', badge: 'status-error' },
};
const CASSES_ACCEPT = '.xlsx,.xls,.csv,.pdf';

const provenanceCasse = (l, multiFichiers) => [
  multiFichiers || !l.page ? l.fichier_nom : null,
  l.feuille ? `feuille « ${l.feuille} »` : null,
  l.page ? `page ${l.page}` : null,
  l.ligne_source ? `ligne ${l.ligne_source}` : null,
].filter(Boolean).join(' · ') || '—';

const nomsFichiersImport = (i) => (Array.isArray(i.fichiers) && i.fichiers.length ? i.fichiers.map(f => f.nom) : [i.filename]);

function CassesView({ user, editMode, onOpenDossier, onImported }) {
  const [canEdit, setCanEdit] = useState(false);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [imports, setImports] = useState([]);
  const [detail, setDetail] = useState(null);       // { import, lignes } de l'import consulté
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filtreType, setFiltreType] = useState('');
  const [filtreFichier, setFiltreFichier] = useState('');
  const [search, setSearch] = useState('');
  const [choixCandidat, setChoixCandidat] = useState({}); // ligneId → dossierId choisi
  const [validating, setValidating] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const fileRef = useRef(null);
  const detailRef = useRef(null);

  // Suppression réservée au super-admin en mode édition (cf. règle « Mode édition »).
  const canDelete = editMode && user?.role === 'super-admin';

  useEffect(() => {
    permissionsApi.checkAccess('takata')
      .then(r => setCanEdit(!!r.data.permissions?.peut_modifier))
      .catch(() => setCanEdit(false));
  }, []);

  const loadImports = () => {
    takataApi.getCassesImports().then(r => setImports(r.data.imports || [])).catch(() => {});
  };
  useEffect(loadImports, []);

  const openImport = (id, { scroll = false } = {}) => {
    setLoadingDetail(true);
    setFiltreType(''); setFiltreFichier(''); setSearch(''); setChoixCandidat({});
    return takataApi.getCasseImport(id)
      .then(r => {
        setDetail(r.data);
        if (scroll) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      })
      .catch(() => setError("Impossible de charger le détail de l'import"))
      .finally(() => setLoadingDetail(false));
  };

  const reloadDetail = () => {
    if (!detail) return;
    takataApi.getCasseImport(detail.import.id).then(r => setDetail(r.data)).catch(() => {});
  };

  const ajouterFichiers = (liste) => {
    const nouveaux = Array.from(liste || []).filter(f => /\.(xlsx|xls|csv|pdf)$/i.test(f.name));
    if (nouveaux.length < (liste?.length || 0)) setError('Seuls les fichiers Excel (.xlsx, .xls, .csv) et PDF sont acceptés.');
    else setError('');
    setFiles(prev => {
      const cles = new Set(prev.map(f => `${f.name}|${f.size}`));
      return [...prev, ...nouveaux.filter(f => !cles.has(`${f.name}|${f.size}`))];
    });
  };

  const handleImport = async () => {
    if (!files.length || uploading) return;
    setUploading(true); setError('');
    try {
      const { data } = await takataApi.importerCasses(files);
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      loadImports();
      await openImport(data.import.id, { scroll: true });
      if (onImported) onImported();          // rafraîchit la liste des dossiers (statuts modifiés)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Erreur lors de l'import");
    } finally {
      setUploading(false);
    }
  };

  const handleValider = async (ligne) => {
    const candidats = ligne.candidats || [];
    const dossierId = candidats.length === 1 ? candidats[0].id : Number(choixCandidat[ligne.id]);
    if (!dossierId) { setError('Choisissez le dossier à rapprocher.'); return; }
    const cand = candidats.find(c => c.id === dossierId);
    if (!window.confirm(`Valider le rapprochement du VIN ${ligne.vin_normalise} avec le dossier #${dossierId}${cand ? ` (VIN ${cand.vin})` : ''} ?\n\nLe dossier passera au statut « Cassé » (s'il ne l'est pas déjà), le fichier « ${ligne.fichier_nom} » sera joint à ses documents et l'opération sera inscrite à son journal.`)) return;
    setValidating(ligne.id); setError('');
    try {
      await takataApi.validerCasseLigne(ligne.id, dossierId);
      reloadDetail();
      loadImports();
      if (onImported) onImported();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la validation');
    } finally {
      setValidating(null);
    }
  };

  const toggleSelect = (id) => setSelection(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const handleDeleteSelection = async () => {
    const ids = [...selection];
    if (!ids.length) return;
    if (!window.confirm(`Supprimer ${ids.length} import(s) de l'historique ?\n\nLes statuts passés en « Cassé » et les documents joints aux dossiers ne seront PAS annulés (ils restent tracés dans le journal des dossiers). Les fichiers d'origine de l'import seront supprimés.`)) return;
    try {
      await takataApi.deleteCassesImportsBulk(ids);
      if (detail && ids.includes(detail.import.id)) setDetail(null);
      setSelection(new Set());
      loadImports();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteOne = async (i) => {
    if (!window.confirm(`Supprimer l'import du ${formatDateTime(i.created_at)} de l'historique ?\n\nLes statuts passés en « Cassé » et les documents joints aux dossiers ne seront PAS annulés. Les fichiers d'origine de l'import seront supprimés.`)) return;
    try {
      await takataApi.deleteCasseImport(i.id);
      if (detail?.import?.id === i.id) setDetail(null);
      setSelection(prev => { const n = new Set(prev); n.delete(i.id); return n; });
      loadImports();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const imp = detail?.import;
  const lignes = useMemo(() => detail?.lignes || [], [detail]);
  const fichiersDetail = imp ? nomsFichiersImport(imp) : [];
  const multiFichiers = fichiersDetail.length > 1;
  const compteParType = useMemo(() => {
    const c = { a_valider: 0 };
    for (const l of lignes) {
      c[l.match_type] = (c[l.match_type] || 0) + 1;
      if (l.action === 'a_valider') c.a_valider++;
    }
    return c;
  }, [lignes]);
  const lignesFiltrees = useMemo(() => lignes.filter(l => {
    if (filtreType === 'a_valider' ? l.action !== 'a_valider' : (filtreType && l.match_type !== filtreType)) return false;
    if (filtreFichier !== '' && String(l.fichier_index) !== filtreFichier) return false;
    if (search) {
      const q = search.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (q && !`${l.vin_lu || ''}${l.vin_normalise || ''}${l.dossier_vin || ''}${l.dossier_immat || ''}`.toUpperCase().replace(/[^A-Z0-9]/g, '').includes(q)) return false;
    }
    return true;
  }), [lignes, filtreType, filtreFichier, search]);

  const lienDossier = (id, libelle) => (
    <a
      href={`?page=takata&dossier=${id}`}
      className="takata-casses-link"
      onClick={e => { if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button !== 1) { e.preventDefault(); onOpenDossier && onOpenDossier(id); } }}
      title="Ouvrir le dossier"
    >
      {libelle}
    </a>
  );

  const statutImport = (i) => {
    const s = CASSES_STATUTS_IMPORT[i.statut_traitement] || CASSES_STATUTS_IMPORT.termine;
    return <span className={s.badge} title={i.message_erreur || undefined}>{s.label}</span>;
  };

  return (
    <div className="takata-casses">
      {/* Zone d'upload — réservée au droit « modifier » sur Takata */}
      {canEdit && (
        <div className="takata-casses-upload">
          <div className="takata-casses-upload-title">🚧 Importer un fichier de casses</div>
          <p className="takata-casses-upload-hint">
            Un ou plusieurs fichiers Excel (.xlsx, .xls, .csv) ou PDF. Les VIN sont extraits (colonne « VIN » /
            « N° de série », sinon toutes les cellules ; texte des PDF), contrôlés (17 caractères, sans I, O ni Q)
            et rapprochés des dossiers TAKATA. Un match exact passe le dossier en « Cassé » et joint le fichier à
            ses documents ; un match partiel est à valider ci-dessous.
          </p>
          <label
            className={`takata-casses-dropzone${dragging ? ' dragging' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); ajouterFichiers(e.dataTransfer.files); }}
            title="Glisser-déposer ou cliquer pour choisir des fichiers"
          >
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={CASSES_ACCEPT}
              style={{ display: 'none' }}
              onChange={e => { ajouterFichiers(e.target.files); e.target.value = ''; }}
            />
            <span className="takata-casses-dropzone-icon">📤</span>
            <span>Glisser-déposer ou cliquer pour choisir (Excel / PDF, plusieurs fichiers possibles)</span>
          </label>
          {files.length > 0 && (
            <ul className="takata-casses-files">
              {files.map((f, k) => (
                <li key={`${f.name}-${f.size}`}>
                  <span>{/\.pdf$/i.test(f.name) ? '📄' : '📊'} {f.name}</span>
                  <span className="takata-casses-meta">{(f.size / 1024).toFixed(0)} Ko</span>
                  <button className="btn-icon" title="Retirer ce fichier" disabled={uploading} onClick={() => setFiles(prev => prev.filter((_, j) => j !== k))}>✕</button>
                </li>
              ))}
            </ul>
          )}
          <div className="takata-casses-upload-form">
            <button className="btn-primary" disabled={!files.length || uploading} onClick={handleImport}>
              {uploading ? 'Import en cours…' : `Importer ${files.length > 1 ? `les ${files.length} fichiers` : 'le fichier'}`}
            </button>
          </div>
        </div>
      )}
      {error && <div className="takata-casses-error">{error}</div>}

      {/* Détail de l'import consulté */}
      {loadingDetail && <div className="takata-loading">Chargement…</div>}
      {imp && !loadingDetail && (
        <div className="takata-casses-detail" ref={detailRef}>
          <div className="takata-casses-detail-head">
            <h3 className="takata-casses-section-title">
              Import du {formatDateTime(imp.created_at)}
              <span className="takata-casses-meta">{imp.auteur ? ` · ${imp.auteur}` : ''} · {fichiersDetail.join(', ')}</span>
            </h3>
            <div className="action-buttons">
              {statutImport(imp)}
              <button className="btn-icon" title="Fermer le détail" onClick={() => setDetail(null)}>✕</button>
            </div>
          </div>
          {imp.message_erreur && <div className="takata-casses-error">{imp.message_erreur}</div>}

          <div className="takata-dashboard-summary">
            <div className="takata-dash-card">
              <div className="takata-dash-card-value">{imp.nb_vins_lus}</div>
              <div className="takata-dash-card-label">VIN extraits</div>
            </div>
            <div className="takata-dash-card takata-dash-card--green">
              <div className="takata-dash-card-value">{imp.nb_match_exact}</div>
              <div className="takata-dash-card-label">Matchs exacts</div>
            </div>
            <div className="takata-dash-card takata-dash-card--orange">
              <div className="takata-dash-card-value">{imp.nb_match_partiel}</div>
              <div className="takata-dash-card-label">Matchs partiels{imp.nb_partiels_a_valider ? ` (${imp.nb_partiels_a_valider} à valider)` : ''}</div>
            </div>
            <div className="takata-dash-card">
              <div className="takata-dash-card-value">{imp.nb_non_trouves}</div>
              <div className="takata-dash-card-label">Non trouvés</div>
            </div>
            <div className="takata-dash-card takata-dash-card--red">
              <div className="takata-dash-card-value">{imp.nb_erreurs}</div>
              <div className="takata-dash-card-label">Erreurs</div>
            </div>
            <div className="takata-dash-card takata-dash-card--green">
              <div className="takata-dash-card-value">{imp.nb_statut_change}</div>
              <div className="takata-dash-card-label">Dossiers passés en « Cassé »</div>
            </div>
            <div className="takata-dash-card">
              <div className="takata-dash-card-value">{imp.nb_deja_casse}</div>
              <div className="takata-dash-card-label">Dossiers déjà « Cassé »</div>
            </div>
          </div>

          <div className="page-toolbar">
            <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
            {multiFichiers && (
              <select className={`page-select${filtreFichier !== '' ? ' active-filter' : ''}`} value={filtreFichier} onChange={e => setFiltreFichier(e.target.value)}>
                <option value="">Tous les fichiers</option>
                {fichiersDetail.map((nom, k) => <option key={k} value={String(k)}>{nom}</option>)}
              </select>
            )}
          </div>
          <div className="filter-chips-bar">
            <div className="filter-chips-row">
              {[{ id: '', label: 'Tous', n: lignes.length }, ...CASSES_TYPES.map(t => ({ ...t, n: compteParType[t.id] || 0 })), { id: 'a_valider', label: '⏳ À valider', n: compteParType.a_valider }].map(c => (
                <button
                  key={c.id || 'tous'}
                  className={`filter-chip ${filtreType === c.id ? 'chip-active' : 'chip-default'}${c.n === 0 && c.id ? ' chip-empty' : ''}`}
                  onClick={() => setFiltreType(c.id)}
                >
                  {c.label} <span className="chip-count">{c.n}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>VIN brut</th><th>VIN normalisé</th><th>Provenance</th><th>Résultat</th>
                  <th>Dossier lié</th><th>Action effectuée</th><th>Message</th>
                </tr>
              </thead>
              <tbody>
                {lignesFiltrees.length === 0 ? (
                  <tr><td colSpan={7} className="takata-empty">Aucune ligne</td></tr>
                ) : lignesFiltrees.map(l => {
                  const type = CASSES_TYPES.find(t => t.id === l.match_type);
                  const action = CASSES_ACTIONS[l.action];
                  const candidats = l.candidats || [];
                  const aValider = l.action === 'a_valider';
                  return (
                    <tr key={l.id}>
                      <td className="takata-casses-mono">{l.vin_lu || <em className="text-muted">—</em>}</td>
                      <td className="takata-casses-mono">{l.vin_normalise || '—'}</td>
                      <td>{provenanceCasse(l, multiFichiers)}</td>
                      <td>{type && <span className={type.badge}>{type.label}</span>}</td>
                      <td>
                        {l.dossier_id && !(aValider && candidats.length > 1) ? (
                          <>
                            {lienDossier(l.dossier_id, `#${l.dossier_id}`)}
                            <div className="takata-casses-meta">{[l.dossier_vin, l.dossier_immat, l.dossier_marque].filter(Boolean).join(' · ')}</div>
                            {l.dossier_statut && <div className="takata-casses-meta">Statut : {statutInfo(l.dossier_statut).label}</div>}
                          </>
                        ) : candidats.length > 0 ? (
                          <div className="takata-casses-candidats">
                            {candidats.map(c => (
                              <div key={c.id}>
                                {lienDossier(c.id, `#${c.id}`)} <span className="takata-casses-mono">{c.vin}</span>
                                <div className="takata-casses-meta">{c.detail || [c.immat, c.marque].filter(Boolean).join(' · ')}</div>
                              </div>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td>
                        {action ? (action.badge ? <span className={action.badge}>{action.label}</span> : <span className="text-muted">{action.label}</span>) : '—'}
                        {l.valide_at && <div className="takata-casses-meta">Validé par {l.valide_par_nom || '—'} le {formatDateTime(l.valide_at)}</div>}
                        {aValider && canEdit && (
                          <div className="takata-casses-valider">
                            {candidats.length > 1 && (
                              <select className="page-select" value={choixCandidat[l.id] || ''} onChange={e => setChoixCandidat(prev => ({ ...prev, [l.id]: e.target.value }))}>
                                <option value="">Choisir le dossier…</option>
                                {candidats.map(c => <option key={c.id} value={c.id}>#{c.id} · {c.vin}</option>)}
                              </select>
                            )}
                            <button
                              className="btn-primary btn-sm"
                              disabled={validating === l.id || (candidats.length > 1 && !choixCandidat[l.id])}
                              onClick={() => handleValider(l)}
                            >
                              {validating === l.id ? 'Validation…' : 'Valider ce rapprochement'}
                            </button>
                          </div>
                        )}
                      </td>
                      <td>{l.motif || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Historique des imports */}
      <div className="takata-casses-block">
        <div className="takata-casses-block-title">
          🗄️ Historique des imports
          {canDelete && selection.size > 0 && (
            <button className="btn-danger btn-sm" style={{ marginLeft: 12 }} onClick={handleDeleteSelection}>
              🗑️ Supprimer la sélection ({selection.size})
            </button>
          )}
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {canDelete && <th style={{ width: 32 }}></th>}
                <th>Date</th><th>Par</th><th>Fichier(s)</th>
                <th>VIN extraits</th><th>Exacts</th><th>Partiels</th><th>Non trouvés</th><th>Erreurs</th>
                <th>Passés en Cassé</th><th>Déjà Cassé</th><th>Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {imports.length === 0 ? (
                <tr><td colSpan={canDelete ? 13 : 12} className="takata-empty">Aucun import pour le moment</td></tr>
              ) : imports.map(i => (
                <tr key={i.id} className={`takata-casses-row${detail?.import?.id === i.id ? ' takata-row-active' : ''}`} onClick={() => openImport(i.id, { scroll: true })}>
                  {canDelete && <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selection.has(i.id)} onChange={() => toggleSelect(i.id)} /></td>}
                  <td>{formatDateTime(i.created_at)}</td>
                  <td>{i.auteur || '—'}</td>
                  <td>{nomsFichiersImport(i).map((n, k) => <div key={k}>{n}</div>)}</td>
                  <td>{i.nb_vins_lus}</td>
                  <td>{i.nb_match_exact}</td>
                  <td>{i.nb_match_partiel}{i.nb_partiels_a_valider ? <span className="takata-casses-meta"> ({i.nb_partiels_a_valider} à valider)</span> : null}</td>
                  <td>{i.nb_non_trouves}</td>
                  <td>{i.nb_erreurs}</td>
                  <td>{i.nb_statut_change}</td>
                  <td>{i.nb_deja_casse}</td>
                  <td>{statutImport(i)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Voir le détail" onClick={() => openImport(i.id, { scroll: true })}>👁️</button>
                      {canDelete && <button className="btn-icon delete" title="Supprimer cet import" onClick={() => handleDeleteOne(i)}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Reporting constructeur ──────────────────────────────────────────────────

function ReportingConstructeurView({ dossiers }) {
  const [search, setSearch] = useState('');
  const [filtreMarque, setFiltreMarque] = useState([]);
  const [filtreVille, setFiltreVille] = useState([]);
  const [filtreCampagne, setFiltreCampagne] = useState([]);
  const [filtreLocation, setFiltreLocation] = useState('');
  const [filtreDateRdv, setFiltreDateRdv] = useState(''); // '', 'today', 'week', 'month', 'past', 'none'
  const [filtreDateEnr, setFiltreDateEnr] = useState(''); // '', 'today', 'week', 'month', '3months'
  const [colFilters, setColFilters] = useState({}); // { colKey: 'value' } — filtres inline par en-tête
  const [page, setPage] = useState(1);
  const PAGE_SIZE_REPORTING = 50;
  const [localEdits, setLocalEdits] = useState({}); // { [dossierId]: { field: value } }
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [allContacts, setAllContacts] = useState({});
  const [allRdv, setAllRdv] = useState({});
  const [allPrets, setAllPrets] = useState({});
  const [filtreStatutRdv, setFiltreStatutRdv] = useState([]);
  const [filtreStatutDossier, setFiltreStatutDossier] = useState([]); // multi-statuts (OU) — [] = tous
  const [tooltipPos, setTooltipPos] = useState(null);
  const [tooltipDossierId, setTooltipDossierId] = useState(null);
  const [modalDossier, setModalDossier] = useState(null);
  const tooltipTimer = React.useRef(null);

  const clearTooltipTimer = () => { if (tooltipTimer.current) { clearTimeout(tooltipTimer.current); tooltipTimer.current = null; } };
  const scheduleCloseTooltip = () => { clearTooltipTimer(); tooltipTimer.current = setTimeout(() => { setTooltipDossierId(null); setTooltipPos(null); }, 150); };

  useEffect(() => {
    takataApi.getAllContacts().then(r => setAllContacts(r.data.contacts || {})).catch(() => {});
    takataApi.getAllRdv().then(r => setAllRdv(r.data.rdv || {})).catch(() => {});
    takataApi.getAllPrets().then(r => setAllPrets(r.data.prets || {})).catch(() => {});
  }, []);

  // Statut du RDV le plus récent (tous statuts confondus) pour un dossier.
  // allRdv[dossierId] est trié par date_heure ASC côté backend → le dernier
  // élément est le RDV le plus récent, cohérent avec `d.dernier_rdv`.
  const getLastRdvStatut = (dossierId) => {
    const rdvs = allRdv[dossierId];
    if (!rdvs || !rdvs.length) return null;
    return rdvs[rdvs.length - 1].statut || null;
  };

  const getField = (d, field) => (localEdits[d.id] && localEdits[d.id][field] !== undefined) ? localEdits[d.id][field] : d[field];
  // Repli sur takata_dossiers.ville (saisie manuelle dans la modale) — cf. villeDuPrincipal.
  const getVille = (d) => villeDuPrincipal(d) || villeSansCP(getField(d, 'ville'));
  const getCodePostal = (d) => (villeDuPrincipal(d) ? cpDuPrincipal(d) : cpFromVille(getField(d, 'ville')));

  const updateField = (dossierId, field, value) => {
    setLocalEdits(prev => ({ ...prev, [dossierId]: { ...prev[dossierId], [field]: value } }));
    takataApi.patchReporting(dossierId, { [field]: value }).catch(() => {});
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(1);
  };
  const si = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const marques = [...new Set(dossiers.map(d => d.marque).filter(Boolean))].sort();
  const villes = [...new Set(dossiers.map(d => getVille(d)).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
  const locations = [...new Set(dossiers.map(d => d.location).filter(Boolean))].sort();

  const getDelaiReparation = (d) => {
    const rep = d.replacement_date_driv || d.replacement_date_pass;
    return diffJours(d.date_premier_contact, rep);
  };

  const getDelaiMAD = (d) => {
    const p = (allPrets[d.id] || [])[0];
    const j = diffJours(p?.created_at, p?.date_debut);
    return j != null && j >= 0 ? j : null;
  };

  const setColFilter = (col, val) => { setColFilters(prev => ({ ...prev, [col]: val })); setPage(1); };

  const filtered = dossiers.filter(d => {
    if (filtreMarque.length && !filtreMarque.includes(d.marque)) return false;
    if (filtreVille.length && !filtreVille.includes(getVille(d))) return false;
    if (filtreCampagne.length && !filtreCampagne.includes(getStatutCampagneLabel(d))) return false;
    if (filtreLocation && d.location !== filtreLocation) return false;
    if (filtreDateEnr) {
      if (!d.date_premier_contact) return false;
      const now = new Date(); now.setHours(0,0,0,0);
      const enr = new Date(d.date_premier_contact); enr.setHours(0,0,0,0);
      if (filtreDateEnr === 'today' && enr.getTime() !== now.getTime()) return false;
      if (filtreDateEnr === 'week') { const start = new Date(now); start.setDate(start.getDate() - 7); if (enr < start || enr > now) return false; }
      if (filtreDateEnr === 'month') { const start = new Date(now); start.setDate(start.getDate() - 30); if (enr < start || enr > now) return false; }
      if (filtreDateEnr === '3months') { const start = new Date(now); start.setDate(start.getDate() - 90); if (enr < start || enr > now) return false; }
    }
    if (filtreStatutDossier.length && !filtreStatutDossier.includes(d.statut)) return false;
    if (filtreStatutRdv.length) {
      const st = getLastRdvStatut(d.id);
      if (!st || !filtreStatutRdv.includes(st)) return false;
    }
    if (filtreDateRdv) {
      // Aligné sur la colonne « Date RDV proposé » qui affiche désormais le RDV
      // le plus récent (dernier_rdv), tous statuts confondus.
      if (filtreDateRdv === 'none') { if (d.dernier_rdv) return false; }
      else if (!d.dernier_rdv) return false;
      else {
        const now = new Date(); now.setHours(0,0,0,0);
        const rdv = new Date(d.dernier_rdv); rdv.setHours(0,0,0,0);
        if (filtreDateRdv === 'today' && rdv.getTime() !== now.getTime()) return false;
        if (filtreDateRdv === 'week') { const end = new Date(now); end.setDate(end.getDate() + 7); if (rdv < now || rdv > end) return false; }
        if (filtreDateRdv === 'month') { const end = new Date(now); end.setDate(end.getDate() + 30); if (rdv < now || rdv > end) return false; }
        if (filtreDateRdv === 'past' && rdv >= now) return false;
      }
    }
    if (search && !matchesSearch(search, d.vin, d.immat)) return false;
    // Filtres inline par colonne
    for (const [col, val] of Object.entries(colFilters)) {
      if (!val) continue;
      // Filtres texte : même tolérance que la recherche globale (séparateurs et
      // ponctuation neutralisés). Les filtres à valeur exacte plus bas sont des
      // sélecteurs, pas des recherches : ils restent comparés tels quels.
      if (col === 'vin') {
        if (!matchesSearch(val, d.vin, d.immat)) return false;
      } else if (col === 'client_nom') {
        const cl = clientDuReporting(d);
        if (!matchesSearch(val, cl.nom, cl.prenom, cl.telephone, cl.email)) return false;
      } else if (col === 'remorquage_statut' || col === 'veh_pret_statut' || col === 'reclamation_statut') {
        if ((d[col] || '') !== val) return false;
      } else if (col === 'type_rdv') {
        if ((d.type_rdv || '') !== val) return false;
      } else if (col === 'ville') {
        if (!matchesSearch(val, getVille(d))) return false;
      } else {
        if (!matchesSearch(val, d[col])) return false;
      }
    }
    return true;
  }).sort((a, b) => {
    let va, vb;
    const dateFields = ['created_at', 'date_premier_contact', 'prochain_rdv', 'dernier_rdv', 'date_rdv2',
      'date_proposition_mobilite', 'date_mise_en_oeuvre_mobilite', 'date_remplacement_airbag',
      'date_reclamation', 'date_previsionnelle_resolution', 'date_effective_resolution'];
    if (dateFields.includes(sortCol)) {
      va = a[sortCol] ? new Date(a[sortCol]).getTime() : 0;
      vb = b[sortCol] ? new Date(b[sortCol]).getTime() : 0;
    } else if (sortCol === 'delai_reparation') {
      va = getDelaiReparation(a) ?? -1; vb = getDelaiReparation(b) ?? -1;
    } else if (sortCol === 'delai_mad') {
      va = getDelaiMAD(a) ?? -1; vb = getDelaiMAD(b) ?? -1;
    } else if (sortCol === 'client_nom') {
      va = clientDuReporting(a).nom.toLowerCase(); vb = clientDuReporting(b).nom.toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    } else if (sortCol === 'ville') {
      va = getVille(a).toLowerCase(); vb = getVille(b).toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    } else {
      va = String(a[sortCol] || '').toLowerCase();
      vb = String(b[sortCol] || '').toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  const doExportReporting = () => {
    const header = ['VIN', 'Immat', 'Statut', 'Statut Campagne', 'Date enr.', 'Marque', 'Modèle', 'Location', 'Nom client', 'Prénom client', 'Téléphone', 'Email', 'Adresse', 'Code Postal', 'Ville', 'Type RDV', 'Date RDV proposé', 'Statut RDV', 'Remorquage', 'Véh. Prêt', 'Réclamation', 'Origine info'];
    const rows = filtered.map(d => [
      d.vin || '',
      d.immat || '',
      d.statut ? statutInfo(d.statut).label : '',
      getStatutCampagneLabel(d),
      formatDate(d.date_premier_contact),
      d.marque || '',
      d.modele || '',
      d.location || '',
      clientDuReporting(d).nom,
      clientDuReporting(d).prenom,
      clientDuReporting(d).telephone,
      clientDuReporting(d).email,
      clientDuReporting(d).adresse,
      getCodePostal(d),
      getVille(d),
      d.type_rdv === 'atelier' ? 'Atelier' : d.type_rdv === 'domicile' ? 'Domicile' : d.type_rdv || '',
      formatDate(d.dernier_rdv),
      (() => { const st = getLastRdvStatut(d.id); return st ? rdvStatutInfo(st).label : ''; })(),
      getField(d, 'remorquage_statut') || '',
      getField(d, 'veh_pret_statut') || '',
      getField(d, 'reclamation_statut') || '',
      getField(d, 'source_info') || '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws['!cols'] = header.map((_, i) => ({ wch: i === 11 ? 30 : 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporting');
    XLSX.writeFile(wb, `takata_reporting_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className="page-header-btn" onClick={doExportReporting}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export ({filtered.length})
        </button>
      </div>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher VIN / Immat..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <MultiSelectFilter
          label="Toutes les marques"
          options={marques.map(m => ({ value: m, label: m }))}
          selected={filtreMarque}
          onChange={v => { setFiltreMarque(v); setPage(1); }}
        />
        <MultiSelectFilter
          label="Toutes les villes"
          options={villes.map(v => ({ value: v, label: v }))}
          selected={filtreVille}
          onChange={v => { setFiltreVille(v); setPage(1); }}
        />
        <MultiSelectFilter
          label="Statut campagne"
          options={[{ value: 'Terminé', label: 'Terminé' }, { value: 'Partiel', label: 'Partiel' }, { value: 'A faire', label: 'A faire' }]}
          selected={filtreCampagne}
          onChange={v => { setFiltreCampagne(v); setPage(1); }}
        />
        <select className={`page-select${filtreLocation ? ' active-filter' : ''}`} value={filtreLocation} onChange={e => { setFiltreLocation(e.target.value); setPage(1); }}>
          <option value="">Location</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className={`page-select${filtreDateEnr ? ' active-filter' : ''}`} value={filtreDateEnr} onChange={e => { setFiltreDateEnr(e.target.value); setPage(1); }}>
          <option value="">Date enr.</option>
          <option value="today">Aujourd'hui</option>
          <option value="week">7 derniers jours</option>
          <option value="month">30 derniers jours</option>
          <option value="3months">90 derniers jours</option>
        </select>
        <select className={`page-select${filtreDateRdv ? ' active-filter' : ''}`} value={filtreDateRdv} onChange={e => { setFiltreDateRdv(e.target.value); setPage(1); }}>
          <option value="">Date RDV</option>
          <option value="today">Aujourd'hui</option>
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
          <option value="past">Passés</option>
          <option value="none">Sans RDV</option>
        </select>
      </div>
      <div className="table-container" style={{ overflowX: 'auto' }}>
        <table style={{ minWidth: 1400 }}>
          <thead>
            <tr>
              <th className="takata-th-sort" onClick={() => toggleSort('statut')}>Statut{si('statut')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('date_premier_contact')}>Date enr.{si('date_premier_contact')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('marque')}>Marque{si('marque')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('vin')}>VIN / Immat{si('vin')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('client_nom')}>Coord. client{si('client_nom')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('ville')}>Ville{si('ville')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('type_rdv')}>Type RDV{si('type_rdv')}</th>
              <th className="takata-th-sort" onClick={() => toggleSort('dernier_rdv')}>Date RDV proposé{si('dernier_rdv')}</th>
              <th>Statut RDV</th>
              <th>Journal</th>
            </tr>
            <tr className="takata-filter-row">
              <th>
                <MultiSelectFilter
                  label="Tous"
                  options={STATUTS.map(s => ({ value: s.value, label: s.label }))}
                  selected={filtreStatutDossier}
                  onChange={v => { setFiltreStatutDossier(v); setPage(1); }}
                />
              </th>
              <th></th>
              <th><input className="takata-col-filter" placeholder="Marque" value={colFilters.marque || ''} onChange={e => setColFilter('marque', e.target.value)} /></th>
              <th><input className="takata-col-filter" placeholder="VIN / Immat" value={colFilters.vin || ''} onChange={e => setColFilter('vin', e.target.value)} /></th>
              <th><input className="takata-col-filter" placeholder="Nom, tél, email" value={colFilters.client_nom || ''} onChange={e => setColFilter('client_nom', e.target.value)} /></th>
              <th><input className="takata-col-filter" placeholder="Ville" value={colFilters.ville || ''} onChange={e => setColFilter('ville', e.target.value)} /></th>
              <th>
                <select className="takata-col-filter" value={colFilters.type_rdv || ''} onChange={e => setColFilter('type_rdv', e.target.value)}>
                  <option value="">Tous</option>
                  <option value="atelier">Atelier</option>
                  <option value="domicile">Domicile</option>
                </select>
              </th>
              <th></th>
              <th>
                <MultiSelectFilter
                  label="Tous"
                  options={STATUTS_RDV.map(s => ({ value: s.value, label: s.label }))}
                  selected={filtreStatutRdv}
                  onChange={v => { setFiltreStatutRdv(v); setPage(1); }}
                />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="10" className="takata-empty">Aucun dossier</td></tr>
            ) : filtered.slice((page - 1) * PAGE_SIZE_REPORTING, page * PAGE_SIZE_REPORTING).map(d => (
              <tr key={d.id} className="takata-row" onClick={() => setModalDossier(d)} style={{ cursor: 'pointer' }}>
                <td>{(() => { const st = statutInfo(d.statut); return <span className="takata-badge" style={{ background: fondStatut(st.color), color: texteTheme(st.color) }}>{st.label}</span>; })()}</td>
                <td>{formatDate(d.date_premier_contact)}</td>
                <td><div>{d.marque || '—'}</div><div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{d.modele || ''}</div></td>
                <td className="takata-vin-cell">
                  <div className="takata-vin"><Copyable value={d.vin} titre="Copier le VIN">{d.vin}</Copyable></div>
                  <div className="takata-immat"><Copyable value={d.immat} titre="Copier l'immatriculation">{d.immat || ''}</Copyable></div>
                </td>
                <td style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                  {(() => { const cl = clientDuReporting(d); const nomComplet = [cl.nom, cl.prenom].filter(Boolean).join(' '); return (<>
                  <div style={{ fontWeight: 600 }}><Copyable value={nomComplet} titre="Copier le nom">{nomComplet || '—'}</Copyable></div>
                  <div style={{ color: 'var(--text-secondary)' }}>
                    <Copyable value={cl.telephone} titre="Copier le téléphone">{cl.telephone}</Copyable>
                    {cl.telephone && cl.email ? ' — ' : ''}
                    <Copyable value={cl.email} titre="Copier l'e-mail">{cl.email}</Copyable>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}><Copyable value={cl.adresse} titre="Copier l'adresse">{cl.adresse}</Copyable></div>
                  </>); })()}
                </td>
                <td><Copyable value={getVille(d)} titre="Copier la ville">{getVille(d) || '—'}</Copyable></td>
                <td>{d.type_rdv === 'atelier' ? 'Atelier' : d.type_rdv === 'domicile' ? 'Domicile' : d.type_rdv || '—'}</td>
                <td>{formatDate(d.dernier_rdv)}</td>
                <td style={{ textAlign: 'center' }}>{(() => {
                  const st = getLastRdvStatut(d.id);
                  if (!st) return '—';
                  const info = rdvStatutInfo(st);
                  const bg = info.color.startsWith('#') ? info.color + '22' : 'var(--bg-muted)';
                  return <span className="takata-badge" style={{ background: bg, color: info.color }}>{info.label}</span>;
                })()}</td>
                <td
                  style={{ fontSize: '0.78rem', lineHeight: 1.5, cursor: (allContacts[d.id] || []).length > 0 ? 'pointer' : 'default' }}
                  onMouseEnter={e => {
                    if ((allContacts[d.id] || []).length > 1) {
                      clearTooltipTimer();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPos({ top: rect.top, left: Math.min(rect.left, window.innerWidth - 520) });
                      setTooltipDossierId(d.id);
                    }
                  }}
                  onMouseLeave={scheduleCloseTooltip}
                >
                  {(() => {
                    const contacts = allContacts[d.id] || [];
                    if (!contacts.length) return '—';
                    const last = contacts[contacts.length - 1];
                    const t = TYPES_CONTACT.find(x => x.value === last.type) || { label: last.type };
                    const r = RESULTATS_CONTACT.find(x => x.value === last.resultat) || { label: last.resultat };
                    return (
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>{formatDate(last.date_heure)}</span>
                        {' / '}<strong>{t.label}</strong>
                        {' / '}<span style={{ color: texteTheme(r.color) }}>{r.label}</span>
                        {last.notes && <span style={{ color: 'var(--text-secondary)' }}>{' / '}{last.notes}</span>}
                        {contacts.length > 1 && <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: '0.7rem' }}>+{contacts.length - 1}</span>}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalDossier && (() => {
        const d = modalDossier;
        const st = statutInfo(d.statut);
        const contacts = allContacts[d.id] || [];
        const delaiMAD = diffJours(d.created_at, d.date_mise_en_oeuvre_mobilite);
        const repDate = d.replacement_date_driv || d.replacement_date_pass;
        const delaiRep = diffJours(d.date_premier_contact, repDate);
        const Row = ({ label, value }) => (
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-default)' }}>
            <span style={{ width: 260, flexShrink: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{label}</span>
            {/* Copyable ignore de lui-même les valeurs JSX (badge Statut) et les
                valeurs vides : aucun bouton n'apparaît dans ces cas. */}
            <span style={{ fontWeight: 500, fontSize: '0.9rem', flex: 1 }}>
              <Copyable value={value} titre={`Copier : ${label}`}>{value || '—'}</Copyable>
            </span>
          </div>
        );
        const EditRow = ({ label, field, type = 'text' }) => {
          const val = getField(d, field) || '';
          return (
            <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-default)' }}>
              <span style={{ width: 260, flexShrink: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{label}</span>
              <input type={type} style={{ flex: 1, height: 30, fontSize: '0.85rem', border: '1px solid var(--border-default)', borderRadius: 6, padding: '0 8px', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                value={type === 'date' ? (val || '').substring(0, 10) : val}
                onChange={e => updateField(d.id, field, e.target.value)} />
            </div>
          );
        };
        const SelectRow = ({ label, field, options }) => (
          <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-default)' }}>
            <span style={{ width: 260, flexShrink: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{label}</span>
            <select className="page-select" style={{ height: 30, fontSize: '0.85rem' }} value={getField(d, field) || ''} onChange={e => updateField(d.id, field, e.target.value)}>
              <option value="">—</option>
              {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        );
        return (
          <div className="modal-overlay">
            <div className="modal-content large" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reporting — {d.vin}</h2>
                <button className="modal-close" onClick={() => setModalDossier(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 40px' }}>
                  <div>
                    <Row label="Date enregistrement" value={formatDate(d.date_premier_contact)} />
                    <Row label="VIN" value={d.vin} />
                    <Row label="Immatriculation" value={d.immat} />
                    <Row label="Client" value={[clientDuReporting(d).nom, clientDuReporting(d).prenom].filter(Boolean).join(' ')} />
                    <Row label="Téléphone" value={clientDuReporting(d).telephone} />
                    <Row label="E-mail" value={clientDuReporting(d).email} />
                    <Row label="Adresse" value={clientDuReporting(d).adresse} />
                    {villeDuPrincipal(d)
                      ? <Row label="Ville" value={villeDuPrincipal(d)} />
                      : <EditRow label="Ville" field="ville" />}
                    <Row label="Marque" value={d.marque} />
                    <Row label="Modèle" value={d.modele} />
                    <Row label="Statut" value={<span className="takata-badge" style={{ background: fondStatut(st.color), color: texteTheme(st.color) }}>{st.label}</span>} />
                    <SelectRow label="Origine de l'info" field="source_info" options={ORIGINES_INFO.map(o => ({ value: o, label: o }))} />
                    {getField(d, 'source_info') === 'Autre' && <EditRow label="Préciser" field="source_info_autre" />}
                    <Row label="Location" value={d.location} />
                  </div>
                  <div>
                    <Row label="Date premier contact" value={formatDate(d.date_premier_contact || contacts.find(c => c.est_premier_contact)?.date_heure)} />
                    <Row label="Date appel & proposition RDV" value={formatDate(d.date_premier_contact)} />
                    <Row label="Date RDV proposé" value={formatDate(d.dernier_rdv)} />
                    <Row label="Type RDV" value={d.type_rdv === 'atelier' ? 'Atelier' : d.type_rdv === 'domicile' ? 'Domicile' : d.type_rdv} />
                    <SelectRow label="Statut remorquage" field="remorquage_statut" options={[{ value: 'demande', label: 'Demandé' }, { value: 'traite', label: 'Traité' }]} />
                    <Row label="Statut RDV" value={(() => {
                      const rdvSt = getLastRdvStatut(d.id);
                      if (!rdvSt) return '—';
                      const info = rdvStatutInfo(rdvSt);
                      const bg = info.color.startsWith('#') ? info.color + '22' : 'var(--bg-muted)';
                      return <span className="takata-badge" style={{ background: bg, color: info.color }}>{info.label}</span>;
                    })()} />
                    <Row label="Airbag Conducteur Concerné" value={d.driv_airbag_concerned ? '✅ Oui' : '—'} />
                    <Row label="Airbag Conducteur Remplacé" value={d.driv_airbag_replaced ? '✅ Oui' : '—'} />
                    <Row label="Date Remplacement Driv" value={formatDate(d.replacement_date_driv)} />
                    <Row label="Airbag Passager Concerné" value={d.pass_airbag_concerned ? '✅ Oui' : '—'} />
                    <Row label="Airbag Passager Remplacé" value={d.passenger_airbag_replaced ? '✅ Oui' : '—'} />
                    <Row label="Date Remplacement Pass" value={formatDate(d.replacement_date_pass)} />
                    <Row label="Délai réparation" value={delaiRep != null && delaiRep >= 0 ? `${delaiRep} j` : null} />
                    <Row label="Prêt VH — Décision" value={(() => { const p = (allPrets[d.id] || [])[0]; return p ? (p.propose ? 'Accepté' : 'Refusé') : null; })()} />
                    <SelectRow label="Statut réclamation" field="reclamation_statut" options={[{ value: 'en_cours', label: 'En cours' }, { value: 'termine', label: 'Terminé' }]} />
                  </div>
                </div>
                <div style={{ marginTop: 20 }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: 10 }}>Véhicule de prêt</h3>
                  <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: 12 }}>
                    {(() => {
                      const p = (allPrets[d.id] || [])[0];
                      const dateProp = p?.created_at ? formatDate(p.created_at) : null;
                      const dateMoeuvre = p?.date_debut ? formatDate(p.date_debut) : null;
                      const delai = diffJours(p?.created_at, p?.date_debut);
                      return <>
                        <Row label="Date proposition mobilité" value={dateProp} />
                        <Row label="Date mise en œuvre mobilité" value={dateMoeuvre} />
                        <Row label="Délai Mise à disposition du véh." value={delai != null && delai >= 0 ? `${delai} j` : null} />
                      </>;
                    })()}
                  </div>
                </div>

                <div style={{ marginTop: 20 }}>
                  <h3 style={{ fontSize: '0.95rem', marginBottom: 10 }}>Réclamation</h3>
                  <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: 12 }}>
                    <EditRow label="Date réclamation client" field="date_reclamation" type="date" />
                    <EditRow label="Motif réclamation" field="motif_reclamation" />
                    <EditRow label="Détail réclamation" field="detail_reclamation" />
                    <EditRow label="Solution proposée" field="solution_proposee" />
                    <EditRow label="Date prévisionnelle résolution" field="date_previsionnelle_resolution" type="date" />
                    <EditRow label="Avancement réclamation" field="avancement_reclamation" />
                    <EditRow label="Date effective résolution" field="date_effective_resolution" type="date" />
                  </div>
                </div>

                {contacts.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <h3 style={{ fontSize: '0.95rem', marginBottom: 10 }}>Journal ({contacts.length} contact{contacts.length > 1 ? 's' : ''})</h3>
                    <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: 12, maxHeight: 250, overflowY: 'auto' }}>
                      {contacts.map((c, i) => {
                        const ct = TYPES_CONTACT.find(x => x.value === c.type) || { label: c.type };
                        const cr = RESULTATS_CONTACT.find(x => x.value === c.resultat) || { label: c.resultat };
                        return (
                          <div key={i} style={{ borderBottom: i < contacts.length - 1 ? '1px solid var(--border-default)' : 'none', paddingBottom: 4, marginBottom: 4, fontSize: '0.85rem' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{formatDateTime(c.date_heure)}</span>
                            {' / '}<strong>{ct.label}</strong>
                            {' / '}<span style={{ color: texteTheme(cr.color) }}>{cr.label}</span>
                            {c.notes && <span style={{ color: 'var(--text-secondary)' }}>{' / '}{c.notes}</span>}
                            {c.est_premier_contact && <span style={{ background: 'var(--color-success-light)', color: 'light-dark(var(--color-success), var(--color-success-dark))', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', marginLeft: 6 }}>🤝 1er contact</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {tooltipDossierId && tooltipPos && allContacts[tooltipDossierId] && (
        <div
          className="reporting-journal-tooltip"
          style={{ display: 'block', top: tooltipPos.top, left: tooltipPos.left }}
          onMouseEnter={clearTooltipTimer}
          onMouseLeave={scheduleCloseTooltip}
        >
          <div style={{ fontWeight: 600, marginBottom: 6, borderBottom: '1px solid var(--border-default)', paddingBottom: 4 }}>Historique ({allContacts[tooltipDossierId].length} contacts)</div>
          {allContacts[tooltipDossierId].map((c, i) => {
            const ct = TYPES_CONTACT.find(x => x.value === c.type) || { label: c.type };
            const cr = RESULTATS_CONTACT.find(x => x.value === c.resultat) || { label: c.resultat };
            return (
              <div key={i} style={{ borderBottom: i < allContacts[tooltipDossierId].length - 1 ? '1px solid var(--border-default)' : 'none', paddingBottom: 3, marginBottom: 3 }}>
                <span style={{ color: 'var(--text-muted)' }}>{formatDate(c.date_heure)}</span>
                {' / '}<strong>{ct.label}</strong>
                {' / '}<span style={{ color: texteTheme(cr.color) }}>{cr.label}</span>
                {c.notes && <span style={{ color: 'var(--text-secondary)' }}>{' / '}{c.notes}</span>}
              </div>
            );
          })}
        </div>
      )}

      {(() => {
        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE_REPORTING));
        const safePage = Math.min(page, totalPages);
        if (totalPages <= 1) return null;
        return (
          <div className="takata-pagination">
            <button disabled={safePage <= 1} onClick={() => setPage(1)}>⏮</button>
            <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>◀</button>
            <span>{safePage} / {totalPages}</span>
            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>▶</button>
            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>⏭</button>
          </div>
        );
      })()}

    </>
  );
}

// ─── Vue Stocks airbags ───────────────────────────────────────────────────────

function StocksView({ user, concessions, isAdmin }) {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [filtreConstr, setFiltreConstr] = useState('');
  // Concession : suit le choix du menu (2026-10-26) pour le super-admin / téléopérateur,
  // comme la liste principale ; les autres comptes restent sur leur périmètre Takata (serveur).
  const [filtreConcession] = useState(() =>
    (['super-admin', 'teleoperateur'].includes(user?.role) && user?.concession_id) ? String(user.concession_id) : '');

  useEffect(() => {
    loadStocks();
  }, [filtreConstr, filtreConcession]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStocks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtreConstr) params.constructeur = filtreConstr;
      if (filtreConcession) params.concession_id = filtreConcession;
      const res = await takataApi.getStocks(params);
      setStocks(res.data.stocks);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openEdit = (stock) => { setEditingStock(stock); setShowForm(true); };
  const openCreate = () => { setEditingStock(null); setShowForm(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette référence ?')) return;
    try { await takataApi.deleteStock(id); setStocks(prev => prev.filter(s => s.id !== id)); }
    catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  return (
    <div className="takata-subview">
      <div className="page-toolbar">
        <select className={`page-select${filtreConstr ? ' active-filter' : ''}`} value={filtreConstr} onChange={e => setFiltreConstr(e.target.value)}>
          <option value="">Tous constructeurs</option>
          {CONSTRUCTEURS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        {isAdmin && <button className="btn-primary" onClick={openCreate}>+ Ajouter une référence</button>}
      </div>

      {showForm && (
        <StockForm
          stock={editingStock}
          concessions={concessions}
          onSaved={(s) => { setShowForm(false); loadStocks(); }}
          onClose={() => setShowForm(false)}
        />
      )}

      {loading ? <div className="takata-loading">Chargement…</div> : (
        <div className="table-container">
          <table className="takata-table">
            <thead>
              <tr><th>Référence</th><th>Désignation</th><th>Constructeur</th><th>Concession</th><th>En stock</th><th>Commandé</th><th>Seuil alerte</th><th>Dernière MAJ</th>{isAdmin && <th></th>}</tr>
            </thead>
            <tbody>
              {stocks.length === 0 && <tr><td colSpan={9} className="takata-empty">Aucune référence</td></tr>}
              {stocks.map(s => {
                const alerte = s.quantite_stock <= s.seuil_alerte;
                return (
                  <tr key={s.id} className={alerte ? 'takata-row-warn' : ''}>
                    <td><strong>{s.reference}</strong></td>
                    <td>{s.designation || '—'}</td>
                    <td>{s.constructeur || '—'}</td>
                    <td>{s.concession_nom || '—'}</td>
                    <td><span style={{ color: alerte ? 'light-dark(#dc2626, var(--color-error-dark))' : 'light-dark(#22c55e, var(--color-success-dark))', fontWeight: 700 }}>{s.quantite_stock}</span>{alerte && ' ⚠'}</td>
                    <td>{s.quantite_commandee}</td>
                    <td>{s.seuil_alerte}</td>
                    <td>{formatDate(s.derniere_maj)}</td>
                    {isAdmin && <td><button className="btn-icon" onClick={() => openEdit(s)}>✏️</button><button className="btn-icon" onClick={() => handleDelete(s.id)}>🗑️</button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StockForm({ stock, concessions, onSaved, onClose }) {
  const [form, setForm] = useState({
    reference: stock?.reference || '', designation: stock?.designation || '',
    constructeur: stock?.constructeur || '', concession_id: stock?.concession_id || '',
    quantite_stock: stock?.quantite_stock ?? 0, quantite_commandee: stock?.quantite_commandee ?? 0,
    seuil_alerte: stock?.seuil_alerte ?? 5,
  });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, concession_id: form.concession_id || null };
      const res = stock ? await takataApi.updateStock(stock.id, data) : await takataApi.createStock(data);
      onSaved(res.data.stock);
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="takata-contact-form" style={{ marginBottom: 16 }}>
      <form onSubmit={handleSubmit}>
        <div className="takata-form-grid">
          <div className="takata-fg"><label>Référence <span className="req">*</span></label><input value={form.reference} onChange={e => set('reference', e.target.value)} required /></div>
          <div className="takata-fg"><label>Désignation</label><input value={form.designation} onChange={e => set('designation', e.target.value)} /></div>
          <div className="takata-fg"><label>Constructeur</label><select value={form.constructeur} onChange={e => set('constructeur', e.target.value)}><option value="">—</option>{CONSTRUCTEURS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div className="takata-fg"><label>Concession</label><select value={form.concession_id} onChange={e => set('concession_id', e.target.value)}><option value="">—</option>{concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}</select></div>
          <div className="takata-fg"><label>Qté en stock</label><input type="number" value={form.quantite_stock} onChange={e => set('quantite_stock', parseInt(e.target.value) || 0)} min={0} /></div>
          <div className="takata-fg"><label>Qté commandée</label><input type="number" value={form.quantite_commandee} onChange={e => set('quantite_commandee', parseInt(e.target.value) || 0)} min={0} /></div>
          <div className="takata-fg"><label>Seuil d'alerte</label><input type="number" value={form.seuil_alerte} onChange={e => set('seuil_alerte', parseInt(e.target.value) || 5)} min={0} /></div>
        </div>
        <div className="takata-contact-footer">
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Enregistrement…' : stock ? 'Mettre à jour' : 'Créer'}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Vue Budget ───────────────────────────────────────────────────────────────

function BudgetView({ concessions, isAdmin, user }) {
  const [subTab, setSubTab] = useState('constructeurs');
  return (
    <div className="takata-subview">
      <div className="takata-sub-tabs">
        {[
          { id: 'constructeurs', label: '🏭 Budgets constructeurs' },
          { id: 'depenses',      label: '🧾 Suivi des dépenses' },
        ].map(t => (
          <button key={t.id} className={`takata-sub-tab${subTab === t.id ? ' active' : ''}`} onClick={() => setSubTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      {subTab === 'constructeurs' && <BudgetConstructeursView concessions={concessions} isAdmin={isAdmin} user={user} />}
      {subTab === 'depenses'      && <DepensesView isAdmin={isAdmin} />}
    </div>
  );
}

function BudgetConstructeursView({ concessions, isAdmin, user }) {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ constructeur: '', concession_id: '', periode: '', budget_previsionnel: '' });
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  useEffect(() => { loadBudgets(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBudgets = async () => {
    setLoading(true);
    try { const res = await takataApi.getBudgets({}); setBudgets(res.data.budgets); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await takataApi.createBudget({ ...form, concession_id: form.concession_id || null, budget_previsionnel: form.budget_previsionnel || null });
      setBudgets(prev => [res.data.budget, ...prev]);
      setShowForm(false);
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
    finally { setSaving(false); }
  };

  const handleValider = async (id, type) => {
    try {
      const res = type === 'prev' ? await takataApi.validerPrevisionnel(id) : await takataApi.validerFinal(id);
      setBudgets(prev => prev.map(b => b.id === id ? res.data.budget : b));
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  const handleUpdate = async (id, field, value) => {
    try {
      const res = await takataApi.updateBudget(id, { [field]: value || null });
      setBudgets(prev => prev.map(b => b.id === id ? res.data.budget : b));
    } catch (err) { alert(err.response?.data?.error || 'Erreur.'); }
  };

  return (
    <div>
      <div className="takata-toolbar">
        <div />
        <button className="btn-primary" onClick={() => setShowForm(v => !v)}>{showForm ? '— Annuler' : '+ Nouvelle ligne budget'}</button>
      </div>

      {showForm && (
        <form className="takata-contact-form" style={{ marginBottom: 16 }} onSubmit={handleCreate}>
          <div className="takata-form-grid">
            <div className="takata-fg"><label>Période <span className="req">*</span></label><input value={form.periode} onChange={e => set('periode', e.target.value)} placeholder="ex: 2026-T1" required /></div>
            <div className="takata-fg"><label>Constructeur</label><select value={form.constructeur} onChange={e => set('constructeur', e.target.value)}><option value="">—</option>{CONSTRUCTEURS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            <div className="takata-fg"><label>Concession</label><select value={form.concession_id} onChange={e => set('concession_id', e.target.value)}><option value="">—</option>{concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}</select></div>
            <div className="takata-fg"><label>Budget prévisionnel (€)</label><input type="number" value={form.budget_previsionnel} onChange={e => set('budget_previsionnel', e.target.value)} min={0} step="0.01" /></div>
          </div>
          <div className="takata-contact-footer">
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Création…' : 'Créer'}</button>
          </div>
        </form>
      )}

      {loading ? <div className="takata-loading">Chargement…</div> : (
        <div className="table-container">
          <table className="takata-table">
            <thead>
              <tr><th>Période</th><th>Constructeur</th><th>Concession</th><th>Prévisionnel</th><th>En cours</th><th>Final</th><th>Statut</th>{isAdmin && <th>Actions</th>}</tr>
            </thead>
            <tbody>
              {budgets.length === 0 && <tr><td colSpan={8} className="takata-empty">Aucun budget</td></tr>}
              {budgets.map(b => (
                <tr key={b.id}>
                  <td><strong>{b.periode}</strong></td>
                  <td>{b.constructeur || '—'}</td>
                  <td>{b.concession_nom || '—'}</td>
                  <td>
                    {isAdmin && !b.previsionnel_valide ? (
                      <input type="number" defaultValue={b.budget_previsionnel || ''} className="takata-budget-input" step="0.01" onBlur={e => handleUpdate(b.id, 'budget_previsionnel', e.target.value)} />
                    ) : b.budget_previsionnel ? `${parseFloat(b.budget_previsionnel).toLocaleString('fr-FR')} €` : '—'}
                    {b.previsionnel_valide && <span className="takata-validated">✓ {formatDate(b.previsionnel_valide_le)}</span>}
                  </td>
                  <td>
                    {isAdmin && !b.final_valide ? (
                      <input type="number" defaultValue={b.budget_en_cours || ''} className="takata-budget-input" step="0.01" onBlur={e => handleUpdate(b.id, 'budget_en_cours', e.target.value)} />
                    ) : b.budget_en_cours ? `${parseFloat(b.budget_en_cours).toLocaleString('fr-FR')} €` : '—'}
                  </td>
                  <td>
                    {isAdmin && !b.final_valide ? (
                      <input type="number" defaultValue={b.budget_final || ''} className="takata-budget-input" step="0.01" onBlur={e => handleUpdate(b.id, 'budget_final', e.target.value)} />
                    ) : b.budget_final ? `${parseFloat(b.budget_final).toLocaleString('fr-FR')} €` : '—'}
                    {b.final_valide && <span className="takata-validated">✓ {formatDate(b.final_valide_le)}</span>}
                  </td>
                  <td>
                    {b.final_valide ? <span className="takata-badge" style={{ background: 'var(--color-success-light)', color: 'var(--color-success-dark)' }}>Clôturé</span>
                      : b.previsionnel_valide ? <span className="takata-badge" style={{ background: 'var(--color-info-light)', color: 'var(--color-info-dark)' }}>Validé prev.</span>
                      : <span className="takata-badge" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>En saisie</span>}
                  </td>
                  {isAdmin && (
                    <td>
                      {!b.previsionnel_valide && b.budget_previsionnel && <button className="btn-xs" onClick={() => handleValider(b.id, 'prev')}>Valider prev.</button>}
                      {b.previsionnel_valide && !b.final_valide && b.budget_final && <button className="btn-xs btn-success" onClick={() => handleValider(b.id, 'final')}>Clôturer</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Vue Suivi des dépenses ───────────────────────────────────────────────────

const QUADRIMESTRES = ['Q1', 'Q2', 'Q3'];
const _CY = new Date().getFullYear();
const ANNEES_DEPENSES = Array.from({ length: 5 }, (_, i) => _CY - 2 + i); // current-2 … current+2

// Input avec suggestions natives (datalist) — propose les valeurs existantes, saisie libre autorisée
function DatalistInput({ value, onChange, options, required, disabled, placeholder }) {
  const listId = useMemo(() => `dl-${Math.random().toString(36).slice(2)}`, []);
  return (
    <>
      <input list={listId} value={value} onChange={e => onChange(e.target.value)} required={required} disabled={disabled} placeholder={placeholder} autoComplete="off" />
      <datalist id={listId}>{options.map((o, i) => <option key={i} value={o} />)}</datalist>
    </>
  );
}

// Badge « lien manquant » (devis sans facture / facture sans devis)
function isLinkMissing(d) { return !d.linked_expense_id; }
function missingLabel(d) { return (d.nature === 'DEVIS') ? 'Sans facture' : 'Sans devis'; }

function DepensesView({ isAdmin }) {
  const [depenses, setDepenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { mode:'view', id } | { mode:'create' }
  const [exporting, setExporting] = useState(false);
  const [exportToast, setExportToast] = useState(null); // { msg, type }

  // Filtres
  const [fNature, setFNature] = useState([]);
  const [fAnnee, setFAnnee] = useState([]);
  const [fQuad, setFQuad] = useState([]);
  const [fFournisseur, setFFournisseur] = useState([]);
  const [fSociete, setFSociete] = useState([]);
  const [sansFacture, setSansFacture] = useState(false); // devis sans facture
  const [sansDevis, setSansDevis] = useState(false);     // factures sans devis

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try { const r = await takataApi.getDepenses(); setDepenses(r.data.depenses); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const distinct = (key) => Array.from(new Set(depenses.map(d => d[key]).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const fournisseurOptions = useMemo(() => distinct('fournisseur'), [depenses]); // eslint-disable-line react-hooks/exhaustive-deps
  const societeOptions     = useMemo(() => distinct('societe_facturee'), [depenses]); // eslint-disable-line react-hooks/exhaustive-deps
  const anneeOptions       = useMemo(() => [...new Set(depenses.map(d => d.annee).filter(Boolean))].sort((a, b) => b - a), [depenses]);

  const filteredDepenses = useMemo(() => depenses.filter(d => {
    if (fNature.length && !fNature.includes(d.nature || 'FACTURE')) return false;
    if (fAnnee.length && !fAnnee.includes(d.annee)) return false;
    if (fQuad.length && !fQuad.includes(d.quadrimestre)) return false;
    if (fFournisseur.length && !fFournisseur.includes(d.fournisseur)) return false;
    if (fSociete.length && !fSociete.includes(d.societe_facturee)) return false;
    if (sansFacture && !(d.nature === 'DEVIS' && isLinkMissing(d))) return false;
    if (sansDevis && !(d.nature === 'FACTURE' && isLinkMissing(d))) return false;
    return true;
  }), [depenses, fNature, fAnnee, fQuad, fFournisseur, fSociete, sansFacture, sansDevis]); // eslint-disable-line react-hooks/exhaustive-deps

  const showExportToast = (msg, type = 'error') => {
    setExportToast({ msg, type });
    setTimeout(() => setExportToast(null), 4000);
  };

  // Export du tableau tel qu'affiché : `filteredDepenses` porte déjà tous les
  // filtres actifs (Nature/Année/Quad./Fournisseur/Société/liens manquants),
  // appliqués côté client comme le reste de cette vue (`getDepenses()` charge
  // tout, sans pagination) — le fichier correspond donc exactement à l'écran.
  // « Quad. » et « Date / N° du document » sont scindées en 2 colonnes chacune.
  const exportExcel = () => {
    if (!filteredDepenses.length) { showExportToast('Aucune dépense à exporter'); return; }
    setExporting(true);
    try {
      const HEADER = ['Nature', 'Quadrimestre', 'Année', 'Fournisseur', 'Société facturée', 'Date', 'N° du document', 'Prestation', 'Montant HT', 'Montant TTC', 'Facture'];
      const rows = filteredDepenses.map(d => [
        d.nature || 'FACTURE',
        d.quadrimestre || '',
        d.annee || '',
        d.fournisseur || '',
        d.societe_facturee || '',
        d.date_facture ? toExcelDateVal(d.date_facture) : null,
        d.numero_facture || '',
        d.prestation || '',
        d.montant_ht != null && d.montant_ht !== '' ? parseFloat(d.montant_ht) : null,
        d.montant_ttc != null && d.montant_ttc !== '' ? parseFloat(d.montant_ttc) : null,
        d.facture_nom || '',
      ]);
      const ws = XLSX.utils.aoa_to_sheet([HEADER, ...rows]);
      // Colonne F (Date) : vraie cellule date Excel, format court.
      for (let r = 2; r <= rows.length + 1; r++) {
        const ref = `F${r}`;
        if (ws[ref] && ws[ref].v instanceof Date) { ws[ref].t = 'd'; ws[ref].z = 'dd/mm/yyyy'; }
      }
      ws['!cols'] = [
        { wch: 10 }, { wch: 13 }, { wch: 8 }, { wch: 22 }, { wch: 20 },
        { wch: 12 }, { wch: 18 }, { wch: 34 }, { wch: 12 }, { wch: 12 }, { wch: 26 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Dépenses');
      XLSX.writeFile(wb, `takata-depenses-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error(err);
      showExportToast('Erreur lors de la génération du fichier Excel');
    } finally {
      setExporting(false);
    }
  };

  const applyChanged = (updated) => {
    const arr = (Array.isArray(updated) ? updated : [updated]).filter(Boolean);
    setDepenses(prev => prev.map(d => arr.find(u => u.id === d.id) || d));
  };

  const fmtMontant = (v) => v ? `${parseFloat(v).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—';
  const totalHT  = filteredDepenses.reduce((s, d) => s + (parseFloat(d.montant_ht)  || 0), 0);
  const totalTTC = filteredDepenses.reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0);

  const modalDep = modal?.mode === 'view' ? depenses.find(d => d.id === modal.id) : null;

  return (
    <div>
      <div className="takata-toolbar">
        <div className="dep-totaux">
          <span>Total HT : <strong>{totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span>
          <span>Total TTC : <strong>{totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></span>
        </div>
        <button
          className="btn-secondary"
          onClick={exportExcel}
          disabled={exporting || filteredDepenses.length === 0}
          title="Exporter les dépenses affichées (filtres actifs inclus) en Excel"
        >
          {exporting ? '⏳ Export…' : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: -2, marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Exporter Excel
            </>
          )}
        </button>
        <button className="btn-primary" onClick={() => setModal({ mode: 'create' })}>+ Ajouter une dépense</button>
      </div>

      {exportToast && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--color-error-light)', color: 'light-dark(var(--color-error), var(--color-error-dark))', borderRadius: 6, fontSize: '0.9rem' }}>
          ❌ {exportToast.msg}
        </div>
      )}

      <div className="filter-chips-bar" style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <MultiSelectFilter label="Nature" options={[{ value: 'DEVIS', label: 'DEVIS' }, { value: 'FACTURE', label: 'FACTURE' }]} selected={fNature} onChange={setFNature} />
        <MultiSelectFilter label="Année" options={anneeOptions.map(a => ({ value: a, label: String(a) }))} selected={fAnnee} onChange={setFAnnee} />
        <MultiSelectFilter label="Quadrimestre" options={QUADRIMESTRES.map(q => ({ value: q, label: q }))} selected={fQuad} onChange={setFQuad} />
        <MultiSelectFilter label="Fournisseur" options={fournisseurOptions.map(v => ({ value: v, label: v }))} selected={fFournisseur} onChange={setFFournisseur} />
        <MultiSelectFilter label="Société facturée" options={societeOptions.map(v => ({ value: v, label: v }))} selected={fSociete} onChange={setFSociete} />
        <button className={`filter-chip ${sansFacture ? 'chip-active' : 'chip-default'}`} onClick={() => setSansFacture(v => !v)}>⚠ Devis sans facture</button>
        <button className={`filter-chip ${sansDevis ? 'chip-active' : 'chip-default'}`} onClick={() => setSansDevis(v => !v)}>⚠ Factures sans devis</button>
      </div>

      {loading ? <div className="takata-loading">Chargement…</div> : (
        <div className="table-container">
          <table className="takata-table">
            <thead>
              <tr>
                <th>Nature</th><th>Quad.</th><th>Fournisseur</th><th>Société facturée</th><th>Date / N° du document</th><th>Prestation</th>
                <th>Montant HT</th><th>Montant TTC</th><th>Facture</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepenses.length === 0 && <tr><td colSpan={9} className="takata-empty">Aucune dépense</td></tr>}
              {filteredDepenses.map(d => (
                <tr key={d.id} className="takata-row" style={{ cursor: 'pointer' }} onClick={() => setModal({ mode: 'view', id: d.id })}>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600, background: d.nature === 'DEVIS' ? 'var(--color-warning-light)' : 'var(--color-info-light)', color: d.nature === 'DEVIS' ? 'var(--color-warning)' : 'var(--color-info)' }}>{d.nature || 'FACTURE'}</span>
                    {isLinkMissing(d) && <span className="dep-alert-badge" title={missingLabel(d)}>⚠ {missingLabel(d)}</span>}
                  </td>
                  <td>{d.quadrimestre && d.annee ? `${d.quadrimestre} · ${d.annee}` : d.quadrimestre || '—'}</td>
                  <td><strong>{d.fournisseur}</strong></td>
                  <td>{d.societe_facturee || '—'}</td>
                  <td>{d.date_facture ? formatDate(d.date_facture) : '—'}{d.numero_facture && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>N° {d.numero_facture}</div>}</td>
                  <td>{d.prestation || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMontant(d.montant_ht)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMontant(d.montant_ttc)}</td>
                  <td className="dep-facture-cell" onClick={e => e.stopPropagation()}>
                    {d.facture_s3_key
                      ? <DepenseThumbnail depId={d.id} nom={d.facture_nom} type={d.facture_type} />
                      : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {filteredDepenses.length > 0 && (
              <tfoot>
                <tr className="dep-total-row">
                  <td colSpan={6}><strong>Total</strong></td>
                  <td style={{ textAlign: 'right' }}><strong>{totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></td>
                  <td style={{ textAlign: 'right' }}><strong>{totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong></td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {modalDep && (
        <DepenseModal
          key={modalDep.id}
          depense={modalDep}
          depenses={depenses}
          navigable={filteredDepenses}
          isAdmin={isAdmin}
          fournisseurs={fournisseurOptions}
          societes={societeOptions}
          onClose={() => setModal(null)}
          onNavigate={(id) => setModal({ mode: 'view', id })}
          onChanged={applyChanged}
          onDeleted={(id) => { setDepenses(prev => prev.filter(x => x.id !== id)); setModal(null); }}
        />
      )}
      {modal?.mode === 'create' && (
        <DepenseModal
          depense={null}
          depenses={depenses}
          isAdmin={isAdmin}
          fournisseurs={fournisseurOptions}
          societes={societeOptions}
          onClose={() => setModal(null)}
          onCreated={(newDep) => { setDepenses(prev => [newDep, ...prev]); setModal({ mode: 'view', id: newDep.id }); }}
        />
      )}
    </div>
  );
}

// ─── Modale détail / édition d'une dépense (création si depense=null) ──────────
function DepenseModal({ depense, depenses, navigable = [], isAdmin, fournisseurs = [], societes = [], onClose, onNavigate, onChanged, onDeleted, onCreated }) {
  const isCreate = !depense;
  const editable = isCreate || isAdmin;
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const fileRef = useRef(null);

  const [form, setForm] = useState(() => ({
    nature: depense?.nature || 'FACTURE',
    annee: depense?.annee || new Date().getFullYear(),
    quadrimestre: depense?.quadrimestre || '',
    fournisseur: depense?.fournisseur || '',
    societe_facturee: depense?.societe_facturee || '',
    numero_facture: depense?.numero_facture || '',
    date_facture: depense?.date_facture ? depense.date_facture.split('T')[0] : '',
    prestation: depense?.prestation || '',
    montant_ht: depense?.montant_ht || '',
    montant_ttc: depense?.montant_ttc || '',
  }));
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // Navigation (même pattern que DetailDossierModal / FactureModal)
  const currentIdx = isCreate ? -1 : navigable.findIndex(d => d.id === depense.id);
  const prevDep = currentIdx > 0 ? navigable[currentIdx - 1] : null;
  const nextDep = currentIdx >= 0 && currentIdx < navigable.length - 1 ? navigable[currentIdx + 1] : null;
  const canNavigate = !!onNavigate && currentIdx >= 0;

  const save = async () => {
    if (!form.fournisseur.trim()) { alert('Fournisseur obligatoire'); return; }
    if (!form.annee) { alert('Année obligatoire'); return; }
    if (!form.quadrimestre) { alert('Quadrimestre obligatoire'); return; }
    setSaving(true);
    try {
      if (isCreate) {
        const res = await takataApi.createDepense(form);
        onCreated(res.data.depense);
      } else {
        const res = await takataApi.updateDepense(depense.id, form);
        onChanged(res.data.depense);
      }
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try { await takataApi.deleteDepense(depense.id); onDeleted(depense.id); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const uploadFacture = async (file) => {
    setUploading(true);
    try {
      const presign = await takataApi.presignFacture(depense.id, { nom: file.name, type_mime: file.type });
      await fetch(presign.data.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const res = await takataApi.confirmFacture(depense.id, { s3_key: presign.data.s3_key, nom: file.name, type_mime: file.type });
      onChanged(res.data.depense);
    } catch (err) { alert(err.response?.data?.error || 'Erreur upload'); }
    finally { setUploading(false); }
  };

  const removeFacture = async () => {
    if (!window.confirm('Supprimer la pièce jointe ?')) return;
    try { const res = await takataApi.deleteFacture(depense.id); onChanged(res.data.depense); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const doLink = async () => {
    if (!linkTarget) return;
    try { const res = await takataApi.linkDepense(depense.id, parseInt(linkTarget)); onChanged([res.data.depense, res.data.linked]); setLinkTarget(''); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const doUnlink = async () => {
    try { const res = await takataApi.unlinkDepense(depense.id); onChanged([res.data.depense, res.data.linked]); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  // Candidats à la liaison : dépenses de nature opposée, non déjà liées
  const linkCandidates = !isCreate
    ? depenses.filter(d => d.id !== depense.id && (d.nature || 'FACTURE') !== (depense.nature || 'FACTURE') && !d.linked_expense_id)
    : [];

  const natureLabel = form.nature === 'DEVIS' ? 'devis' : 'facture';
  const oppositeLabel = form.nature === 'DEVIS' ? 'facture' : 'devis';

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          {canNavigate && <button className="takata-nav-btn" disabled={!prevDep} onClick={() => prevDep && onNavigate(prevDep.id)} title="Dépense précédente">◀</button>}
          <h2>{isCreate ? 'Nouvelle dépense' : `Dépense ${depense.nature || 'FACTURE'} — ${depense.fournisseur}`}</h2>
          {canNavigate && <span className="takata-modal-counter">{currentIdx + 1}/{navigable.length}</span>}
          {canNavigate && <button className="takata-nav-btn" disabled={!nextDep} onClick={() => nextDep && onNavigate(nextDep.id)} title="Dépense suivante">▶</button>}
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="takata-form-grid">
            <div className="takata-fg"><label>Nature</label>
              <select value={form.nature} onChange={e => set('nature', e.target.value)} disabled={!editable}>
                <option value="DEVIS">DEVIS</option><option value="FACTURE">FACTURE</option>
              </select>
            </div>
            <div className="takata-fg"><label>Année <span className="req">*</span></label>
              <select value={form.annee} onChange={e => set('annee', parseInt(e.target.value))} disabled={!editable} required>
                {ANNEES_DEPENSES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="takata-fg"><label>Quadrimestre <span className="req">*</span></label>
              <select value={form.quadrimestre} onChange={e => set('quadrimestre', e.target.value)} disabled={!editable} required>
                <option value="">— Choisir —</option>
                {QUADRIMESTRES.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div className="takata-fg"><label>Fournisseur <span className="req">*</span></label>
              <DatalistInput value={form.fournisseur} onChange={v => set('fournisseur', v)} options={fournisseurs} required disabled={!editable} />
            </div>
            <div className="takata-fg"><label>Société facturée</label>
              <DatalistInput value={form.societe_facturee} onChange={v => set('societe_facturee', v)} options={societes} disabled={!editable} />
            </div>
            <div className="takata-fg"><label>N° du document</label><input value={form.numero_facture} onChange={e => set('numero_facture', e.target.value)} disabled={!editable} /></div>
            <div className="takata-fg"><label>Date de facture</label><input type="date" value={form.date_facture} onChange={e => set('date_facture', e.target.value)} disabled={!editable} /></div>
            <div className="takata-fg takata-fg--wide"><label>Prestation</label><input value={form.prestation} onChange={e => set('prestation', e.target.value)} disabled={!editable} /></div>
            <div className="takata-fg"><label>Montant HT (€)</label><input type="number" step="0.01" min="0" value={form.montant_ht} onChange={e => set('montant_ht', e.target.value)} disabled={!editable} /></div>
            <div className="takata-fg"><label>Montant TTC (€)</label><input type="number" step="0.01" min="0" value={form.montant_ttc} onChange={e => set('montant_ttc', e.target.value)} disabled={!editable} /></div>
          </div>

          {!isCreate && (
            <>
              {/* Pièce jointe */}
              <div className="dep-modal-section">
                <h3>Pièce jointe</h3>
                {depense.facture_s3_key ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <DepenseThumbnail depId={depense.id} nom={depense.facture_nom} type={depense.facture_type} />
                    {editable && (
                      <div className="action-buttons">
                        <button className="btn-xs" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? '⏳' : '🔄 Remplacer'}</button>
                        <button className="btn-xs" onClick={removeFacture}>🗑️ Supprimer</button>
                      </div>
                    )}
                  </div>
                ) : editable ? (
                  <button className="btn-xs" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? '⏳' : '📎 Joindre'}</button>
                ) : <span style={{ color: 'var(--text-muted)' }}>Aucune pièce jointe</span>}
                <input type="file" style={{ display: 'none' }} ref={fileRef} onChange={e => { if (e.target.files[0]) uploadFacture(e.target.files[0]); e.target.value = ''; }} />
              </div>

              {/* Liaison devis ↔ facture */}
              <div className="dep-modal-section">
                <h3>{form.nature === 'DEVIS' ? 'Facture rattachée' : 'Devis rattaché'}</h3>
                {depense.linked_expense_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                      type="button"
                      className="dep-linked-open"
                      title={`Ouvrir ce ${oppositeLabel}`}
                      onClick={() => onNavigate && onNavigate(depense.linked_expense_id)}
                    >
                      {depense.linked_nature} — <strong>{depense.linked_fournisseur}</strong>{depense.linked_numero_facture ? ` · N° ${depense.linked_numero_facture}` : ''}{depense.linked_date_facture ? ` · ${formatDate(depense.linked_date_facture)}` : ''} ↗
                    </button>
                    {editable && <button className="btn-xs" onClick={doUnlink}>Délier</button>}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span className="dep-alert-badge">⚠ Ce {natureLabel} n'a pas de {oppositeLabel} rattaché(e)</span>
                    {editable && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <select value={linkTarget} onChange={e => setLinkTarget(e.target.value)}>
                          <option value="">— Choisir un(e) {oppositeLabel} —</option>
                          {linkCandidates.map(c => <option key={c.id} value={c.id}>{c.fournisseur}{c.numero_facture ? ` · N° ${c.numero_facture}` : ''}{c.date_facture ? ` · ${formatDate(c.date_facture)}` : ''}</option>)}
                        </select>
                        <button className="btn-secondary btn-sm" disabled={!linkTarget} onClick={doLink}>Lier</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {!isCreate && isAdmin && <button className="btn-danger btn-sm" onClick={handleDelete} style={{ marginRight: 'auto' }}>🗑 Supprimer</button>}
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          {editable && <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : isCreate ? 'Créer' : 'Enregistrer'}</button>}
        </div>
      </div>
    </div>
  );
}

function DepenseThumbnail({ depId, nom, type }) {
  const [url, setUrl] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const isImage = type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(nom || '');
  const isPdf   = type === 'application/pdf' || /\.pdf$/i.test(nom || '');

  useEffect(() => {
    takataApi.getFactureUrl(depId).then(r => setUrl(r.data.url)).catch(() => {});
  }, [depId]);

  if (!url) return <div className="dep-thumb-loading">…</div>;

  return (
    <>
      <div className="dep-thumb" onClick={() => setLightbox(true)} title={nom}>
        {isImage
          ? <img src={url} alt={nom} className="dep-thumb-img" />
          : <div className="dep-thumb-icon">{isPdf ? '📄' : '📎'}<span>{nom}</span></div>}
      </div>
      {lightbox && (
        <div className="doc-viewer-overlay" onClick={() => setLightbox(false)}>
          <div className="doc-viewer-content" onClick={e => e.stopPropagation()}>
            <div className="doc-viewer-header">
              <span className="doc-viewer-title">{nom}</span>
              <button className="doc-viewer-close" onClick={() => setLightbox(false)}>✕</button>
            </div>
            <div className="doc-viewer-body">
              {isImage && <img src={url} alt={nom} className="doc-viewer-image" />}
              {isPdf   && <embed src={url} type="application/pdf" className="doc-viewer-pdf" />}
              {!isImage && !isPdf && (
                <div className="doc-viewer-fallback">
                  <span style={{ fontSize: 60 }}>📎</span><p>{nom}</p>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="btn-primary">Télécharger</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Vue Courriers globale (tous dossiers) ───────────────────────────────────

function CourriersGlobalView({ user, onOpenDossier, onFiltersChange }) {
  const [courriers, setCourriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreConstructeur, setFiltreConstructeur] = useState([]);
  // Concession : suit le choix du menu (2026-10-26) pour le super-admin / téléopérateur,
  // comme la liste principale ; les autres comptes restent sur leur périmètre Takata (serveur).
  const [filtreConcession] = useState(() =>
    (['super-admin', 'teleoperateur'].includes(user?.role) && user?.concession_id) ? [String(user.concession_id)] : []);
  const [filtreMode, setFiltreMode] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState([]);

  const loadCourriers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filtreConstructeur.length) params.constructeur = filtreConstructeur.join(',');
      if (filtreConcession.length) params.concession_id = filtreConcession.join(',');
      if (filtreMode.length) params.mode_envoi = filtreMode.join(',');
      if (filtreStatut.length) params.statut_retour = filtreStatut.join(',');
      if (onFiltersChange) onFiltersChange(params);
      const res = await takataApi.getAllCourriers(params);
      setCourriers(res.data.courriers);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filtreConstructeur, filtreConcession, filtreMode, filtreStatut]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadCourriers(); }, [loadCourriers]);

  const constructeurs = useMemo(() => {
    const set = new Set(courriers.map(c => c.constructeur).filter(Boolean));
    return [...set].sort();
  }, [courriers]);

  const modeLabel = (m) => {
    if (m === 'simple') return '📨 Simple';
    if (m === 'recommande') return '📬 RAR';
    return '—';
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher VIN, immat, destinataire…" value={search} onChange={e => setSearch(e.target.value)} />
        <MultiSelectFilter
          label="Constructeur"
          options={constructeurs.map(c => ({ value: c, label: c }))}
          selected={filtreConstructeur}
          onChange={setFiltreConstructeur}
        />
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <MultiSelectFilter
          label="Mode d'envoi"
          options={[{ value: 'simple', label: '📨 Simple' }, { value: 'recommande', label: '📬 Recommandé' }]}
          selected={filtreMode}
          onChange={setFiltreMode}
        />
        <MultiSelectFilter
          label="Statut retour"
          options={STATUTS_RETOUR.map(s => ({ value: s.value, label: s.label }))}
          selected={filtreStatut}
          onChange={setFiltreStatut}
        />
      </div>

      {loading ? <div className="takata-loading">Chargement…</div> : (
        <>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{courriers.length} courrier{courriers.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>VIN</th>
                  <th>Immat</th>
                  <th>Marque / Modèle</th>
                  <th>Destinataire</th>
                  <th>Mode</th>
                  <th>Type</th>
                  <th>Date envoi</th>
                  <th>Statut La Poste</th>
                  <th>Statut retour</th>
                  <th>Lot</th>
                </tr>
              </thead>
              <tbody>
                {courriers.map(c => {
                  const retour = retourInfo(c.statut_retour);
                  return (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => onOpenDossier({ id: c.dossier_id, vin: c.vin })}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.vin}</td>
                      <td>{c.immat || '—'}</td>
                      <td>{[c.marque, c.modele].filter(Boolean).join(' ') || '—'}</td>
                      <td>{c.destinataire_nom || [c.client_nom, c.client_prenom].filter(Boolean).join(' ') || '—'}</td>
                      <td>{modeLabel(c.mode_envoi)}</td>
                      <td>{c.type === '1er_envoi' ? '1er envoi' : c.type === 'relance' ? 'Relance' : '—'}</td>
                      <td>{formatDate(c.date_envoi)}</td>
                      <td>{c.statut_laposte || '—'}</td>
                      <td><span className="takata-badge" style={{ background: retour.color + '22', color: texteTheme(retour.color), fontSize: 11 }}>{retour.label}</span></td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nom_envoi || '—'}</td>
                    </tr>
                  );
                })}
                {courriers.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>Aucun courrier</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Modale Import Maileva ────────────────────────────────────────────────────

function ImportMailevaModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [modeEnvoi, setModeEnvoi] = useState('recommande');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await takataApi.importMaileva(file, modeEnvoi);
      setResult(res.data);
      if (res.data.imported > 0) onImported();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📬 Import Maileva</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 13 }}>
            Importez un fichier CSV exporté depuis Maileva. Les courriers seront rattachés aux dossiers Takata via le VIN (colonne « Référence interne du destinataire »).
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Type d'envoi</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { value: 'simple', label: '📨 Courrier simple' },
                { value: 'recommande', label: '📬 Recommandé (RAR)' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setModeEnvoi(opt.value)}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: modeEnvoi === opt.value ? 600 : 400, cursor: 'pointer',
                    border: `2px solid ${modeEnvoi === opt.value ? 'var(--color-accent)' : 'var(--border-default)'}`,
                    background: modeEnvoi === opt.value ? 'var(--color-accent)11' : 'var(--bg-surface)',
                    color: modeEnvoi === opt.value ? 'var(--color-accent)' : 'var(--text-secondary)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={e => { setFile(e.target.files[0] || null); setResult(null); }}
            style={{ marginBottom: 16 }}
          />
          {result && (
            <div style={{ background: 'var(--bg-muted)', borderRadius: 8, padding: 12, fontSize: 13 }}>
              <div><strong>{result.imported}</strong> courrier{result.imported !== 1 ? 's' : ''} importé{result.imported !== 1 ? 's' : ''} sur {result.total} lignes</div>
              {result.skipped > 0 && <div style={{ color: 'var(--text-muted)' }}>{result.skipped} ligne{result.skipped !== 1 ? 's' : ''} ignorée{result.skipped !== 1 ? 's' : ''} (VIN invalide)</div>}
              {result.notFound > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ color: 'light-dark(var(--color-warning), var(--color-warning-dark))', marginBottom: 6 }}>{result.notFound} VIN non trouvé{result.notFound !== 1 ? 's' : ''} en base</div>
                  {result.vinsNotFound && result.vinsNotFound.length > 0 && (
                    <>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 8 }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-surface)', textAlign: 'left' }}>
                            <th style={{ padding: '4px 6px', borderBottom: '1px solid var(--border-default)' }}>VIN</th>
                            <th style={{ padding: '4px 6px', borderBottom: '1px solid var(--border-default)' }}>Destinataire</th>
                            <th style={{ padding: '4px 6px', borderBottom: '1px solid var(--border-default)' }}>Immat</th>
                            <th style={{ padding: '4px 6px', borderBottom: '1px solid var(--border-default)' }}>CP / Ville</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.vinsNotFound.slice(0, 20).map((v, i) => (
                            <tr key={i}>
                              <td style={{ padding: '3px 6px', fontFamily: 'monospace', fontSize: 11 }}>{v.vin}</td>
                              <td style={{ padding: '3px 6px' }}>{v.destinataire}</td>
                              <td style={{ padding: '3px 6px' }}>{v.immat}</td>
                              <td style={{ padding: '3px 6px' }}>{v.cp_ville}</td>
                            </tr>
                          ))}
                          {result.vinsNotFound.length > 20 && <tr><td colSpan={4} style={{ padding: '3px 6px', color: 'var(--text-muted)' }}>… et {result.vinsNotFound.length - 20} autres</td></tr>}
                        </tbody>
                      </table>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => {
                          const header = 'VIN;Destinataire;Immat;CP_Ville;Lot\n';
                          const rows = result.vinsNotFound.map(v => `${v.vin};${v.destinataire};${v.immat};${v.cp_ville};${v.nom_envoi}`).join('\n');
                          const blob = new Blob(['\ufeff' + header + rows], { type: 'text/csv;charset=utf-8' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `vins_non_trouves_${new Date().toISOString().slice(0, 10)}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: -1 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Exporter les VINs non trouvés (.csv)
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          <button className="btn-primary" onClick={handleImport} disabled={!file || importing}>
            {importing ? 'Import en cours…' : 'Importer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Vue Import VINs ──────────────────────────────────────────────────────────

// Mapping colonnes Excel Suzuki → champs DB
const EXCEL_COL_MAP = {
  'BRAND':                                  'marque',
  'MODEL/TYPE':                             'modele',
  'TYPE APPROVAL \nNUMBER':                 'type_approval_number',
  'TYPE APPROVAL NUMBER':                   'type_approval_number',
  'VIN':                                    'vin',
  'LOCATION':                               'location',
  'RECALL CODE':                            'recall_code',
  'STOP DRIVE ?':                           'stop_drive',
  'DATE OF SENDING OF THE 1ST LETTER':      'date_first_letter',
  'DRIVER AIRBAG CONCERNED ?':              'driv_airbag_concerned',
  'driv_airbag_concerned':                  'driv_airbag_concerned',
  'DRIVER AIRBAG REPLACED ?':               'driv_airbag_replaced',
  'REPLACEMENT DATE DRIV':                  'replacement_date_driv',
  'REPLACEMENT_DATE_driv':                  'replacement_date_driv',
  'PASSENGER AIRBAG CONCERNED ?':           'pass_airbag_concerned',
  'pass_airbag_concerned':                  'pass_airbag_concerned',
  'PASSENGER AIRBAG REPLACED ?':            'passenger_airbag_replaced',
  'REPLACEMENT DATE PASS':                  'replacement_date_pass',
  'REPLACEMENT_DATE_pass':                  'replacement_date_pass',
  'DATE OF SENDING OF THE LAST LETTER':     'date_last_letter',
  'IMMAT':                                  'immat',
  'STATUT':                                 'statut',
  'STATUT.1':                               'statut',
  'recall_code_driv':                       'recall_code_driv',
  'recall_code_pass':                       'recall_code_pass',
  'REPLACEMENT DATE':                       'replacement_date_driv',
  'CONCESSION':                             'concession_label',
  'SOURCE INFO':                            'source_info',
  'SOURCE_INFO':                            'source_info',
  'Source Info':                             'source_info',
  'DATE OF PRODUCTION':                     'date_production',
  'DATE PRODUCTION':                        'date_production',
  'Date of Production':                     'date_production',
};

function parseExcelDate(v) {
  if (!v) return null;
  if (v instanceof Date) {
    // Utiliser les composants locaux pour éviter le décalage UTC
    const y = v.getFullYear(), m = String(v.getMonth() + 1).padStart(2, '0'), d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const n = parseFloat(v);
  if (!isNaN(n) && n > 40000) {
    // Excel serial → UTC days, extraire en UTC pour éviter le décalage timezone
    const ms = Math.round((n - 25569) * 86400 * 1000);
    const dt = new Date(ms);
    const y = dt.getUTCFullYear(), m = String(dt.getUTCMonth() + 1).padStart(2, '0'), d = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(v).trim();
  const dp = Date.parse(s);
  if (isNaN(dp)) return null;
  const dt2 = new Date(dp);
  const y = dt2.getFullYear(), m = String(dt2.getMonth() + 1).padStart(2, '0'), d = String(dt2.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function ImportView({ concessions, isAdmin, onImported }) {
  const [text, setText] = useState('');
  const [constructeur, setConstructeur] = useState('');
  const [concessionId, setConcessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  // ── Import Excel structuré ──────────────────────────────────────────────
  const [xlsxRows, setXlsxRows] = useState(null);
  const [xlsxConcessionId, setXlsxConcessionId] = useState('');
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [xlsxResult, setXlsxResult] = useState(null);
  const xlsxRef = useRef();

  // ── Import IMMATs simple (VIN → IMMAT, ne crée jamais) ─────────────────
  const [immatRows, setImmatRows] = useState(null);
  const [immatLoading, setImmatLoading] = useState(false);
  const [immatResult, setImmatResult] = useState(null);
  const [immatFileName, setImmatFileName] = useState('');
  const immatRef = useRef();

  const handleImmatFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImmatRows(null); setImmatResult(null); setImmatFileName(file.name);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      // On parcourt toutes les feuilles et on prend la première qui contient des colonnes VIN + IMMAT
      let raw = null;
      let sheetUsed = null;
      for (const name of wb.SheetNames) {
        const candidate = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: null });
        if (candidate.length >= 2) {
          const headers = candidate[0].map(h => (h ? String(h).trim().toUpperCase() : ''));
          if (headers.some(h => /VIN/.test(h)) && headers.some(h => /IMMAT/.test(h))) {
            raw = candidate; sheetUsed = name; break;
          }
        }
      }
      if (!raw) {
        alert('Aucune feuille avec les colonnes VIN et IMMAT n\'a été trouvée. Vérifie les en-têtes.');
        return;
      }
      const headers = raw[0].map(h => (h ? String(h).trim().toUpperCase() : ''));
      const vinIdx = headers.findIndex(h => /^VIN$/.test(h) || /\bVIN\b/.test(h));
      const immatIdx = headers.findIndex(h => /^IMMAT/.test(h) || /\bIMMAT/.test(h) || /^IMMATRICULATION$/.test(h));
      if (vinIdx === -1 || immatIdx === -1) {
        alert(`Colonnes introuvables (feuille "${sheetUsed}"). En-têtes détectés : ${headers.join(', ')}`);
        return;
      }
      const rows = [];
      for (let i = 1; i < raw.length; i++) {
        const vin = raw[i][vinIdx];
        const immat = raw[i][immatIdx];
        if (!vin || !immat) continue;
        const vinStr = String(vin).trim().toUpperCase();
        const immatStr = String(immat).trim().toUpperCase().replace(/\s+/g, '');
        if (vinStr.length < 5 || !immatStr) continue;
        rows.push({ vin: vinStr, immat: immatStr });
      }
      if (rows.length === 0) {
        alert('Aucune ligne exploitable (VIN + IMMAT non vides) trouvée.');
        return;
      }
      setImmatRows(rows);
    } catch (err) {
      alert('Erreur lecture Excel : ' + err.message);
    }
  };

  const handleImmatImport = async () => {
    if (!immatRows?.length) return;
    setImmatLoading(true); setImmatResult(null);
    try {
      const res = await takataApi.importImmats({ rows: immatRows });
      setImmatResult(res.data);
      if (res.data.updated > 0) onImported();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur import IMMATs.');
    } finally {
      setImmatLoading(false);
    }
  };

  const resetImmatImport = () => {
    setImmatRows(null); setImmatResult(null); setImmatFileName('');
    if (immatRef.current) immatRef.current.value = '';
  };

  const handleXlsxFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setXlsxRows(null); setXlsxResult(null);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array', cellDates: true });
      const sheet = wb.Sheets['File'] || wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
      if (raw.length < 2) { alert('Feuille vide ou format inattendu.'); return; }

      // Ligne 0 = headers
      const headers = raw[0].map(h => (h ? String(h).trim().replace(/\r?\n/g, ' ') : ''));
      // Index normalisé (uppercase, espaces simplifiés) pour matching souple
      const normalizedMap = {};
      for (const [k, v] of Object.entries(EXCEL_COL_MAP)) {
        normalizedMap[k.toUpperCase().replace(/\s+/g, ' ').trim()] = v;
      }
      const rows = [];
      for (let i = 1; i < raw.length; i++) {
        const row = {};
        headers.forEach((h, j) => {
          const field = EXCEL_COL_MAP[h] || normalizedMap[h.toUpperCase().replace(/\s+/g, ' ').trim()];
          if (field) row[field] = raw[i][j];
        });
        if (row.vin && String(row.vin).trim().length >= 5) {
          // Convertir dates
          ['date_first_letter', 'replacement_date_driv', 'replacement_date_pass', 'date_last_letter', 'date_production'].forEach(f => {
            row[f] = parseExcelDate(row[f]);
          });
          // Normaliser statut : minuscules + suppression des accents (ex: "Réparé" → "repare")
          if (row.statut) row.statut = String(row.statut).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          rows.push(row);
        }
      }
      setXlsxRows(rows);
    } catch (err) { alert('Erreur lecture Excel : ' + err.message); }
  };

  const handleXlsxImport = async () => {
    if (!xlsxRows?.length) return;
    setXlsxLoading(true); setXlsxResult(null);
    try {
      const res = await takataApi.importExcel({ rows: xlsxRows, concession_id: xlsxConcessionId || undefined });
      setXlsxResult(res.data);
      if ((res.data.created + res.data.updated) > 0) onImported();
    } catch (err) { alert(err.response?.data?.error || 'Erreur import Excel.'); }
    finally { setXlsxLoading(false); }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText(ev.target.result);
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (!text.trim()) return;
    setLoading(true); setResult(null);
    try {
      const res = await takataApi.importVins({ lignes: text, constructeur: constructeur || undefined, concession_id: concessionId || undefined });
      setResult(res.data);
      if (res.data.created > 0) onImported();
    } catch (err) { alert(err.response?.data?.error || 'Erreur import.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="takata-subview">

      {/* ── Import IMMATs simple (VIN → IMMAT, n'ajoute jamais de dossier) ── */}
      <div className="takata-import-wrap" style={{ marginBottom: 24, borderLeft: '4px solid var(--color-success)' }}>
        <div className="takata-import-hd">
          <h3 style={{ margin: 0 }}>🪪 Import IMMATs (enrichissement)</h3>
          <p className="takata-import-info">
            Charge un fichier Excel contenant les colonnes <code>VIN</code> et <code>IMMAT</code>.
            Seuls les VINs <strong>déjà présents</strong> dans la base sont mis à jour ; les VINs inconnus sont listés en « non trouvés » et <strong>aucun dossier n'est créé</strong>.
          </p>
        </div>

        <div className="takata-import-options">
          <div className="takata-fg">
            <label>Fichier Excel (.xlsx)</label>
            <input ref={immatRef} type="file" accept=".xlsx,.xls" onChange={handleImmatFile} />
          </div>
        </div>

        {immatRows && (
          <div className="takata-import-footer">
            <span className="takata-import-count">
              📋 {immatRows.length} ligne(s) VIN+IMMAT détectée(s){immatFileName && ` dans "${immatFileName}"`}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-secondary" onClick={resetImmatImport} disabled={immatLoading}>Annuler</button>
              <button className="btn-primary" onClick={handleImmatImport} disabled={immatLoading}>
                {immatLoading ? 'Import en cours…' : '⬆️ Mettre à jour les IMMATs'}
              </button>
            </div>
          </div>
        )}

        {immatResult && (
          <div className="takata-import-result">
            <div className="takata-import-result-summary">
              <span className="takata-import-ok">✅ {immatResult.updated} mis à jour</span>
              <span className="takata-import-dup">= {immatResult.unchanged} inchangé(s)</span>
              <span className="takata-import-err" style={{ background: 'var(--color-warning-light)', color: 'var(--color-warning-dark, #92400e)' }}>
                🔍 {immatResult.not_found_count} VIN(s) non trouvé(s)
              </span>
              {immatResult.errors > 0 && <span className="takata-import-err">❌ {immatResult.errors} erreur(s)</span>}
            </div>
            {immatResult.not_found?.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  Voir les {immatResult.not_found.length} VIN(s) non trouvé(s)
                </summary>
                <ul className="takata-import-errors-list" style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {immatResult.not_found.map((v, i) => <li key={i}><code>{v}</code></li>)}
                </ul>
              </details>
            )}
            {immatResult.details_erreurs?.length > 0 && (
              <ul className="takata-import-errors-list">
                {immatResult.details_erreurs.slice(0, 20).map((e, i) => (
                  <li key={i}><code>{e.vin || '—'}</code> — {e.raison}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ── Import Excel (Suzuki / format constructeur) ── */}
      <div className="takata-import-wrap" style={{ marginBottom: 24, borderLeft: '4px solid #3b82f6' }}>
        <div className="takata-import-hd">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h3 style={{ margin: 0 }}>📊 Import Excel constructeur</h3>
            <a
              href="/takata_import_template.xlsx"
              download="takata_import_template.xlsx"
              className="btn-template-dl"
              title="Télécharger le fichier modèle"
            >
              ⬇️ Fichier modèle
            </a>
          </div>
          <p className="takata-import-info">
            Charge le fichier de suivi fourni par le constructeur (format Suzuki et compatibles).
            Les VINs existants sont <strong>mis à jour</strong>, les nouveaux sont <strong>créés</strong>.
          </p>
        </div>

        <div className="takata-import-options">
          <div className="takata-fg">
            <label>Concession par défaut</label>
            <select value={xlsxConcessionId} onChange={e => setXlsxConcessionId(e.target.value)}>
              <option value="">— optionnel —</option>
              {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div className="takata-fg">
            <label>Fichier Excel (.xlsx)</label>
            <input ref={xlsxRef} type="file" accept=".xlsx,.xls" onChange={handleXlsxFile} />
          </div>
        </div>

        {xlsxRows && (
          <div className="takata-import-footer">
            <span className="takata-import-count">📋 {xlsxRows.length} véhicule(s) détecté(s) dans la feuille File</span>
            <button className="btn-primary" onClick={handleXlsxImport} disabled={xlsxLoading}>
              {xlsxLoading ? 'Import en cours…' : '⬆️ Lancer l\'import Excel'}
            </button>
          </div>
        )}

        {xlsxResult && (
          <div className="takata-import-result">
            <div className="takata-import-result-summary">
              <span className="takata-import-ok">✅ {xlsxResult.created} créé{xlsxResult.created !== 1 ? 's' : ''}</span>
              <span className="takata-import-dup">🔄 {xlsxResult.updated} mis à jour</span>
              {xlsxResult.errors > 0 && <span className="takata-import-err">❌ {xlsxResult.errors} erreur{xlsxResult.errors !== 1 ? 's' : ''}</span>}
            </div>
            {xlsxResult.details_erreurs?.length > 0 && (
              <ul className="takata-import-errors-list">
                {xlsxResult.details_erreurs.slice(0, 10).map((e, i) => (
                  <li key={i}><code>{e.vin}</code> — {e.raison}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ── Import CSV texte ── */}
      <div className="takata-import-wrap">
        <div className="takata-import-hd">
          <h3>Import en masse de VINs</h3>
          <p className="takata-import-info">Colle ou charge un fichier CSV/texte. Format : <code>VIN[,immat[,nom[,prénom[,téléphone[,email]]]]]</code> — une ligne par véhicule.</p>
        </div>

        <div className="takata-import-options">
          <div className="takata-fg">
            <label>Constructeur par défaut</label>
            <select value={constructeur} onChange={e => setConstructeur(e.target.value)}>
              <option value="">— optionnel —</option>
              {CONSTRUCTEURS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="takata-fg">
            <label>Concession par défaut</label>
            <select value={concessionId} onChange={e => setConcessionId(e.target.value)}>
              <option value="">— optionnel —</option>
              {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div className="takata-fg">
            <label>Charger un fichier</label>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} />
          </div>
        </div>

        <textarea
          className="takata-import-textarea"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={'VF1ABC12345678901\nVF1DEF98765432109,AA-123-BB,DUPONT,Jean,0696123456\n# Les lignes commençant par # sont ignorées'}
          rows={12}
        />

        <div className="takata-import-footer">
          <span className="takata-import-count">{text.split('\n').filter(l => l.trim() && !l.startsWith('#')).length} ligne(s) détectée(s)</span>
          <button className="btn-primary" onClick={handleImport} disabled={loading || !text.trim()}>
            {loading ? 'Import en cours…' : '⬆️ Lancer l\'import'}
          </button>
        </div>

        {result && (
          <div className="takata-import-result">
            <div className="takata-import-result-summary">
              <span className="takata-import-ok">✅ {result.created} créé{result.created !== 1 ? 's' : ''}</span>
              <span className="takata-import-dup">🔁 {result.duplicates} doublon{result.duplicates !== 1 ? 's' : ''}</span>
              {result.errors > 0 && <span className="takata-import-err">❌ {result.errors} erreur{result.errors !== 1 ? 's' : ''}</span>}
            </div>
            {result.details_erreurs?.length > 0 && (
              <div className="takata-import-errors">
                <strong>Erreurs :</strong>
                {result.details_erreurs.map((e, i) => <div key={i} className="takata-import-error-line">• {e.line} — {e.raison}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Modal Export Excel ──────────────────────────────────────────────────────

// ─── Tableau de bord ─────────────────────────────────────────────────────────

const fmtNum = (n) => n.toLocaleString('fr-FR');

const MONTH_LABELS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

// Date de complétion d'un dossier "Terminé" : MAX des dates de remplacement des
// airbags concernés. Fallback sur updated_at pour les cas inspectés sans date.
function getCompletionDateOf(d, statutLabel) {
  if (statutLabel !== 'Terminé') return null;
  const dates = [];
  if (d.driv_airbag_concerned && d.driv_airbag_replaced && d.replacement_date_driv) dates.push(d.replacement_date_driv);
  if (d.pass_airbag_concerned && d.passenger_airbag_replaced && d.replacement_date_pass) dates.push(d.replacement_date_pass);
  if (dates.length === 0 && d.updated_at) dates.push(d.updated_at);
  if (dates.length === 0) return null;
  return dates.reduce((max, x) => (new Date(x) > new Date(max) ? x : max));
}

function pctOf(t, n) { return t > 0 ? Math.round((n / t) * 100) : 0; }

// ── Zone de filtres + indicateurs agrégés (KPIs), en tête de l'onglet Dashboard ──
// Distinct de TakataDashboard ci-dessous (suivi d'avancement par location/marque/année,
// calculé client-side depuis les dossiers déjà chargés) : ici les 8 indicateurs sont
// calculés côté serveur en SQL (GET /api/takata/dashboard). La période ne s'applique
// qu'aux indicateurs 1 à 4 ; les indicateurs 5 à 8 sont « à aujourd'hui » (marque seule).
// Export Excel généré côté front (convention du module, cf. ExportChoiceModal).
const KPI_DEFS = [
  { key: 'nb_avec_date_enr',   label: 'Dossiers enregistrés sur la période', periode: true },
  { key: 'nb_partiel_a_faire', label: 'Enregistrés sur la période — Partiel / À faire', periode: true, ton: 'warn' },
  { key: 'nb_termine',         label: 'Terminés (remplacement sur la période)', periode: true },
  { key: 'delai_moyen_jours',  label: 'Délai moyen de remplacement (j)', periode: true, jours: true },
  { key: 'nb_j15_j20',         label: 'Partiel / À faire — enr. entre J-20 et J-15', ton: 'warn' },
  { key: 'nb_plus_21j',        label: 'Partiel / À faire — enr. il y a plus de 21 j', ton: 'alert' },
  { key: 'nb_rdv_programme',   label: 'Statut « RDV programmé »' },
  { key: 'nb_dernier_contact', label: 'Dernier contact sur la période', periode: true },
];

// Date locale AAAA-MM-JJ (toISOString donnerait la date UTC : le lendemain en soirée aux Antilles).
const dateLocaleISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const kpiDebutParDefaut = () => { const d = new Date(); return dateLocaleISO(new Date(d.getFullYear(), d.getMonth(), 1)); };
const kpiFinParDefaut = () => dateLocaleISO(new Date());

function TakataKpiDashboard() {
  const [dateDebut, setDateDebut] = useState(kpiDebutParDefaut);
  const [dateFin, setDateFin] = useState(kpiFinParDefaut);
  const [marque, setMarque] = useState('');
  const [marques, setMarques] = useState([]);
  const [location, setLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (dateDebut) params.dateDebut = dateDebut;
      if (dateFin) params.dateFin = dateFin;
      if (marque) params.marque = marque;
      if (location) params.location = location;
      const res = await takataApi.getDashboardKpis(params);
      setKpis(res.data);
      if (Array.isArray(res.data.marques)) setMarques(res.data.marques);
      if (Array.isArray(res.data.locations)) setLocations(res.data.locations);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des indicateurs.');
      setKpis(null);
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, marque, location]);

  useEffect(() => { load(); }, [load]);

  const reinitialiser = () => { setDateDebut(kpiDebutParDefaut()); setDateFin(kpiFinParDefaut()); setMarque(''); setLocation(''); };
  const filtresModifies = dateDebut !== kpiDebutParDefaut() || dateFin !== kpiFinParDefaut() || !!marque || !!location;

  const valeurKpi = (def) => {
    const v = kpis?.[def.key];
    if (def.jours) return v != null ? v : null;
    return v || 0;
  };

  const exporterExcel = () => {
    if (!kpis) return;
    const fr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
    const rows = [
      ['Indicateurs Takata — Tableau de bord'],
      ['Période (indicateurs 1 à 4 et 8)', `${fr(dateDebut)} → ${fr(dateFin)}`],
      ['Marque', marque || 'Toutes les marques'],
      ['Location', location || 'Toutes locations'],
      ['Généré le', new Date().toLocaleString('fr-FR')],
      [],
      ['Indicateur', 'Valeur', 'Filtre période'],
      ...KPI_DEFS.map(def => [def.label, valeurKpi(def) ?? '—', def.periode ? 'Oui' : "Non (à aujourd'hui)"]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 50 }, { wch: 22 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Indicateurs');
    XLSX.writeFile(wb, `takata_indicateurs_${dateLocaleISO(new Date())}.xlsx`);
  };

  const toutAZero = kpis && KPI_DEFS.every(def => !kpis[def.key]);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className="page-header-btn" onClick={exporterExcel} disabled={!kpis || loading}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Exporter Excel
        </button>
      </div>
      <div className="page-toolbar" style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
          Du
          <input type="date" className="page-select" value={dateDebut} max={dateFin || undefined} onChange={e => setDateDebut(e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
          au
          <input type="date" className="page-select" value={dateFin} min={dateDebut || undefined} onChange={e => setDateFin(e.target.value)} />
        </label>
        <select className={`page-select${marque ? ' active-filter' : ''}`} value={marque} onChange={e => setMarque(e.target.value)}>
          <option value="">Toutes les marques</option>
          {marques.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className={`page-select${location ? ' active-filter' : ''}`} value={location} onChange={e => setLocation(e.target.value)}>
          <option value="">Toutes locations</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {filtresModifies && <button className="btn-secondary btn-sm" onClick={reinitialiser}>✕ Réinitialiser</button>}
      </div>

      {loading ? (
        <div className="takata-loading">Chargement des indicateurs…</div>
      ) : error ? (
        <div className="takata-error">{error}</div>
      ) : !kpis || toutAZero ? (
        <div className="takata-empty-journal">Aucune donnée pour ces critères.</div>
      ) : (
        <>
          <div className="takata-stats-bar">
            {KPI_DEFS.map(def => {
              const v = valeurKpi(def);
              return (
                <div key={def.key} className={`takata-stat${def.ton ? ` takata-stat--${def.ton}` : ''}`}>
                  <span className="takata-stat-val">{def.jours ? (v != null ? `${v.toLocaleString('fr-FR')} j` : '—') : v.toLocaleString('fr-FR')}</span>
                  <span className="takata-stat-lbl">{def.label}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Les indicateurs 1 à 4 et « Dernier contact sur la période » suivent la période ; les 3 autres sont calculés à la date du jour (seules la marque et la location s'appliquent).
          </div>
        </>
      )}
    </div>
  );
}

function TakataDashboard({ dossiers, getStatutCampagne }) {
  const [filtreLocation, setFiltreLocation] = useState('');
  const [filtreMarque, setFiltreMarque] = useState('');
  const [filtreAnnee, setFiltreAnnee] = useState('');
  const [filtreMois, setFiltreMois] = useState('');
  const [expandedMarques, setExpandedMarques] = useState(() => new Set());
  const [expandedYears, setExpandedYears] = useState(() => new Set());
  const [pdfLoading, setPdfLoading] = useState(false);
  const chartRef = useRef(null);

  // Périmètre absolu : location + marque (les KPI Total ne dépendent QUE de ces filtres)
  const periphDossiers = useMemo(() => dossiers.filter(d => {
    const loc = d.location || '(non renseigné)';
    const marque = d.marque || '(non renseigné)';
    if (filtreLocation && loc !== filtreLocation) return false;
    if (filtreMarque && marque !== filtreMarque) return false;
    return true;
  }), [dossiers, filtreLocation, filtreMarque]);

  // Pour chaque dossier du périmètre : statut + date de complétion (cached)
  const enriched = useMemo(() => periphDossiers.map(d => {
    const statut = getStatutCampagne(d).label;
    const completion = getCompletionDateOf(d, statut);
    return { d, statut, completion };
  }), [periphDossiers, getStatutCampagne]);

  // Helper : un dossier terminé est-il dans la fenêtre année/mois sélectionnée ?
  const inTimeWindow = useCallback((completion) => {
    if (!completion) return false;
    const dt = new Date(completion);
    if (filtreAnnee && dt.getFullYear() !== Number(filtreAnnee)) return false;
    if (filtreMois && (dt.getMonth() + 1) !== Number(filtreMois)) return false;
    return true;
  }, [filtreAnnee, filtreMois]);

  // KPI : Total = périmètre (location/marque uniquement). Terminés (période) =
  // filtré en plus par année/mois si actif, pour la carte « VIN Terminés ».
  // ⚠️ Restants et Avancement doivent rester indépendants du filtre période :
  // un dossier terminé en 2021 reste terminé quand on filtre sur 2022, il ne
  // doit pas repasser « restant ». Ils utilisent donc toujours le total
  // terminé tous temps confondus (totalTermineAllTime), jamais totalTermine.
  const totalVin = periphDossiers.length;
  const totalTermineAllTime = useMemo(
    () => enriched.filter(e => e.statut === 'Terminé').length,
    [enriched]
  );
  const totalTermine = useMemo(() => {
    if (!filtreAnnee && !filtreMois) return totalTermineAllTime;
    return enriched.filter(e => e.statut === 'Terminé' && inTimeWindow(e.completion)).length;
  }, [enriched, filtreAnnee, filtreMois, inTimeWindow, totalTermineAllTime]);
  const pctGlobal = pctOf(totalVin, totalTermineAllTime);

  // Options
  const locationOptions = useMemo(() => {
    const set = new Set();
    for (const d of dossiers) set.add(d.location || '(non renseigné)');
    return Array.from(set).sort();
  }, [dossiers]);
  const marqueOptions = useMemo(() => {
    const set = new Set();
    for (const d of dossiers) set.add(d.marque || '(non renseigné)');
    return Array.from(set).sort();
  }, [dossiers]);
  const anneeOptions = useMemo(() => {
    const set = new Set();
    for (const d of dossiers) {
      const cd = getCompletionDateOf(d, getStatutCampagne(d).label);
      if (cd) set.add(new Date(cd).getFullYear());
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [dossiers, getStatutCampagne]);

  // Arbre Location → Marque → Année → Mois
  // - location.total / marque.total = nb VINs du périmètre (toujours)
  // - location.termine / marque.termine = terminés TOUS TEMPS (sert à Restants/Avancement,
  //   qui ne doivent pas dépendre du filtre année/mois — cf. totalTermineAllTime plus haut)
  // - location.terminePeriode / marque.terminePeriode = terminés dans la fenêtre
  //   année/mois si un filtre est actif (sert à la colonne « Terminés (période) »)
  // - year/month nodes ne contiennent que les terminés de la période (date connue)
  const tree = useMemo(() => {
    const locs = {};
    for (const e of enriched) {
      const loc = e.d.location || '(non renseigné)';
      const marque = e.d.marque || '(non renseigné)';
      const isTermine = e.statut === 'Terminé';
      const countAsTermine = isTermine && (!filtreAnnee && !filtreMois ? true : inTimeWindow(e.completion));

      if (!locs[loc]) locs[loc] = { total: 0, termine: 0, terminePeriode: 0, marques: {} };
      locs[loc].total++;
      if (isTermine) locs[loc].termine++;
      if (countAsTermine) locs[loc].terminePeriode++;

      if (!locs[loc].marques[marque]) locs[loc].marques[marque] = { total: 0, termine: 0, terminePeriode: 0, years: {} };
      const m = locs[loc].marques[marque];
      m.total++;
      if (isTermine) m.termine++;
      if (countAsTermine) m.terminePeriode++;

      if (countAsTermine && e.completion) {
        const dt = new Date(e.completion);
        const y = dt.getFullYear();
        const mo = dt.getMonth() + 1;
        if (!m.years[y]) m.years[y] = { total: 0, termine: 0, months: {} };
        m.years[y].total++;
        m.years[y].termine++;
        if (!m.years[y].months[mo]) m.years[y].months[mo] = { total: 0, termine: 0 };
        m.years[y].months[mo].total++;
        m.years[y].months[mo].termine++;
      }
    }
    return locs;
  }, [enriched, filtreAnnee, filtreMois, inTimeWindow]);

  // Données de la courbe : nb terminés par année-mois.
  // - Si filtreAnnée actif : on affiche les 12 mois de l'année (zéros inclus).
  // - Sinon : on agrège tous les terminés sur tout l'historique.
  // - filtreMois isolé restreint au mois sélectionné.
  const chartData = useMemo(() => {
    const counts = {};
    for (const e of enriched) {
      if (e.statut !== 'Terminé' || !e.completion) continue;
      const dt = new Date(e.completion);
      const y = dt.getFullYear();
      const mo = dt.getMonth() + 1;
      if (filtreAnnee && y !== Number(filtreAnnee)) continue;
      if (filtreMois && mo !== Number(filtreMois)) continue;
      const key = `${y}-${String(mo).padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    // Si année unique sélectionnée et pas de mois précis, compléter avec les 12 mois à 0
    if (filtreAnnee && !filtreMois) {
      const y = Number(filtreAnnee);
      for (let mo = 1; mo <= 12; mo++) {
        const key = `${y}-${String(mo).padStart(2, '0')}`;
        if (!(key in counts)) counts[key] = 0;
      }
    }
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ periode: k, termines: v }));
  }, [enriched, filtreAnnee, filtreMois]);

  // Toggles
  const toggleMarque = (loc, marque) => {
    const key = `${loc}|||${marque}`;
    setExpandedMarques(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleYear = (loc, marque, y) => {
    const key = `${loc}|||${marque}|||${y}`;
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const resetFilters = () => {
    setFiltreLocation(''); setFiltreMarque(''); setFiltreAnnee(''); setFiltreMois('');
  };
  const hasFilters = !!(filtreLocation || filtreMarque || filtreAnnee || filtreMois);

  // Export PDF
  const exportPDF = async () => {
    setPdfLoading(true);
    try {
      const jsPDFMod = await import('jspdf');
      const jsPDF = jsPDFMod.default || jsPDFMod.jsPDF;
      const autoTableMod = await import('jspdf-autotable');
      const autoTable = autoTableMod.default || autoTableMod;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(16); doc.setFont(undefined, 'bold');
      doc.text('Tableau de bord Takata', 14, 15);
      doc.setFontSize(9); doc.setFont(undefined, 'normal');
      doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, 14, 21);
      const filtersText = [
        filtreLocation && `Location : ${filtreLocation}`,
        filtreMarque && `Marque : ${filtreMarque}`,
        filtreAnnee && `Année : ${filtreAnnee}`,
        filtreMois && `Mois : ${MONTH_LABELS[Number(filtreMois)]}`,
      ].filter(Boolean).join('  ·  ');
      if (filtersText) doc.text(`Filtres actifs : ${filtersText}`, 14, 27);

      // KPIs
      let y = filtersText ? 36 : 32;
      const kpis = [
        ['VIN Total', fmtNum(totalVin)],
        [`VIN Terminés${(filtreAnnee || filtreMois) ? ' (période)' : ''}`, fmtNum(totalTermine)],
        ['VIN Restants', fmtNum(totalVin - totalTermineAllTime)],
        ['Avancement', `${pctGlobal} %`],
      ];
      const kpiW = 65, kpiH = 18;
      doc.setFontSize(10);
      kpis.forEach(([lbl, val], i) => {
        const x = 14 + i * (kpiW + 4);
        doc.setDrawColor(180); doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, y, kpiW, kpiH, 2, 2, 'FD');
        doc.setFont(undefined, 'bold'); doc.setFontSize(14);
        doc.text(val, x + kpiW / 2, y + 9, { align: 'center' });
        doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(100);
        doc.text(lbl, x + kpiW / 2, y + 15, { align: 'center' });
        doc.setTextColor(0);
      });
      y += kpiH + 6;

      // Chart (capture SVG)
      const svgEl = chartRef.current?.querySelector('svg');
      if (svgEl && chartData.length > 0) {
        try {
          const bbox = svgEl.getBoundingClientRect();
          const scale = 2;
          const svgString = new XMLSerializer().serializeToString(svgEl);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(bbox.width * scale));
          canvas.height = Math.max(1, Math.round(bbox.height * scale));
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          const img = new Image();
          const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
          await new Promise((resolve, reject) => {
            img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); resolve(); };
            img.onerror = reject;
            img.src = url;
          });
          const dataUrl = canvas.toDataURL('image/png');
          const targetW = 260;
          const ratio = canvas.height / canvas.width;
          const targetH = Math.min(80, targetW * ratio);
          doc.setFontSize(11); doc.setFont(undefined, 'bold');
          doc.text('Évolution des dossiers terminés', 14, y); y += 3;
          doc.addImage(dataUrl, 'PNG', 14, y, targetW, targetH);
          y += targetH + 4;
        } catch (err) {
          console.warn('SVG → PNG failed:', err);
        }
      }

      // Table condensée (Location + Marque + sous-total location)
      const body = [];
      Object.entries(tree).sort(([a], [b]) => a.localeCompare(b)).forEach(([loc, locData]) => {
        Object.entries(locData.marques).sort(([a], [b]) => a.localeCompare(b)).forEach(([marque, mData]) => {
          const mTermineAffiche = (filtreAnnee || filtreMois) ? mData.terminePeriode : mData.termine;
          body.push([loc, marque, fmtNum(mData.total), fmtNum(mTermineAffiche), fmtNum(mData.total - mData.termine), `${pctOf(mData.total, mData.termine)} %`]);
        });
        const locTermineAffiche = (filtreAnnee || filtreMois) ? locData.terminePeriode : locData.termine;
        body.push([
          { content: `Sous-total ${loc}`, colSpan: 2, styles: { fontStyle: 'bold', fillColor: [241, 245, 249] } },
          { content: fmtNum(locData.total), styles: { fontStyle: 'bold', fillColor: [241, 245, 249], halign: 'center' } },
          { content: fmtNum(locTermineAffiche), styles: { fontStyle: 'bold', fillColor: [241, 245, 249], halign: 'center' } },
          { content: fmtNum(locData.total - locData.termine), styles: { fontStyle: 'bold', fillColor: [241, 245, 249], halign: 'center' } },
          { content: `${pctOf(locData.total, locData.termine)} %`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], halign: 'center' } },
        ]);
      });
      body.push([
        { content: 'TOTAL', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [203, 213, 225] } },
        { content: fmtNum(totalVin), styles: { fontStyle: 'bold', fillColor: [203, 213, 225], halign: 'center' } },
        { content: fmtNum(totalTermine), styles: { fontStyle: 'bold', fillColor: [203, 213, 225], halign: 'center' } },
        { content: fmtNum(totalVin - totalTermineAllTime), styles: { fontStyle: 'bold', fillColor: [203, 213, 225], halign: 'center' } },
        { content: `${pctGlobal} %`, styles: { fontStyle: 'bold', fillColor: [203, 213, 225], halign: 'center' } },
      ]);

      autoTable(doc, {
        startY: y,
        head: [['Location', 'Marque', 'Total', 'Terminés', 'Restants', 'Avancement']],
        body,
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          2: { halign: 'center' }, 3: { halign: 'center' },
          4: { halign: 'center' }, 5: { halign: 'center' },
        },
        margin: { left: 14, right: 14 },
      });

      doc.save(`takata_dashboard_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert('Erreur génération PDF : ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // Tri des locations + marques pour le rendu
  const sortedLocs = useMemo(() => Object.entries(tree).sort(([a], [b]) => a.localeCompare(b)), [tree]);

  return (
    <div className="takata-dashboard">
      {/* ── Toolbar filtres + actions ── */}
      <div className="takata-dash-filters">
        <select className="page-select" value={filtreLocation} onChange={e => setFiltreLocation(e.target.value)}>
          <option value="">Toutes locations</option>
          {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className="page-select" value={filtreMarque} onChange={e => setFiltreMarque(e.target.value)}>
          <option value="">Toutes marques</option>
          {marqueOptions.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="page-select" value={filtreAnnee} onChange={e => setFiltreAnnee(e.target.value)}>
          <option value="">Toutes années</option>
          {anneeOptions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="page-select" value={filtreMois} onChange={e => setFiltreMois(e.target.value)}>
          <option value="">Tous mois</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
            <option key={m} value={m}>{MONTH_LABELS[m]}</option>
          ))}
        </select>
        {hasFilters && (
          <button className="btn-secondary btn-sm" onClick={resetFilters}>✕ Réinitialiser</button>
        )}
        <div className="takata-dash-actions">
          <button className="btn-secondary" onClick={exportPDF} disabled={pdfLoading} title="Exporter la page en PDF">
            {pdfLoading ? '⏳ Génération…' : '📄 Export PDF'}
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="takata-dashboard-summary">
        <div className="takata-dash-card">
          <div className="takata-dash-card-value">{fmtNum(totalVin)}</div>
          <div className="takata-dash-card-label">VIN Total</div>
        </div>
        <div className="takata-dash-card takata-dash-card--green">
          <div className="takata-dash-card-value">{fmtNum(totalTermine)}</div>
          <div className="takata-dash-card-label">
            VIN Terminés{(filtreAnnee || filtreMois) ? ' (période)' : ''}
          </div>
        </div>
        <div className="takata-dash-card takata-dash-card--orange">
          <div className="takata-dash-card-value">{fmtNum(totalVin - totalTermineAllTime)}</div>
          <div className="takata-dash-card-label">VIN Restants</div>
        </div>
        <div className="takata-dash-card">
          <div className="takata-dash-card-value">{pctGlobal}%</div>
          <div className="takata-dash-card-label">Avancement</div>
        </div>
      </div>

      {/* ── Courbe ── */}
      <div className="takata-dash-chart" ref={chartRef}>
        <div className="takata-dash-chart-title">
          📈 Évolution des dossiers terminés
          {filtreAnnee && ` — ${filtreAnnee}`}
          {filtreMois && ` / ${MONTH_LABELS[Number(filtreMois)]}`}
        </div>
        {chartData.length === 0 ? (
          <div className="takata-dash-empty">Aucun dossier terminé sur la période sélectionnée.</div>
        ) : (
          <DashboardChart data={chartData} />
        )}
      </div>

      {/* ── Tableau ── */}
      <div className="table-container">
        <table className="takata-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Location / Marque / Année / Mois</th>
              <th style={{ textAlign: 'center' }}>VIN Total</th>
              <th style={{ textAlign: 'center' }}>VIN Terminés{(filtreAnnee || filtreMois) ? ' (période)' : ''}</th>
              <th style={{ textAlign: 'center' }}>Restants</th>
              <th style={{ textAlign: 'center' }}>Avancement</th>
            </tr>
          </thead>
          <tbody>
            {sortedLocs.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>
                Aucun dossier ne correspond aux filtres.
              </td></tr>
            )}
            {sortedLocs.map(([loc, locData]) => {
              const sortedMarques = Object.entries(locData.marques).sort(([a], [b]) => a.localeCompare(b));
              return (
                <React.Fragment key={loc}>
                  {sortedMarques.map(([marque, mData]) => {
                    const key = `${loc}|||${marque}`;
                    const expanded = expandedMarques.has(key);
                    const mPct = pctOf(mData.total, mData.termine);
                    const mTermineAffiche = (filtreAnnee || filtreMois) ? mData.terminePeriode : mData.termine;
                    const hasYears = Object.keys(mData.years).length > 0;
                    return (
                      <React.Fragment key={marque}>
                        <tr
                          className={`takata-dash-row${hasYears ? ' takata-dash-row--clickable' : ''}`}
                          onClick={hasYears ? () => toggleMarque(loc, marque) : undefined}
                        >
                          <td style={{ textAlign: 'center' }}>
                            {hasYears ? (expanded ? '▾' : '▸') : ''}
                          </td>
                          <td>
                            <span className="takata-dash-loc">{loc}</span>
                            <span className="takata-dash-sep"> · </span>
                            <strong>{marque}</strong>
                          </td>
                          <td style={{ textAlign: 'center' }}>{fmtNum(mData.total)}</td>
                          <td style={{ textAlign: 'center', color: 'var(--color-success-dark)', fontWeight: 600 }}>{fmtNum(mTermineAffiche)}</td>
                          <td style={{ textAlign: 'center', color: mData.total - mData.termine > 0 ? 'light-dark(#dc2626, var(--color-error-dark))' : 'light-dark(#16a34a, var(--color-success-dark))' }}>{fmtNum(mData.total - mData.termine)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div className="takata-progress-bar">
                              <div className="takata-progress-fill" style={{ width: `${mPct}%` }} />
                              <span className="takata-progress-label">{mPct}%</span>
                            </div>
                          </td>
                        </tr>
                        {expanded && Object.entries(mData.years).sort(([a], [b]) => Number(b) - Number(a)).map(([year, yData]) => {
                          const yKey = `${loc}|||${marque}|||${year}`;
                          const yExpanded = expandedYears.has(yKey);
                          const hasMonths = Object.keys(yData.months).length > 0;
                          return (
                            <React.Fragment key={year}>
                              <tr
                                className={`takata-dash-row takata-dash-row--year${hasMonths ? ' takata-dash-row--clickable' : ''}`}
                                onClick={hasMonths ? () => toggleYear(loc, marque, year) : undefined}
                              >
                                <td style={{ textAlign: 'center' }}>{hasMonths ? (yExpanded ? '▾' : '▸') : ''}</td>
                                <td style={{ paddingLeft: 32 }}>📅 {year}</td>
                                <td></td>
                                <td style={{ textAlign: 'center', color: 'var(--color-success-dark)', fontWeight: 600 }}>{fmtNum(yData.termine)}</td>
                                <td></td>
                                <td></td>
                              </tr>
                              {yExpanded && Object.entries(yData.months).sort(([a], [b]) => Number(a) - Number(b)).map(([mo, moData]) => (
                                <tr key={mo} className="takata-dash-row takata-dash-row--month">
                                  <td></td>
                                  <td style={{ paddingLeft: 56, color: 'var(--text-secondary)' }}>
                                    {MONTH_LABELS[Number(mo)]} {year}
                                  </td>
                                  <td></td>
                                  <td style={{ textAlign: 'center', color: 'var(--color-success-dark)' }}>{fmtNum(moData.termine)}</td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  {/* Sous-total location */}
                  <tr className="takata-dash-row takata-dash-subtotal">
                    <td></td>
                    <td><strong>Sous-total {loc}</strong></td>
                    <td style={{ textAlign: 'center' }}><strong>{fmtNum(locData.total)}</strong></td>
                    <td style={{ textAlign: 'center', color: 'var(--color-success-dark)' }}><strong>{fmtNum((filtreAnnee || filtreMois) ? locData.terminePeriode : locData.termine)}</strong></td>
                    <td style={{ textAlign: 'center', color: locData.total - locData.termine > 0 ? 'light-dark(#dc2626, var(--color-error-dark))' : 'light-dark(#16a34a, var(--color-success-dark))' }}><strong>{fmtNum(locData.total - locData.termine)}</strong></td>
                    <td style={{ textAlign: 'center' }}><strong>{pctOf(locData.total, locData.termine)}%</strong></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, background: 'var(--bg-muted)' }}>
              <td></td>
              <td>TOTAL</td>
              <td style={{ textAlign: 'center' }}>{fmtNum(totalVin)}</td>
              <td style={{ textAlign: 'center', color: 'var(--color-success-dark)' }}>{fmtNum(totalTermine)}</td>
              <td style={{ textAlign: 'center', color: totalVin - totalTermineAllTime > 0 ? 'light-dark(#dc2626, var(--color-error-dark))' : 'light-dark(#16a34a, var(--color-success-dark))' }}>{fmtNum(totalVin - totalTermineAllTime)}</td>
              <td style={{ textAlign: 'center' }}>{pctGlobal}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Recharts chargé en lazy (≈ 250 Ko) — ne pèse sur le bundle initial que si le dashboard est ouvert.
function DashboardChart({ data }) {
  const [Mod, setMod] = useState(null);
  useEffect(() => {
    let alive = true;
    import('recharts').then(m => { if (alive) setMod(m); });
    return () => { alive = false; };
  }, []);
  if (!Mod) return <div className="takata-dash-empty">Chargement du graphique…</div>;
  const { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } = Mod;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="periode" tick={{ fontSize: 11, fill: '#475569' }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#475569' }} />
        <Tooltip
          formatter={(v) => [fmtNum(v), 'Terminés']}
          contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 12 }}
        />
        <Line type="monotone" dataKey="termines" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Modale Export — Choix du type ──────────────────────────────────────────

const STATUTS_LABEL = {
  a_contacter: 'À contacter', en_cours: 'En cours', rdv_programme: 'RDV programmé',
  repare: 'Réparé', epave: 'Épave', casse: 'Cassé', clos: 'Clos',
  hors_perimetre: 'Hors périmètre', chgt_proprietaire: 'Chgt propriétaire',
  vole: 'Volé', reforme: 'Réformé',
};

// Minuit LOCAL (cf. commentaire toXlsDate — la lib xlsx sérialise via l'heure locale).
const toExcelDateVal = (v) => {
  if (!v) return null;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  if (s.length <= 10) { const d = new Date(+m[1], +m[2] - 1, +m[3]); return isNaN(d) ? null : d; }
  const d = new Date(s);
  if (isNaN(d)) return new Date(+m[1], +m[2] - 1, +m[3]);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// ── Parsing des coordonnées de détection webapp (note « Coordonnées renseignées — … ») ──
// Format source : "Civilité: M. | Nom: … | Tel: … | Adresse: … | Ville: … | Commentaire: …"
const webappCoordsFromNote = (notes) => {
  if (!notes) return '';
  const i = notes.indexOf('Coordonnées renseignées');
  return i >= 0 ? notes.slice(i).replace('Coordonnées renseignées — ', '') : '';
};

const webappParseCoords = (str) => {
  const r = { civilite: '', nom: '', telephone: '', adresse: '', ville: '', commentaire: '' };
  if (!str) return r;
  str.split(' | ').forEach(part => {
    const i = part.indexOf(': ');
    if (i < 0) return;
    const k = part.slice(0, i).trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const v = part.slice(i + 2).trim();
    if (k === 'civilite') r.civilite = v;
    else if (k === 'nom') r.nom = v;
    else if (k === 'tel') r.telephone = v;
    else if (k === 'adresse') r.adresse = v;
    else if (k === 'ville') r.ville = v;
    else if (k === 'commentaire') r.commentaire = v;
  });
  return r;
};

// Helpers export « Dossiers » / « Reporting constructeur » — dérivés des contacts déjà
// chargés pour la colonne Journal (aucun appel serveur supplémentaire).
const contactDateYYYYMMDD = (v) => {
  if (!v) return '';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}${m[2]}${m[3]}` : '';
};
// Date du dernier contact du journal (le plus récent, contacts triés date_heure ASC par le backend).
const lastContactDate = (contacts) => (contacts && contacts.length) ? contacts[contacts.length - 1].date_heure : null;
// Dates de toutes les entrées de journal, format AAAAMMJJ, concaténées par « ; », de la plus récente
// à la plus ancienne (contacts reçus triés date_heure ASC → inversés ; ordre demandé le 2026-10-13).
const journalDatesYYYYMMDD = (contacts) => [...(contacts || [])].reverse().map(c => contactDateYYYYMMDD(c.date_heure)).filter(Boolean).join(';');
// Libellé de la dernière action renseignée (colonne « Action » du formulaire, champ `resultat` en base
// — cf. RESULTATS_CONTACT, ex. Détruit / Chgt propriétaire), en repartant de l'entrée la plus récente.
const lastActionLabel = (contacts) => {
  if (!contacts || !contacts.length) return '';
  for (let i = contacts.length - 1; i >= 0; i--) {
    if (contacts[i].resultat) return RESULTATS_CONTACT.find(x => x.value === contacts[i].resultat)?.label || contacts[i].resultat;
  }
  return '';
};
// Compte de saisie Webapp : PAS un champ structuré — le formulaire terrain (webapp-detection)
// n'est pas authentifié par compte CRM (POST /api/takata/detection protégé par clé API, pas par
// utilisateur ; `takata_contacts.user_id` reste NULL pour ces entrées). Seul le nom saisi à l'écran
// de la webapp est conservé, en texte libre, dans la note « [Webapp] Détecté sur parking par XXX. » —
// même principe d'extraction que `webappCoordsFromNote`/`webappParseCoords` ci-dessous. Meilleur effort :
// si la webapp évolue et change ce texte, l'extraction ne trouve plus rien (colonne vide, pas d'erreur).
// Le compte peut contenir des points (« soguava.interim2 ») : on s'arrête au point SUIVI d'un espace ou
// de la fin (« par soguava.interim2. Airbag conducteur. »). Avant le 2026-10-13, l'arrêt au premier point
// réduisait soguava.interim et soguava.interim2 à « soguava ».
const WEBAPP_AGENT_RE = /^\[Webapp\] Détecté sur parking par (.+?)\.(?=\s|$)/;
const webappAgentLabel = (contacts) => {
  if (!contacts || !contacts.length) return '';
  for (let i = contacts.length - 1; i >= 0; i--) {
    const m = (contacts[i].notes || '').match(WEBAPP_AGENT_RE);
    if (m) return m[1].trim();
  }
  return '';
};

// Remarque Webapp (Absent / Adresse introuvable) de la détection webapp la plus récente du dossier —
// champ structuré `takata_contacts.remarque_webapp`. Vide si cette détection est à l'état neutre,
// ou si le dossier n'a aucune détection webapp.
const webappRemarqueLabel = (contacts) => {
  if (!contacts || !contacts.length) return '';
  for (let i = contacts.length - 1; i >= 0; i--) {
    if ((contacts[i].notes || '').startsWith('[Webapp]')) return contacts[i].remarque_webapp || '';
  }
  return '';
};

// Condense le journal d'un dossier en UNE cellule Excel lisible : un contact par
// ligne (saut de ligne \n), format « JJ/MM/AAAA HH:MM · Type · Résultat · (1er contact) — notes ».
// Les retours à la ligne internes des notes (ex. notes webapp) sont aplatis pour garder 1 ligne/contact.
const formatJournalCell = (contacts) => {
  if (!contacts || !contacts.length) return '';
  const typeLabel = (t) => TYPES_CONTACT.find(x => x.value === t)?.label || t || '';
  const resLabel = (r) => RESULTATS_CONTACT.find(x => x.value === r)?.label || r || '';
  return contacts.map(c => {
    const bits = [
      c.date_heure ? formatDateTime(c.date_heure) : '',
      typeLabel(c.type),
      resLabel(c.resultat),
    ].filter(Boolean);
    let line = bits.join(' · ');
    if (c.est_premier_contact) line += ' · (1er contact)';
    if (c.notes) line += ` — ${String(c.notes).replace(/\s*\n\s*/g, ' ').trim()}`;
    return line;
  }).join('\n');
};

function ExportChoiceModal({ dossiers, filters, courriersFilters, onClose }) {
  const [loading, setLoading] = useState('');

  const filteredDossiers = dossiers.filter(d => {
    const { filtreConstructeur, filtreStatut, filtreConcession, search } = filters;
    if (filtreConstructeur?.length && !filtreConstructeur.includes(d.marque)) return false;
    if (filtreStatut?.length && !filtreStatut.includes(d.statut)) return false;
    if (filtreConcession?.length && !filtreConcession.includes(String(d.concession_id))) return false;
    if (search && !matchesSearch(search, d.vin, d.immat, d.client_nom)) return false;
    return true;
  });

  const applyDateFormat = (ws, dateColIndexes, rowCount) => {
    dateColIndexes.forEach(ci => {
      const col = XLSX.utils.encode_col(ci);
      for (let r = 2; r <= rowCount + 1; r++) {
        const ref = `${col}${r}`;
        if (ws[ref] && ws[ref].v instanceof Date) { ws[ref].t = 'd'; ws[ref].z = 'dd/mm/yyyy'; }
      }
    });
  };

  // Export des détections terrain de l'onglet Webapp — la colonne « Coordonnées
  // identifiées » (pipe-séparée dans la note) est éclatée : une donnée = une colonne.
  const exportWebapp = async () => {
    setLoading('takata');
    try {
      const res = await takataApi.getWebappDetections({ export: '1' });
      const detections = res.data.detections || [];

      const HEADER = ['Date','Immat','Marque / Modèle','Civilité','Nom','Téléphone','Adresse','Code Postal','Ville','Remarque Webapp','Commentaire'];
      const rows = detections.map(det => {
        const c = webappParseCoords(webappCoordsFromNote(det.notes));
        return [
          det.date ? toXlsDate(det.date) : '',
          det.immat || '',
          [det.marque, det.modele].filter(Boolean).join(' ') || '',
          c.civilite,
          c.nom || det.client_nom || '',
          c.telephone,
          c.adresse,
          cpFromVille(c.ville),
          villeSansCP(c.ville),
          det.remarque_webapp || '',
          c.commentaire,
        ];
      });

      const ws = XLSX.utils.aoa_to_sheet([HEADER, ...rows]);
      // Formater la colonne date (index 0)
      for (let r = 2; r <= rows.length + 1; r++) {
        const ref = `A${r}`;
        if (ws[ref] && ws[ref].v instanceof Date) { ws[ref].t = 'd'; ws[ref].z = 'dd/mm/yyyy'; }
      }
      ws['!cols'] = [{ wch: 16 }, { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 26 }, { wch: 14 }, { wch: 30 }, { wch: 14 }, { wch: 20 }, { wch: 20 }, { wch: 50 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Webapp');
      XLSX.writeFile(wb, `takata_webapp_${new Date().toISOString().slice(0,10)}.xlsx`);
      onClose();
    } catch (err) {
      alert('Erreur export webapp : ' + err.message);
    } finally {
      setLoading('');
    }
  };

  const exportDossiers = async () => {
    setLoading('dossiers');
    try {
      let contactsMap = {};
      let rdvMap = {};
      let pretsMap = {};
      let principalMap = {};
      let designationMap = {};
      try {
        const [cRes, rRes, pRes, clRes] = await Promise.all([takataApi.getAllContacts(), takataApi.getAllRdv(), takataApi.getAllPrets(), takataApi.getExportClients()]);
        contactsMap = cRes.data.contacts || {};
        rdvMap = rRes.data.rdv || {};
        pretsMap = pRes.data.prets || {};
        // Même sélection que l'export Clients (GET /export/clients) : principal en
        // priorité, sinon la fiche la plus récente — réutilisée ici pour la colonne
        // « Client principal », sans dupliquer la logique de choix du client.
        // Case vide (ni Oui ni Non) si le dossier n'a aucune fiche client du tout.
        // `client_created_at` (et non le nom) : sans fiche, l'endpoint renvoie le nom legacy du dossier.
        (clRes.data.dossiers || []).forEach(cd => {
          principalMap[cd.id] = cd.client_created_at ? (cd.is_principal ? 'Oui' : 'Non') : '';
          designationMap[cd.id] = cd.date_designation_principal || null;
        });
      } catch { /* best effort */ }

      const typeLabel = (t) => ({ appel_sortant:'Appel sortant', appel_entrant:'Appel entrant', sms:'SMS', email:'E-mail', visite:'Visite', no_show:'No Show', autres:'Autres', courrier:'Courrier' })[t] || t || '';
      const resultatLabel = (r) => RESULTATS_CONTACT.find(x => x.value === r)?.label || r || '';
      // Lit la constante canonique STATUTS plutôt qu'une table locale : celle qui
      // vivait ici était incomplète (6 valeurs sur 13) et exportait le slug brut
      // pour tout le reste — « piece_en_attente » au lieu de « Pièce(s) en attente ».
      // Libellé canonique de STATUTS (dont « Contacté »), même règle que le tableau.
      const statutLabel = (s) => (s ? statutInfo(s).label : '');
      const pretStatut = (s) => ({ propose:'Proposé', refuse:'Refusé', en_cours:'En cours' })[s] || s || '';
      const reclStatut = (s) => ({ en_cours:'En cours', termine:'Terminé' })[s] || s || '';
      const triBool = (v) => v === true ? 'Oui' : (v === false ? 'Non' : '');
      const oui = (v) => v ? 'Oui' : '';
      // Lit la constante canonique STATUTS_RDV : les tables locales ignoraient
      // « Reporté » et auraient exporté le slug brut.
      const rdvStatutLabel = (s) => STATUTS_RDV.find(x => x.value === s)?.label || s || '';

      // ── Feuille 1 : Dossiers (repasse globale 2026-07-08 : tous les champs de l'onglet Infos) ──
      const HDR_DOS = [
        'VIN','Immat','Marque','Modèle','Statut','Statut Campagne','Date enr.','Client','Téléphone','E-mail','Adresse','Code Postal','Ville','Origine info',
        'Location','Date de production','Type Approval Number','Date 1st Letter','Date Last Letter','Stop Drive','Demande info seulement',
        'Date premier contact','Type RDV',
        'Date proposition du 1er RDV',
        '1ère date RDV proposé','Statut 1er RDV',
        '2e date RDV proposé','Statut 2e RDV',
        '3e date RDV proposé','Statut 3e RDV',
        '4e date RDV proposé','Statut 4e RDV',
        '5e date RDV proposé','Statut 5e RDV',
        'Statut RDV',
        'Airbag Driv Concerné','Recall Code Conducteur','Airbag Driv Remplacé','Date Remplacement Driv','Airbag Driv Inspecté',
        'Airbag Pass Concerné','Recall Code Passager','Airbag Pass Remplacé','Date Remplacement Pass','Airbag Pass Inspecté',
        'Statut véh. prêt','Prêt VH — Décision','Prêt VH — Date début','Prêt VH — Date fin',
        'Statut réclamation','Concession','Assigné à',
        'Date dernier contact','Dates journal (AAAAMMJJ)','Dernière action','Compte de saisie Webapp','Remarque Webapp','Client principal','Date désignation client principal',
        'Journal',
      ];
      const rowsDos = filteredDossiers.map(d => {
        const rdvs = rdvMap[d.id] || [];  // déjà triés date_heure ASC par le backend
        const contacts = contactsMap[d.id] || [];
        return [
        d.vin||'', d.immat||'', d.marque||'', d.modele||'',
        statutLabel(d.statut),
        getStatutCampagneLabel(d),
        toXlsDate(d.date_premier_contact),
        [d.client_prenom, d.client_nom].filter(Boolean).join(' ') || '',
        d.client_telephone||'', d.client_email||'', d.client_adresse||'', cpFromVille(d.ville), villeSansCP(d.ville),
        d.source_info||'',
        d.location||'',
        toXlsDate(d.date_production),
        d.type_approval_number||'',
        toXlsDate(d.date_first_letter),
        toXlsDate(d.date_last_letter),
        oui(d.stop_drive),
        d.demande_info_seulement ? 'VRAI' : 'FAUX',
        (() => { const pc = d.date_premier_contact || (contactsMap[d.id]||[]).find(c => c.est_premier_contact)?.date_heure; return toXlsDate(pc); })(),
        d.type_rdv||'',
        toXlsDate(rdvs[0]?.created_at),
        toXlsDate(rdvs[0]?.date_heure), rdvStatutLabel(rdvs[0]?.statut),
        toXlsDate(rdvs[1]?.date_heure), rdvStatutLabel(rdvs[1]?.statut),
        toXlsDate(rdvs[2]?.date_heure), rdvStatutLabel(rdvs[2]?.statut),
        toXlsDate(rdvs[3]?.date_heure), rdvStatutLabel(rdvs[3]?.statut),
        toXlsDate(rdvs[4]?.date_heure), rdvStatutLabel(rdvs[4]?.statut),
        d.statut_rdv||'',
        triBool(d.driv_airbag_concerned),
        d.recall_code_driv||'',
        triBool(d.driv_airbag_replaced),
        toXlsDate(d.replacement_date_driv),
        oui(d.driv_airbag_inspected),
        triBool(d.pass_airbag_concerned),
        d.recall_code_pass||'',
        triBool(d.passenger_airbag_replaced),
        toXlsDate(d.replacement_date_pass),
        oui(d.pass_airbag_inspected),
        pretStatut(d.veh_pret_statut),
        ...(() => { const p = (pretsMap[d.id] || [])[0]; return [p ? (p.propose ? 'Accepté' : 'Refusé') : '', toXlsDate(p?.date_debut), toXlsDate(p?.date_fin)]; })(),
        reclStatut(d.reclamation_statut),
        d.concession_nom||'',
        [d.assigne_prenom, d.assigne_nom].filter(Boolean).join(' ') || '',
        toXlsDate(lastContactDate(contacts)),
        journalDatesYYYYMMDD(contacts),
        lastActionLabel(contacts),
        webappAgentLabel(contacts),
        webappRemarqueLabel(contacts),
        principalMap[d.id] ?? '',
        toXlsDate(designationMap[d.id]),
        formatJournalCell(contacts),
      ];
      });
      const wsDos = XLSX.utils.aoa_to_sheet([HDR_DOS, ...rowsDos]);
      // Colonnes dates (0-based) : Date enr., Date production, 1st/Last letter, Date 1er contact, Date propo/RDV, Date rempl. Driv/Pass, Prêt VH dates, Date dernier contact
      [6, 15, 17, 18, 21, 23, 24, 26, 28, 30, 32, 38, 43, 47, 48, 52, HDR_DOS.indexOf('Date désignation client principal')].forEach(ci => {
        const col = XLSX.utils.encode_col(ci);
        for (let r = 2; r <= rowsDos.length + 1; r++) {
          const ref = `${col}${r}`;
          if (wsDos[ref] && wsDos[ref].v instanceof Date) { wsDos[ref].t = 'd'; wsDos[ref].z = 'dd/mm/yyyy'; }
        }
      });
      // Renvoi à la ligne sur la colonne Journal (dernière) — best effort (xlsx CE ignore les styles à l'écriture, mais les \n restent lisibles).
      const jnlColDos = XLSX.utils.encode_col(HDR_DOS.length - 1);
      for (let r = 2; r <= rowsDos.length + 1; r++) {
        const ref = `${jnlColDos}${r}`;
        if (wsDos[ref]) wsDos[ref].s = { alignment: { wrapText: true, vertical: 'top' } };
      }
      wsDos['!cols'] = HDR_DOS.map((h, i) => {
        if (i === HDR_DOS.length - 1) return { wch: 90 };        // Journal
        if (['VIN'].includes(h)) return { wch: 20 };
        if (['E-mail','Adresse','Client','Type Approval Number','Dates journal (AAAAMMJJ)','Compte de saisie Webapp'].includes(h)) return { wch: 24 };
        return { wch: 15 };
      });

      // ── Feuille 2 : Journal ───────────────────────────────────────────────
      const HDR_JNL = ['VIN','Immat','Marque','Date / heure','Type','Résultat','Notes','1er contact'];
      const rowsJnl = [];
      for (const d of filteredDossiers) {
        for (const c of (contactsMap[d.id] || [])) {
          rowsJnl.push([
            d.vin||'', d.immat||'', d.marque||'',
            toXlsDate(c.date_heure),
            typeLabel(c.type), resultatLabel(c.resultat),
            c.notes||'',
            c.est_premier_contact ? 'Oui' : '',
          ]);
        }
      }
      const wsJnl = XLSX.utils.aoa_to_sheet([HDR_JNL, ...rowsJnl]);
      for (let r = 2; r <= rowsJnl.length + 1; r++) {
        const ref = `D${r}`;
        if (wsJnl[ref] && wsJnl[ref].v instanceof Date) { wsJnl[ref].t = 'd'; wsJnl[ref].z = 'dd/mm/yyyy'; }
      }
      wsJnl['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 60 }, { wch: 12 }];

      // ── Feuille 3 : RDV ──────────────────────────────────────────────────
      const HDR_RDV = ['VIN','Immat','Marque','Date RDV','Type RDV','Statut','Notes'];
      const rowsRdv = [];
      for (const d of filteredDossiers) {
        for (const r of (rdvMap[d.id] || [])) {
          rowsRdv.push([
            d.vin||'', d.immat||'', d.marque||'',
            toXlsDate(r.date_heure),
            r.type||'', r.statut||'', r.notes||'',
          ]);
        }
      }
      const wsRdv = XLSX.utils.aoa_to_sheet([HDR_RDV, ...rowsRdv]);
      for (let r = 2; r <= rowsRdv.length + 1; r++) {
        const ref = `D${r}`;
        if (wsRdv[ref] && wsRdv[ref].v instanceof Date) { wsRdv[ref].t = 'd'; wsRdv[ref].z = 'dd/mm/yyyy'; }
      }
      wsRdv['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 40 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsDos, 'Dossiers');
      XLSX.utils.book_append_sheet(wb, wsJnl, 'Journal');
      XLSX.utils.book_append_sheet(wb, wsRdv, 'RDV');
      XLSX.writeFile(wb, `takata_dossiers_complet_${new Date().toISOString().slice(0,10)}.xlsx`);
      onClose();
    } catch (err) {
      alert('Erreur export dossiers : ' + err.message);
    } finally {
      setLoading('');
    }
  };

  const exportReporting = async () => {
    setLoading('reporting');
    try {
      let rdvMap = {};
      let contactsMap = {};
      let pretsMap = {};
      let principalMap = {};
      let designationMap = {};
      try {
        const [rdvRes, contactsRes, pretRes, clRes] = await Promise.all([takataApi.getAllRdv(), takataApi.getAllContacts(), takataApi.getAllPrets(), takataApi.getExportClients()]);
        rdvMap = rdvRes.data.rdv || {};
        contactsMap = contactsRes.data.contacts || {};
        pretsMap = pretRes.data.prets || {};
        // Même sélection que l'export Clients (GET /export/clients) : principal en
        // priorité, sinon la fiche la plus récente. Case vide si le dossier n'a
        // aucune fiche client du tout (ni Oui ni Non).
        // `client_created_at` (et non le nom) : sans fiche, l'endpoint renvoie le nom legacy du dossier.
        (clRes.data.dossiers || []).forEach(cd => {
          principalMap[cd.id] = cd.client_created_at ? (cd.is_principal ? 'Oui' : 'Non') : '';
          designationMap[cd.id] = cd.date_designation_principal || null;
        });
      } catch { /* best effort */ }

      const getLastRdvStatut = (id) => {
        const rdvs = rdvMap[id];
        if (!rdvs?.length) return '';
        for (let i = rdvs.length - 1; i >= 0; i--) {
          if (rdvs[i].statut === 'honore') return 'Honoré';
          if (rdvs[i].statut === 'no_show') return 'No Show';
        }
        return '';
      };

      const rdvTypeLabel = (t) => ({ atelier: 'Atelier', domicile: 'Domicile' })[t] || t || '';
      // Lit la constante canonique STATUTS_RDV : les tables locales ignoraient
      // « Reporté » et auraient exporté le slug brut.
      const rdvStatutLabel = (s) => STATUTS_RDV.find(x => x.value === s)?.label || s || '';
      const remStatut = (s) => ({ demande: 'Demandé', traite: 'Traité' })[s] || s || '';
      const pretStatut = (s) => ({ propose: 'Proposé', refuse: 'Refusé', en_cours: 'En cours' })[s] || s || '';
      const reclStatut = (s) => ({ en_cours: 'En cours', termine: 'Terminé' })[s] || s || '';

      const HEADER = [
        'VIN','Immatriculation','Marque','Modèle','Statut','Statut Campagne',
        'Date enregistrement','Client','Téléphone','E-mail','Adresse','Code Postal','Ville',
        'Origine de l\'info','Location','Date premier contact',
        'Date appel & proposition RDV','Date RDV proposé','Type RDV',
        '1ère date RDV proposé','Statut 1er RDV',
        '2e date RDV proposé','Statut 2e RDV',
        '3e date RDV proposé','Statut 3e RDV',
        '4e date RDV proposé','Statut 4e RDV',
        '5e date RDV proposé','Statut 5e RDV',
        'Statut remorquage','Statut RDV',
        'Airbag Conducteur Concerné','Airbag Conducteur Remplacé','Date Remplacement Driv',
        'Airbag Passager Concerné','Airbag Passager Remplacé','Date Remplacement Pass',
        'Délai réparation (j)',
        'Prêt VH — Décision','Prêt VH — Date début','Prêt VH — Date fin',
        'Statut réclamation',
        'Date proposition mobilité','Date mise en œuvre mobilité','Délai MAD véh. (j)',
        'Date réclamation client','Motif réclamation','Détail réclamation',
        'Solution proposée','Date prév. résolution','Avancement réclamation','Date eff. résolution',
        'Journal',
        'Date de production','Type Approval Number',
        'Recall code conducteur','Recall code passager',
        'Date 1st letter','Date last letter',
        'Date dernier contact','Dates journal (AAAAMMJJ)','Dernière action','Compte de saisie Webapp','Client principal','Date désignation client principal',
      ];

      // Colonnes dates (index 0-based dans le tableau de valeurs)
      // Col 38 "Statut véh. de prêt" supprimée → indices ≥39 décalés de -1. Nouvelles dates : 53 (prod), 57 (1st letter), 58 (last letter), 59 (dernier contact).
      const DATE_COL_IDX = [6, 15, 16, 17, 19, 21, 23, 25, 27, 33, 36, 39, 40, 42, 43, 45, 49, 51, 53, 57, 58, 59];

      const rows = filteredDossiers.map(d => {
        const rdvs = rdvMap[d.id] || [];  // triés date_heure ASC par le backend (même source que export Dossiers)
        const contacts = contactsMap[d.id] || [];
        const delaiRep = (() => {
          const rep = d.replacement_date_driv || d.replacement_date_pass;
          const j = diffJours(d.date_premier_contact, rep);
          return j != null && j >= 0 ? j : '';
        })();
        const delaiMAD = (() => {
          const p = (pretsMap[d.id] || [])[0];
          const j = diffJours(p?.created_at, p?.date_debut);
          return j != null && j >= 0 ? j : '';
        })();
        return [
          d.vin||'', d.immat||'', d.marque||'', d.modele||'',
          // Table legacy en priorité ; le reste (dont non_contacte, absent de la table
          // et jusqu'ici exporté en slug brut) passe par statutInfo (libellés STATUTS, dont « Contacté »).
          STATUTS_LABEL[d.statut] || (d.statut ? statutInfo(d.statut).label : ''),
          getStatutCampagneLabel(d),
          toExcelDateVal(d.date_premier_contact),
          ...(() => { const cl = clientDuReporting(d); return [[cl.nom, cl.prenom].filter(Boolean).join(' '), cl.telephone, cl.email, cl.adresse]; })(),
          villeDuPrincipal(d) ? cpDuPrincipal(d) : cpFromVille(d.ville), villeDuPrincipal(d) || villeSansCP(d.ville),
          d.source_info||'',
          d.location||'',
          toExcelDateVal(d.date_premier_contact || (contactsMap[d.id]||[]).find(c => c.est_premier_contact)?.date_heure),
          toExcelDateVal(d.date_premier_contact),
          toExcelDateVal(d.prochain_rdv),
          rdvTypeLabel(d.type_rdv),
          toExcelDateVal(rdvs[0]?.date_heure), rdvStatutLabel(rdvs[0]?.statut),
          toExcelDateVal(rdvs[1]?.date_heure), rdvStatutLabel(rdvs[1]?.statut),
          toExcelDateVal(rdvs[2]?.date_heure), rdvStatutLabel(rdvs[2]?.statut),
          toExcelDateVal(rdvs[3]?.date_heure), rdvStatutLabel(rdvs[3]?.statut),
          toExcelDateVal(rdvs[4]?.date_heure), rdvStatutLabel(rdvs[4]?.statut),
          remStatut(d.remorquage_statut),
          getLastRdvStatut(d.id),
          d.driv_airbag_concerned ? 'Oui' : '',
          d.driv_airbag_replaced ? 'Oui' : '',
          toExcelDateVal(d.replacement_date_driv),
          d.pass_airbag_concerned ? 'Oui' : '',
          d.passenger_airbag_replaced ? 'Oui' : '',
          toExcelDateVal(d.replacement_date_pass),
          delaiRep,
          ...(() => { const p = (pretsMap[d.id] || [])[0]; return [p ? (p.propose ? 'Accepté' : 'Refusé') : '', toExcelDateVal(p?.date_debut), toExcelDateVal(p?.date_fin)]; })(),
          reclStatut(d.reclamation_statut),
          toExcelDateVal((pretsMap[d.id] || [])[0]?.created_at),
          toExcelDateVal((pretsMap[d.id] || [])[0]?.date_debut),
          delaiMAD,
          toExcelDateVal(d.date_reclamation),
          d.motif_reclamation||'', d.detail_reclamation||'', d.solution_proposee||'',
          toExcelDateVal(d.date_previsionnelle_resolution),
          d.avancement_reclamation||'',
          toExcelDateVal(d.date_effective_resolution),
          formatJournalCell(contactsMap[d.id]),
          toExcelDateVal(d.date_production),
          d.type_approval_number || '',
          d.recall_code_driv || '',
          d.recall_code_pass || '',
          toExcelDateVal(d.date_first_letter),
          toExcelDateVal(d.date_last_letter),
          toExcelDateVal(lastContactDate(contacts)),
          journalDatesYYYYMMDD(contacts),
          lastActionLabel(contacts),
          webappAgentLabel(contacts),
          principalMap[d.id] ?? '',
          toExcelDateVal(designationMap[d.id]),
        ];
      });

      const ws = XLSX.utils.aoa_to_sheet([HEADER, ...rows]);
      applyDateFormat(ws, [...DATE_COL_IDX, HEADER.indexOf('Date désignation client principal')], rows.length);
      ws['!cols'] = Array(HEADER.length).fill({ wch: 18 });
      ws['!cols'][0] = { wch: 20 }; // VIN
      const JNL_IDX = HEADER.indexOf('Journal');
      ws['!cols'][JNL_IDX] = { wch: 90 }; // Journal (colonne large)
      ws['!cols'][HEADER.indexOf('Dates journal (AAAAMMJJ)')] = { wch: 24 };
      ws['!cols'][HEADER.indexOf('Compte de saisie Webapp')] = { wch: 24 };
      // Renvoi à la ligne sur la colonne Journal — best effort (xlsx CE ignore les styles, \n restent lisibles).
      const jnlColRep = XLSX.utils.encode_col(JNL_IDX);
      for (let r = 2; r <= rows.length + 1; r++) {
        const ref = `${jnlColRep}${r}`;
        if (ws[ref]) ws[ref].s = { alignment: { wrapText: true, vertical: 'top' } };
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reporting constructeur');

      // ── Onglet Journal ──
      const typeLabel = (t) => TYPES_CONTACT.find(x => x.value === t)?.label || t || '';
      const resultatLabel = (r) => RESULTATS_CONTACT.find(x => x.value === r)?.label || r || '';
      const JOURNAL_HEADER = ['VIN', 'Immatriculation', 'Marque', 'Date / heure', 'Type', 'Résultat', 'Notes', '1er contact'];
      const journalRows = [];
      for (const d of filteredDossiers) {
        const events = contactsMap[d.id] || [];
        for (const c of events) {
          journalRows.push([
            d.vin || '', d.immat || '', d.marque || '',
            toXlsDate(c.date_heure),
            typeLabel(c.type),
            resultatLabel(c.resultat),
            c.notes || '',
            c.est_premier_contact ? 'Oui' : '',
          ]);
        }
      }
      const wsJournal = XLSX.utils.aoa_to_sheet([JOURNAL_HEADER, ...journalRows]);
      applyDateFormat(wsJournal, [3], journalRows.length); // colonne Date/heure
      wsJournal['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 50 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsJournal, 'Journal');

      XLSX.writeFile(wb, `takata_reporting_${new Date().toISOString().slice(0,10)}.xlsx`);
      onClose();
    } catch (err) {
      alert('Erreur export reporting : ' + err.message);
    } finally {
      setLoading('');
    }
  };

  const exportClients = async () => {
    setLoading('clients');
    try {
      // Migration silencieuse : crée les clients principaux manquants depuis les courriers
      try { await takataApi.migrateClientsFromCourriers(); } catch { /* best effort */ }

      const res = await takataApi.getExportClients();
      const dossiers = res.data.dossiers || [];
      if (!dossiers.length) { alert('Aucun dossier à exporter.'); return; }

      // Adresse en 3 colonnes, comme le fichier La Poste (découpage fait côté serveur,
      // cf. GET /export/clients).
      const header = [
        'IMMAT', 'VIN', 'MARQUE', 'STATUT', 'STATUT CAMPAGNE', 'CONCESSION', 'NOM', 'PRÉNOM',
        'RÉSIDENCE, BÂTIMENT', 'ADRESSE', 'LIEU-DIT, BP', 'CP', 'VILLE', 'CLIENT PRINCIPAL',
        'DATE CRÉATION FICHE CLIENT', 'DATE DÉSIGNATION PROPRIÉTAIRE ACTUEL', 'STATUT LA POSTE',
      ];
      const rows = dossiers.map(d => [
        d.immat || '',
        d.vin || '',
        d.marque || '',
        d.statut ? statutInfo(d.statut).label : '',
        getStatutCampagneLabel(d),
        d.concession_nom || '',
        d.nom || '',
        d.prenom || '',
        d.residence || '',
        d.voie || '',
        d.lieu_dit || '',
        d.code_postal || '',
        d.ville || '',
        d.is_principal ? 'OUI' : 'NON',
        toXlsDate(d.client_created_at),
        toXlsDate(d.date_designation_principal),
        d.statut_laposte || '',
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      applyDateFormat(ws, [14, 15], rows.length);
      ws['!cols'] = [12, 20, 14, 14, 16, 18, 24, 18, 24, 30, 18, 8, 20, 16, 14, 18, 20].map(wch => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clients');
      XLSX.writeFile(wb, `takata_clients_${new Date().toISOString().slice(0, 10)}.xlsx`);
      onClose();
    } catch (err) {
      alert('Erreur export clients : ' + err.message);
    } finally {
      setLoading('');
    }
  };

  const exportCourriers = async () => {
    setLoading('courriers');
    try {
      // Dates de la fiche client du dossier (même client que l'export « Clients » :
      // principal, sinon le plus récent), rapprochées par dossier.
      const [res, resClients] = await Promise.all([
        takataApi.getAllCourriers(courriersFilters || {}),
        takataApi.getExportClients(),
      ]);
      const courriers = res.data.courriers || [];
      if (!courriers.length) { alert('Aucun courrier à exporter.'); return; }
      const clientParDossier = new Map((resClients.data.dossiers || []).map(d => [d.id, d]));
      const header = [
        'VIN', 'Immat', 'Marque', 'Modèle', 'Destinataire',
        'Résidence, bâtiment', 'Adresse', 'Lieu-dit, BP', 'CP', 'Ville',
        "Mode d'envoi", 'Type', 'N° recommandé', 'Date envoi',
        'Statut La Poste', 'Date distribution', 'Date non-distribution', 'Cause non-distribution',
        'Statut retour', 'Lot', 'Concession',
        'Date création fiche client', 'Date désignation propriétaire actuel',
      ];
      // Courriers importés avant le découpage (2026-10-13) et jamais réimportés :
      // seule l'adresse recomposée est connue → colonne « Adresse ».
      const decoupee = (c) => c.destinataire_residence || c.destinataire_voie || c.destinataire_lieu_dit;
      const rows = courriers.map(c => [
        c.vin || '', c.immat || '', c.marque || '', c.modele || '',
        c.destinataire_nom || [c.client_nom, c.client_prenom].filter(Boolean).join(' ') || '',
        decoupee(c) ? (c.destinataire_residence || '') : '',
        decoupee(c) ? (c.destinataire_voie || '') : (c.destinataire_adresse || ''),
        decoupee(c) ? (c.destinataire_lieu_dit || '') : '',
        cpFromVille(c.destinataire_cp_ville),
        villeSansCP(c.destinataire_cp_ville),
        c.mode_envoi === 'recommande' ? 'Recommandé' : c.mode_envoi === 'simple' ? 'Simple' : '',
        c.type === '1er_envoi' ? '1er envoi' : c.type === 'relance' ? 'Relance' : c.type || '',
        c.num_recommande || '',
        c.date_envoi ? toXlsDate(c.date_envoi) : '',
        c.statut_laposte || '',
        c.date_distribution ? toXlsDate(c.date_distribution) : '',
        c.date_non_distribution ? toXlsDate(c.date_non_distribution) : '',
        c.cause_non_distribution || '',
        (STATUTS_RETOUR.find(r => r.value === c.statut_retour) || { label: '' }).label || '',
        c.nom_envoi || '', c.concession_nom || '',
        toXlsDate(clientParDossier.get(c.dossier_id)?.client_created_at),
        toXlsDate(clientParDossier.get(c.dossier_id)?.date_designation_principal),
      ]);
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      applyDateFormat(ws, [13, 15, 16, 21, 22], rows.length);
      ws['!cols'] = [18, 12, 12, 16, 28, 24, 30, 18, 8, 22, 12, 10, 16, 12, 20, 14, 18, 24, 18, 28, 20, 14, 18].map(wch => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Courriers');
      XLSX.writeFile(wb, `takata_courriers_${new Date().toISOString().slice(0, 10)}.xlsx`);
      onClose();
    } catch (err) {
      alert('Erreur export courriers : ' + err.message);
    } finally {
      setLoading('');
    }
  };

  const nbDossiers = filteredDossiers.length;
  const filtersBadge = nbDossiers !== dossiers.length ? ` (${nbDossiers} / ${dossiers.length} dossiers)` : ` (${dossiers.length} dossiers)`;

  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📥 Exporter</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 4 }}>
            Choisissez le type d'export{filtersBadge} :
          </p>
          <button className="takata-export-choice-btn" onClick={exportWebapp} disabled={!!loading}>
            <span className="takata-export-choice-icon">
              {loading === 'takata' ? <span className="takata-spin" /> : '📋'}
            </span>
            <div>
              <div className="takata-export-choice-title">Webapp</div>
              <div className="takata-export-choice-sub">Détections terrain — Date · Immat · Marque / Modèle · Civilité · Nom · Téléphone · Adresse · Ville · Commentaire</div>
            </div>
          </button>
          <button className="takata-export-choice-btn" onClick={exportReporting} disabled={!!loading}>
            <span className="takata-export-choice-icon">
              {loading === 'reporting' ? <span className="takata-spin" /> : '📤'}
            </span>
            <div>
              <div className="takata-export-choice-title">Reporting constructeur</div>
              <div className="takata-export-choice-sub">VIN · Statuts · Airbags · Mobilité · Réclamation · Délais…</div>
            </div>
          </button>
          <button className="takata-export-choice-btn" onClick={exportDossiers} disabled={!!loading}>
            <span className="takata-export-choice-icon">
              {loading === 'dossiers' ? <span className="takata-spin" /> : '📂'}
            </span>
            <div>
              <div className="takata-export-choice-title">Dossiers complet</div>
              <div className="takata-export-choice-sub">Toutes données dossiers · Journal complet · RDV — 3 feuilles Excel</div>
            </div>
          </button>
          <button className="takata-export-choice-btn" onClick={exportCourriers} disabled={!!loading}>
            <span className="takata-export-choice-icon">
              {loading === 'courriers' ? <span className="takata-spin" /> : '📬'}
            </span>
            <div>
              <div className="takata-export-choice-title">Courriers</div>
              <div className="takata-export-choice-sub">Courriers filtrés · VIN · Destinataire · Adresse (3 colonnes) · CP · Ville · Mode · Dates · Statut retour · Dates fiche client</div>
            </div>
          </button>
          <button className="takata-export-choice-btn" onClick={exportClients} disabled={!!loading}>
            <span className="takata-export-choice-icon">
              {loading === 'clients' ? <span className="takata-spin" /> : '👤'}
            </span>
            <div>
              <div className="takata-export-choice-title">Clients</div>
              <div className="takata-export-choice-sub">Immat · VIN · Marque · Statut · Statut campagne · Concession · Nom · Prénom · Adresse (3 colonnes) · Dates fiche / propriétaire actuel · Statut La Poste — client principal, sinon le plus récent ; fiches créées depuis le dernier courrier</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Export Excel ──────────────────────────────────────────────────────

function ExportModal({ columns, dossiers, facturesMap, allContactsMap, getStatutCampagne, filters, onClose }) {
  const [selected, setSelected] = useState(() => columns.map(c => c.id));
  const [dragIdx, setDragIdx] = useState(null);
  const [orderedCols, setOrderedCols] = useState(() => [...columns]);

  const toggleCol = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelected(orderedCols.map(c => c.id));
  const selectNone = () => setSelected([]);

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setOrderedCols(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const getFilteredDossiers = () => {
    return dossiers.filter(d => {
      if (filters.search && !matchesSearch(filters.search, d.vin, d.immat, d.client_nom, d.client_prenom, d.client_telephone)) return false;
      if (filters.filtreConstructeur.length && !filters.filtreConstructeur.includes(d.constructeur) && !filters.filtreConstructeur.includes(d.marque)) return false;
      if (filters.filtreStatut.length && !filters.filtreStatut.includes(d.statut)) return false;
      if (filters.filtreConcession.length && !filters.filtreConcession.includes(String(d.concession_id))) return false;
      if (filters.filtreFacture && !facturesMap[d.vin]) return false;
      if (filters.filtreLocation.length && !filters.filtreLocation.includes(d.location)) return false;
      if (filters.filtreCampagne.length && !filters.filtreCampagne.includes(getStatutCampagne(d).label)) return false;
      return true;
    });
  };

  // Colonnes à exporter en tant que vraies dates Excel (format court dd/mm/yyyy)
  const DATE_COLS = new Set([
    'date_first_letter', 'date_last_letter', 'date_production',
    'replacement_date_driv', 'replacement_date_pass',
    'dernier_contact', 'prochain_rdv', 'dernier_rdv', 'facture_date',
    'created_at', 'updated_at',
  ]);

  // Convertit une valeur (ISO, "YYYY-MM-DD" ou timestamp) en Date **minuit local** pour XLSX
  // (cf. commentaire toXlsDate — la lib xlsx sérialise via l'heure locale, UTC → J-1 en UTC-4).
  const toExcelDate = (v) => {
    if (!v) return null;
    const s = String(v);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      if (s.length <= 10) { const dt = new Date(+m[1], +m[2] - 1, +m[3]); return isNaN(dt) ? null : dt; }
      const d = new Date(s);
      if (isNaN(d)) return new Date(+m[1], +m[2] - 1, +m[3]);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return null;
  };

  const formatValue = (col, d) => {
    const facture = facturesMap[d.vin];
    switch (col) {
      case 'statut_campagne': return getStatutCampagne(d).label;
      case 'assigne_nom_complet': return [d.assigne_prenom, d.assigne_nom].filter(Boolean).join(' ') || '';
      case 'driv_airbag_concerned': case 'pass_airbag_concerned':
      case 'stop_drive': case 'demande_info_seulement': case 'accident_signale':
        return d[col] ? 'Oui' : 'Non';
      case 'driv_airbag_replaced':
        if (!d.driv_airbag_concerned) return 'N/A';
        if (d.driv_airbag_inspected) return 'INS';
        return d.driv_airbag_replaced ? 'Oui' : 'Non';
      case 'passenger_airbag_replaced':
        if (!d.pass_airbag_concerned) return 'N/A';
        if (d.pass_airbag_inspected) return 'INS';
        return d.passenger_airbag_replaced ? 'Oui' : 'Non';
      case 'date_first_letter': case 'date_last_letter': case 'date_production':
      case 'replacement_date_driv': case 'replacement_date_pass':
      case 'dernier_contact': case 'prochain_rdv': case 'dernier_rdv':
      case 'created_at': case 'updated_at':
        return toExcelDate(d[col]);
      case 'facture_date': return toExcelDate(facture?.date_facture);
      case 'facture_num': return facture?.num_facture || '';
      case 'facture_montant': return facture?.montant_ht != null ? Number(facture.montant_ht) : '';
      default: return d[col] ?? '';
    }
  };

  const doExport = () => {
    const exportCols = orderedCols.filter(c => selected.includes(c.id));
    const filteredData = getFilteredDossiers();
    const header = exportCols.map(c => c.label);
    const rows = filteredData.map(d => exportCols.map(c => {
      const v = formatValue(c.id, d);
      // Les Date null doivent devenir des cellules vides (sinon xlsx écrit la string "null")
      return v == null ? '' : v;
    }));
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows], { cellDates: true, dateNF: 'dd/mm/yyyy' });
    ws['!cols'] = exportCols.map(() => ({ wch: 20 }));

    // Force le format date courte sur chaque cellule des colonnes date (sécurité,
    // car `dateNF` global peut être écrasé par le format implicite Excel)
    const dateColIndexes = exportCols
      .map((c, i) => DATE_COLS.has(c.id) ? i : -1)
      .filter(i => i >= 0);
    if (dateColIndexes.length) {
      const lastRow = filteredData.length + 1; // header + data
      for (const colIdx of dateColIndexes) {
        for (let r = 1; r < lastRow; r++) {
          const addr = XLSX.utils.encode_cell({ r, c: colIdx });
          const cell = ws[addr];
          if (cell && cell.v instanceof Date) {
            cell.t = 'd';
            cell.z = 'dd/mm/yyyy';
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dossiers');

    // Feuille Journal — une ligne par entrée de contact
    const fmtDateHeure = (v) => {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d)) return String(v);
      return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };
    const journalHeader = ['VIN', 'Immatriculation', 'Constructeur', 'Date/Heure', 'Type', 'Résultat', 'Agent', 'Premier contact', 'Notes'];
    const journalRows = [];
    for (const d of filteredData) {
      const contacts = allContactsMap[d.id] || [];
      for (const c of contacts) {
        journalRows.push([
          d.vin || '',
          d.immat || '',
          d.marque || '',
          fmtDateHeure(c.date_heure),
          c.type || '',
          c.resultat || '',
          c.agent || '',
          c.est_premier_contact ? 'Oui' : '',
          c.notes || '',
        ]);
      }
    }
    const wsJournal = XLSX.utils.aoa_to_sheet([journalHeader, ...journalRows]);
    wsJournal['!cols'] = journalHeader.map((h, i) => ({ wch: i === 8 ? 60 : 22 }));
    XLSX.utils.book_append_sheet(wb, wsJournal, 'Journal');

    XLSX.writeFile(wb, `takata_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    onClose();
  };

  const hasFilters = filters.search || filters.filtreConstructeur.length || filters.filtreStatut.length || filters.filtreConcession.length || filters.filtreLocation.length || filters.filtreFacture || filters.filtreCampagne.length;

  return (
    <div className="takata-export-dropdown" onClick={e => e.stopPropagation()}>
      <div className="takata-export-dropdown-header">
        <span className="takata-export-dropdown-title">📥 Export Excel</span>
        <button className="takata-export-dropdown-close" onClick={onClose}>✕</button>
      </div>
      <div className="takata-export-info">
        {hasFilters
          ? <span className="takata-export-filter-badge">🔍 Filtres actifs — {getFilteredDossiers().length} dossiers</span>
          : <span>{dossiers.length} dossiers</span>}
      </div>
      <div className="takata-export-actions">
        <button className="btn-secondary btn-sm" onClick={selectAll}>Tout cocher</button>
        <button className="btn-secondary btn-sm" onClick={selectNone}>Tout décocher</button>
      </div>
      <div className="takata-export-cols">
        {orderedCols.map((col, idx) => (
          <div
            key={col.id}
            className={`takata-export-col-item${selected.includes(col.id) ? ' selected' : ''}${dragIdx === idx ? ' dragging' : ''}`}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
          >
            <span className="takata-export-drag-handle">⠿</span>
            <input type="checkbox" checked={selected.includes(col.id)} onChange={() => toggleCol(col.id)} />
            <span className="takata-export-col-label">{col.label}</span>
          </div>
        ))}
      </div>
      <div className="takata-export-footer">
        <button className="btn-secondary btn-sm" onClick={onClose}>Annuler</button>
        <button className="btn-primary btn-sm" disabled={selected.length === 0} onClick={doExport}>
          📥 Exporter ({selected.length})
        </button>
      </div>
    </div>
  );
}

// ─── Onglet Webapp — détections terrain ───────────────────────────────────────

function WebappDetectionsView({ onOpenDossier }) {
  const PAGE_SIZE = 50;
  const [detections, setDetections] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);

  useEffect(() => {
    setLoading(true);
    takataApi.getWebappDetections({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
      .then(r => {
        setDetections(r.data.detections || []);
        if (r.data.total != null) setTotal(r.data.total);
      })
      .catch(err => console.error('Erreur chargement détections webapp:', err))
      .finally(() => setLoading(false));
  }, [page]);

  // Export complet : appel avec export=1 (pas de LIMIT, pas de présignature S3)
  const doExportWebapp = async () => {
    setExporting(true);
    try {
      const res = await takataApi.getWebappDetections({ export: '1' });
      const all = res.data.detections || [];
      const header = ['Date', 'Immat', 'Marque / Modèle', 'Civilité', 'Nom', 'Téléphone', 'Adresse', 'Code Postal', 'Ville', 'Remarque Webapp', 'Commentaire'];
      const rows = all.map(det => {
        const c = webappParseCoords(webappCoordsFromNote(det.notes));
        return [
          toXlsDate(det.date),
          det.immat || '',
          [det.marque, det.modele].filter(Boolean).join(' ') || '',
          c.civilite,
          c.nom || det.client_nom || '',
          c.telephone,
          c.adresse,
          cpFromVille(c.ville),
          villeSansCP(c.ville),
          det.remarque_webapp || '',
          c.commentaire,
        ];
      });
      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      for (let r = 2; r <= rows.length + 1; r++) {
        const ref = `A${r}`;
        if (ws[ref] && ws[ref].v instanceof Date) { ws[ref].t = 'd'; ws[ref].z = 'dd/mm/yyyy'; }
      }
      ws['!cols'] = [16, 12, 20, 10, 26, 14, 30, 14, 20, 20, 50].map(wch => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Webapp');
      XLSX.writeFile(wb, `takata_webapp_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Erreur export webapp:', err);
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  if (loading) return <div className="takata-loading">Chargement des détections…</div>;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span className="takata-journal-count">{total} détection{total !== 1 ? 's' : ''} terrain</span>
        {total > 0 && (
          <button className="page-header-btn" onClick={doExportWebapp} disabled={exporting}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {exporting ? 'Export…' : `Export (${total})`}
          </button>
        )}
      </div>

      {detections.length === 0 && total === 0 ? (
        <div className="takata-empty">Aucune détection webapp pour le moment.</div>
      ) : (
        <>
          <div className="table-container">
            <table className="takata-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Immat</th>
                  <th>Marque / Modèle</th>
                  <th>Coordonnées identifiées</th>
                  <th>Photo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {detections.map(det => {
                  const coords = webappCoordsFromNote(det.notes);
                  return (
                    <tr key={det.id}>
                      <td>{formatDateTime(det.date)}</td>
                      <td><strong>{det.immat || '—'}</strong></td>
                      <td>{[det.marque, det.modele].filter(Boolean).join(' ') || '—'}</td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', maxWidth: 340, whiteSpace: 'pre-line' }}>
                        {coords || det.client_nom || '—'}
                      </td>
                      <td>
                        {det.url ? (
                          <img
                            src={det.url}
                            alt="detection"
                            style={{ width: 60, height: 45, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                            onClick={() => setViewerDoc({ name: det.nom, url: det.url, type: 'image/jpeg' })}
                          />
                        ) : '—'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon"
                            title="Ouvrir la fiche Takata"
                            onClick={() => onOpenDossier && onOpenDossier({ id: det.dossier_id, immat: det.immat, vin: det.vin, marque: det.marque, modele: det.modele })}
                          >👁️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="takata-pagination">
              <button disabled={safePage <= 1} onClick={() => setPage(1)}>⏮</button>
              <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>◀</button>
              <span className="takata-pagination-info">Page {safePage} / {totalPages}</span>
              <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>▶</button>
              <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>⏭</button>
            </div>
          )}
        </>
      )}

      {viewerDoc && <TakataDocViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />}
    </div>
  );
}

export default Takata;
export { DetailDossierModal, CreateDossierModal, STATUTS };
